/**
 * V2 Variant Selector - Simplified Visual Inputs
 *
 * Clean, simple approach:
 * - Visual inputs: color swatches, size buttons
 * - All dimensions visible at once
 * - Smart availability indicators
 * - Works on mobile and desktop
 */

// Define VariantSelectorV2 class globally if not already defined
if (typeof window.VariantSelectorV2 === 'undefined') {

// Shared section rendering cache (persists across instances for session)
// Key: variantId, Value: { html: string, timestamp: number }
const sectionRenderingCache = new Map();

// Cross-instance Storefront API variant caches.
// Complete-the-set instantiates V2 selectors per card AND per quick-view modal,
// and the main PDP instantiates its own selector. Without these shared caches,
// each instance fires its own `fetchAllVariantsFromStorefront()` for the same
// product, multiplying the GraphQL request count 2-3× on PDPs with V2-product CTS.
//
// fetchedVariantsCache: completed fetches — productId → Array<sfVariant>
// inFlightStorefrontFetches: currently-in-flight — productId → Promise<Array<sfVariant>>
const fetchedVariantsCache = new Map();
const inFlightStorefrontFetches = new Map();

// Tracks image URLs we've already issued a <link rel="prefetch"> for.
// Prevents duplicate prefetch elements when multiple VariantSelectorV2 instances
// exist for the same product (PDP + CTS card + quick-view modal) or across
// session re-entries. See _prefetchVariantHeroImages() below.
const prefetchedImageUrls = new Set();

window.VariantSelectorV2 = class VariantSelectorV2 {
  constructor(productData, variantConfig, sectionId, productFormId, settings = {}) {
    this.product = productData;
    this.config = variantConfig;
    this.sectionId = sectionId;
    this.productFormId = productFormId;

    this.selectedIndices = [];
    this.variantsBySku = {};
    this.container = null;
    this.mainImageElement = null; // Cached reference for performance
    this.announcer = null; // ARIA live region for screen reader announcements
    this.hideUnavailable = settings.hideUnavailable || false; // Hide unavailable options completely
    this.combineDimensions = settings.combineDimensions !== false; // Combine color/finish dimensions (default: true)
    this.hybridLayout = settings.hybridLayout || false; // Hybrid layout: inline swatches + dropdowns
    this.mobileBreakpoint = 768; // Breakpoint for mobile detection
    this.maxSwatches = settings.maxSwatches || 12; // Max color swatches before converting to dropdown
    this.maxTextButtons = settings.maxTextButtons || 8; // Max text buttons before converting to dropdown

    // Storefront API state for lazy loading variants beyond the fetch threshold
    this.hasFetchedAllVariants = false;
    this.isFetchingVariants = false;
    this.storefrontApiToken = document.querySelector('meta[name="shopify-storefront-token"]')?.content || null;
    const parsedThreshold = parseInt(
      document.querySelector('meta[name="shopify-storefront-variant-fetch-threshold"]')?.content,
      10
    );
    // Clamped to 250 regardless of the parsed value: 250 is Liquid's actual
    // product.variants truncation cap, not just a default, so a malformed or
    // stale settings_data.json value above it must not suppress the fetch at
    // the one point (250+) where skipping it is unsafe.
    this.variantFetchThreshold = Math.min(
      Number.isFinite(parsedThreshold) && parsedThreshold > 0 ? parsedThreshold : 250,
      250
    );

    // Section rendering cache (shared across instances via static property)
    // TTL: 2 minutes - balances freshness with performance
    this.sectionCacheTTL = 2 * 60 * 1000;

    // Prefetch on hover intent (delay prevents prefetch on quick mouse passes)
    this.prefetchHoverTimer = null;
    this.prefetchHoverDelay = 150; // ms to wait before prefetching
    // In-flight hover prefetch: { cacheKey, controller, promise }
    this.hoverPrefetch = null;

    // Load recent selections from localStorage for persistent user preference tracking
    this.recentSelections = this.loadRecentSelections();

    if (!this.validateConfiguration()) {
      this.showError('Invalid V2 configuration structure');
      return;
    }

    // Auto-combine housing + blade finish dimensions if applicable (unless disabled)
    if (this.combineDimensions && this.shouldCombineDimensions()) {
      this.config = this.transformToCombinedDimensions(this.config);
    }

    this.init();
  }

  /**
   * Check if current viewport is mobile
   */
  isMobile() {
    return window.innerWidth < this.mobileBreakpoint;
  }

  /**
   * Check if device supports hover (not touch-only)
   * Used to disable hover preview on touch devices to fix double-tap issue
   */
  supportsHover() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  validateConfiguration() {
    if (!this.config.version) return false;
    if (!Array.isArray(this.config.dimensions) || this.config.dimensions.length === 0) return false;
    if (!Array.isArray(this.config.skus) || this.config.skus.length === 0) return false;
    if (!this.config.combinations) return false;
    return true;
  }

  /**
   * Detect if we should combine color/finish dimensions
   *
   * Combines when:
   * - At least 2 color/finish dimensions exist consecutively
   * - Dimensions have "color" or "finish" in key/label (excluding Color Temp)
   */
  shouldCombineDimensions() {
    const dims = this.config.dimensions;

    // Need at least 2 dimensions to combine
    if (dims.length < 2) return false;

    // Helper: Check if dimension is color/finish related (but NOT Color Temp)
    // Currently disabled - always returns false to prevent dimension combining
    const isColorOrFinish = (_dim) => {
      return false;
    };

    // Find color/finish dimensions with more than 1 option
    // (single-option dimensions are hidden anyway, so ignore them for merge decisions)
    const colorFinishIndices = [];
    for (let i = 0; i < dims.length; i++) {
      if (isColorOrFinish(dims[i]) && dims[i].options?.length > 1) {
        colorFinishIndices.push(i);
      }
    }

    // If we have exactly 2 visible color/finish dimensions, combine them
    if (colorFinishIndices.length === 2) {
      this.combineIndices = colorFinishIndices;
      return true;
    }

    return false;
  }

  /**
   * Transform config to combine two color/finish dimensions into one
   *
   * Strategy:
   * - Keep dimensions before the combined pair as-is
   * - Merge the two color/finish dimensions into single combined dimension
   * - Keep remaining dimensions as-is
   * - Remap combinations to new dimension structure
   */
  transformToCombinedDimensions(originalConfig) {
    const dims = originalConfig.dimensions;

    // Use the indices discovered in shouldCombineDimensions()
    const [idx1, idx2] = this.combineIndices || [1, 2]; // Default to 1,2 for backwards compat

    const firstDim = dims[idx1];
    const secondDim = dims[idx2];

    // Detect separator
    const firstComboKey = Object.keys(originalConfig.combinations)[0];
    const sep = firstComboKey?.includes('/') ? '/' : '|';

    // Build combined options
    const combinedOptions = [];
    const newCombinations = {};

    // For each combination in original config
    for (const [comboKey, skuIndex] of Object.entries(originalConfig.combinations)) {
      const indices = comboKey.split(sep).map(i => parseInt(i));

      // Extract indices for the dimensions we're combining
      const firstOptIdx = indices[idx1];
      const secondOptIdx = indices[idx2];

      // Build indices before, between, and after the combined dims
      const beforeIndices = indices.slice(0, idx1);
      const betweenIndices = indices.slice(idx1 + 1, idx2);
      const afterIndices = indices.slice(idx2 + 1);

      // Create combined finish option label
      const firstOption = firstDim.options[firstOptIdx];
      const secondOption = secondDim.options[secondOptIdx];

      const combinedLabel = `${firstOption.label} / ${secondOption.label}`;

      // Check if we already have this combined option
      let combinedOptIdx = combinedOptions.findIndex(opt => opt.label === combinedLabel);

      if (combinedOptIdx === -1) {
        // Add new combined option
        // Use the watermark that matches the displayed image (first image preferred)
        const useFirstImage = !!firstOption.image;
        const combinedWatermark = useFirstImage
          ? (firstOption.watermark?.trim() || null)
          : (secondOption.watermark?.trim() || null);

        combinedOptions.push({
          label: combinedLabel,
          image: firstOption.image || secondOption.image, // Prefer first image
          watermark: combinedWatermark,
          originalFirstIdx: firstOptIdx,
          originalSecondIdx: secondOptIdx
        });
        combinedOptIdx = combinedOptions.length - 1;
      }

      // Build new combination key
      const newComboKey = [...beforeIndices, combinedOptIdx, ...betweenIndices, ...afterIndices].join(sep);
      newCombinations[newComboKey] = skuIndex;
    }

    // Build new dimensions array
    const newDimensions = [
      ...dims.slice(0, idx1), // Dimensions before the combined pair
      {
        position: idx1,
        key: 'combined',
        label: `${firstDim.label} / ${secondDim.label}`,
        options: combinedOptions
      },
      ...dims.slice(idx1 + 1, idx2), // Dimensions between (if any)
      ...dims.slice(idx2 + 1) // Dimensions after
    ];

    // Renumber positions
    newDimensions.forEach((dim, idx) => {
      dim.position = idx;
    });

    return {
      version: originalConfig.version,
      dimensions: newDimensions,
      skus: originalConfig.skus,
      combinations: newCombinations,
      mediaUrls: originalConfig.mediaUrls
    };
  }

  /**
   * Calculate option-level bestseller ranks from variant-level data
   *
   * For each dimension option, finds all variants that include it and
   * uses the BEST (lowest) rank among those variants.
   *
   * Stores results in this.optionRanks[dimIndex][optIndex]
   */
  calculateOptionRanks() {
    // Was O(dimensions * options-per-dimension * combinations): for every
    // single (dimension, option) pair, it re-scanned every combination
    // looking for matches. For a mega-PDP (up to 250 combinations, several
    // dimensions with many options each) that's tens of thousands of
    // redundant iterations. A combination only ever needs to be visited
    // once — walk the combinations once and update every dimension's
    // option-rank it touches along the way.
    this.optionRanks = this.config.dimensions.map((dimension) => dimension.options.map(() => Infinity));

    for (const [comboKey, skuIndex] of Object.entries(this.config.combinations)) {
      const sku = this.config.skus[skuIndex];
      const rank = this.getVariantRank(this.variantsBySku[sku]);
      if (rank === null) continue;

      const indices = comboKey.split(this.comboSeparator || '|').map(i => parseInt(i));
      indices.forEach((optIndex, dimIndex) => {
        const dimRanks = this.optionRanks[dimIndex];
        if (!dimRanks || optIndex < 0 || optIndex >= dimRanks.length) return;
        if (rank < dimRanks[optIndex]) {
          dimRanks[optIndex] = rank;
        }
      });
    }

    // A dimension whose every label parses as a number (44", 52 in, 1200 mm)
    // is size-like. Sizes read smallest-first; ordering them by sales rank
    // (52", 44", 60") looks broken, so compareOptions skips rank for these.
    this.sizeLikeDimensions = this.config.dimensions.map((dimension) =>
      dimension.options.length > 0 &&
      dimension.options.every((option) => this.extractOptionNumber(this.extractVendorSku(option.label)) !== null)
    );
  }

  // A variant's custom.bestseller_rank (lower sells better), or null when it
  // has none. House writes 999999 for variants with no sales.
  getVariantRank(variant) {
    const rank = parseInt(variant?.bestseller_rank);
    if (isNaN(rank) || rank >= 999999) return null;
    return rank;
  }

  // Numeric portion of an option label, stripping common size suffixes
  extractOptionNumber(label) {
    const cleaned = label.replace(/['"]/g, '').replace(/\s*(inch|in|cm|mm)\s*/gi, '').trim();
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  }

  /**
   * Sort comparator for a dimension's options ({ optIndex, displayLabel }).
   * Size-like dimensions sort numerically; others by bestseller rank first,
   * then numerically (7" before 12"), then alphabetically.
   */
  compareOptions(dimIndex, a, b) {
    if (!this.sizeLikeDimensions?.[dimIndex]) {
      const rankA = this.optionRanks?.[dimIndex]?.[a.optIndex] ?? Infinity;
      const rankB = this.optionRanks?.[dimIndex]?.[b.optIndex] ?? Infinity;
      if (rankA !== rankB) return rankA - rankB;
    }

    const numA = this.extractOptionNumber(a.displayLabel);
    const numB = this.extractOptionNumber(b.displayLabel);
    if (numA !== null && numB !== null) return numA - numB;
    if (numA !== null) return -1;
    if (numB !== null) return 1;
    return a.displayLabel.localeCompare(b.displayLabel, undefined, { sensitivity: 'base' });
  }

  /**
   * A dimension's options in display order, each keeping its config optIndex
   * for data attributes and select values.
   * @returns {Array<{option: Object, optIndex: number, displayLabel: string}>}
   */
  sortedOptionEntries(dimension, dimIndex) {
    return dimension.options
      .map((option, optIndex) => ({ option, optIndex, displayLabel: this.extractVendorSku(option.label) }))
      .sort((a, b) => this.compareOptions(dimIndex, a, b));
  }

  /**
   * Determine if option prices should be hidden
   * Hide when: single dimension AND all variants have the same price
   * @returns {boolean} - True if prices should be hidden
   */
  calculateShouldHideOptionPrices() {
    // Only apply when there's exactly one dimension
    if (this.config.dimensions.length !== 1) {
      return false;
    }

    // Check if all variant prices are the same
    const variants = this.product.variants;
    if (!variants || variants.length === 0) {
      return false;
    }

    const firstPrice = variants[0].price;
    const allSamePrice = variants.every(v => v.price === firstPrice);

    return allSamePrice;
  }

  init() {
    this.container = document.getElementById(`v2-variant-selector-${this.sectionId}`);
    if (!this.container) return;

    // Store instance reference on container for cleanup (used by quickview)
    this.container.v2Instance = this;

    // Read customer type for cache keying (B2B vs B2C)
    // Prefer data attribute, fallback to global FoxTheme.customerType
    this.customerType = this.container.dataset.customerType || window.FoxTheme?.customerType || 'b2c';
    this.isPersonalized = this.container.dataset.personalized === 'true';

    // Create ARIA live region for screen reader announcements
    this.createAnnouncer();

    // Track if this is the initial page load (not a user interaction)
    this.isInitialLoad = true;

    // Accordion configurator state
    this.configuratorMode = 'guided'; // 'guided' or 'summary'
    this.currentStep = 0; // Which dimension is currently expanded
    this.completedSteps = new Set(); // Track which steps have been explicitly selected
    this.hasPreselectedVariant = false; // Whether user arrived with variant in URL

    // Detect combination separator format (support both '|' and '/')
    const firstComboKey = Object.keys(this.config.combinations)[0];
    this.comboSeparator = firstComboKey?.includes('/') ? '/' : '|';

    // Sort dimensions by position
    this.config.dimensions.sort((a, b) => a.position - b.position);

    // Build SKU lookup
    this.product.variants.forEach(variant => {
      this.variantsBySku[variant.sku] = variant;
      const vendorSku = this.extractVendorSku(variant.sku);
      if (vendorSku !== variant.sku) {
        this.variantsBySku[vendorSku] = variant;
      }
    });

    // Calculate option-level ranks from variant bestseller data
    this.calculateOptionRanks();

    // Determine if we should hide option prices (single dimension + all same price)
    this.shouldHideOptionPrices = this.calculateShouldHideOptionPrices();

    // Check if there's a variant in the URL
    const urlParams = new URLSearchParams(window.location.search);
    const hasVariantInUrl = urlParams.has('variant');

    // Initialize selections based on mode:
    // - Hybrid mode: Always pre-select first available variant
    // - Accordion mode: Only pre-select if variant in URL
    if (this.hybridLayout && !hasVariantInUrl) {
      // For hybrid mode, find the first VALID combination (not just index 0 for each)
      this.selectedIndices = this.findFirstValidCombination();
    } else {
      this.selectedIndices = this.config.dimensions.map(dim => {
        if (hasVariantInUrl) {
          // Find default option
          const defaultOption = dim.options.find(opt => opt.default);
          if (defaultOption) {
            return dim.options.indexOf(defaultOption);
          }
          return -1;
        }
        return -1; // No variant in URL = no pre-selection
      });
    }

    // Auto-select single options - if a dimension has only 1 option, select it
    this.config.dimensions.forEach((dim, dimIndex) => {
      if (dim.options.length === 1) {
        this.selectedIndices[dimIndex] = 0;
      }
    });

    // Check URL for preselected variant
    this.selectVariantFromUrl();

    // Render
    this.render();

    // Update variant and fetch product info
    this.updateVariant();

    // Resolve the ATC button's real state on initial load. buy-buttons.liquid
    // server-renders the button DISABLED for V2 multi-variant products
    // (`await_variant_picker`) so a picker that never initializes can't submit
    // the first-available variant. Now that the picker IS live, enable it for a
    // valid selection. fetchAndUpdateProductInfo() skips updateAvailabilityInstant
    // on initial load, so without this call a valid pre-selected variant would
    // leave the button stuck disabled. The no-selection case is already handled
    // by updateVariant() -> setUnavailable() (keeps it disabled / "Select options").
    if (this.currentVariant) {
      this.updateAvailabilityInstant(this.currentVariant);
    }

    // After initial load completes, clear the flag
    this.isInitialLoad = false;

    // Option B2: eagerly fetch variant custom.media via Storefront API on V2 products.
    // Populates variant.storefrontMedia for all variants — the load-bearing media path.
    // Fire-and-forget; failures fall back to featured_media (Shopify native).
    if (this.config?.version && this.storefrontApiToken) {
      this.fetchAllVariantsFromStorefront().catch(() => {});
    }
  }

  extractVendorSku(fullSku) {
    // Extract vendor SKU by removing a brand prefix (2-4 alphanumeric chars at start)
    // Only strips if it matches the pattern of a brand code prefix
    // Examples:
    //   "JY-JYL1506B" -> "JYL1506B" (brand prefix stripped)
    //   "BAF-MK-HK4-042400A258F258G10" -> "MK-HK4-042400A258F258G10" (brand prefix stripped)
    //   "ET2-E31240-20BK" -> "E31240-20BK" (brand prefix with digit stripped)
    //   "Fan Speed Control (3-Wire)" -> "Fan Speed Control (3-Wire)" (no change - not a SKU)
    //   "3-Speed Wall Control" -> "3-Speed Wall Control" (no change - starts with number but is descriptive)

    // Pattern: starts with uppercase letter, followed by 1-3 more alphanumeric chars, then hyphen
    // This matches brand codes like "MFC-", "BAF-", "JY-", "ET2-" but not descriptive text
    const brandPrefixPattern = /^[A-Z][A-Z0-9]{1,3}-/;
    if (brandPrefixPattern.test(fullSku)) {
      return fullSku.replace(brandPrefixPattern, '');
    }
    return fullSku;
  }

  /**
   * Fetch all variants from Storefront API for products at/above the
   * configured variant-fetch threshold (default 250, see variantFetchThreshold)
   * Called lazily when a variant lookup fails and we haven't fetched yet
   * @returns {Promise<boolean>} - Whether fetch was successful and found new variants
   */
  async fetchAllVariantsFromStorefront() {
    if (this.hasFetchedAllVariants || this.isFetchingVariants) {
      return false;
    }

    // Skip the fetch below the configured threshold (default 250, tunable via
    // Theme Settings > Storefront API — see settings_schema.json). Below it,
    // the Liquid-emitted product.variants already covers every variant, so
    // fetching just wastes Storefront API bucket cost (~1-2 cost-pts per
    // page) and adds a failure surface. This threshold is capped at 250 in
    // the schema, so it never suppresses the fetch above Liquid's actual
    // `product.variants` cap: at 250+ we can't distinguish "all of them" from
    // "Liquid truncated us" and must assume the worst.
    //
    // Below the threshold this is also the ONLY source of storefrontMedia:
    // product-media-gallery.liquid renders just the selected variant's media
    // for V2 products unconditionally (no variant-count gate on that side).
    // A product under threshold therefore shows only a single fallback image
    // per non-selected variant (no rich gallery) until this fetch runs.
    // Lowering the threshold trades that degraded experience for Storefront
    // API cost on more products — verify real cost-point/latency budget
    // before lowering it sitewide.
    if (this.product && Array.isArray(this.product.variants) && this.product.variants.length < this.variantFetchThreshold) {
      this.hasFetchedAllVariants = true;
      return false;
    }

    // Respect cooldown after a transient failure to prevent retry storms
    // when the network/API is flaky and the user interacts rapidly with
    // the variant picker.
    if (this._nextRetryEligibleAt && Date.now() < this._nextRetryEligibleAt) {
      return false;
    }

    if (!this.storefrontApiToken) {
      this.hasFetchedAllVariants = true; // Permanent — no retry path without a token
      return false;
    }

    const productId = this.product.id;
    this.isFetchingVariants = true;

    try {
      // Cross-instance cache: another V2 selector on the same product already
      // completed the fetch (typical case: CTS card + quick-view modal for the
      // same product, or rapid re-init). Use the cached variants instead of
      // issuing a duplicate request.
      let allVariants = fetchedVariantsCache.get(productId);

      if (!allVariants) {
        // Check for an in-flight fetch from another instance for the same product.
        // If present, piggyback on it — otherwise initiate the fetch ourselves
        // and register the promise so later instances can find it.
        let fetchPromise = inFlightStorefrontFetches.get(productId);

        if (!fetchPromise) {
          fetchPromise = this._doStorefrontApiFetch(productId);
          inFlightStorefrontFetches.set(productId, fetchPromise);
          // Clear the in-flight entry on settle (success OR failure) so future
          // retries after cooldown are not blocked by a stale rejected promise.
          // Trailing .catch swallows rejection on the cleanup chain only —
          // the original fetchPromise is still awaited below inside try/catch.
          // Without it, .finally() leaks a handled rejection to
          // window.onunhandledrejection (Sentry SHOPIFY-1XV).
          fetchPromise.finally(() => inFlightStorefrontFetches.delete(productId)).catch(() => {});
        }

        allVariants = await fetchPromise;
        fetchedVariantsCache.set(productId, allVariants);
      }

      // Merge the shared variant data into this instance's variantsBySku.
      const { newVariantsAdded } = this._mergeStorefrontVariants(allVariants);

      // Variants past the inline 250 carry ranks too; fold them into option
      // order for the next render rather than reshuffling the open picker.
      if (newVariantsAdded > 0) {
        this.calculateOptionRanks();
      }

      this.hasFetchedAllVariants = true;
      this.isFetchingVariants = false;

      // Option B2: refresh the currently-selected variant's gallery now that
      // storefrontMedia is populated. Covers the race where a user clicked a
      // non-selected variant during the fetch window and landed on featured_image
      // only (Priority 4 fallback) — without this call they'd stay stuck on the
      // single image until they clicked elsewhere and back.
      if (this.currentVariant?.storefrontMedia) {
        this.updateMediaGallery(this.currentVariant);
      }

      // Option B2 prefetch: warm browser cache with each variant's first-image
      // URL so subsequent variant clicks hit cache and skip the preload
      // round-trip in media-gallery.js:705-745 (updateGalleryFromUrls awaits
      // preloadImage before swapping DOM to prevent flash-of-empty-content).
      // Fire-and-forget via requestIdleCallback — never blocks the main thread.
      this._prefetchVariantHeroImages();

      return newVariantsAdded > 0;

    } catch (error) {
      console.error('[V2 SFAPI] Failed to fetch variants:', error);
      // Transient failure — do NOT set hasFetchedAllVariants=true. Without the
      // inline config.media fallback (removed in Option B2), a permanent bail
      // would leave every non-selected variant stuck on featured_image for the
      // rest of the page lifecycle. Release the lock and set a cooldown so the
      // next variant-miss or interaction can retry, without triggering a storm.
      this.isFetchingVariants = false;
      // Longer cooldown on rate-limit / authz failures — Storefront API's
      // anonymous bucket replenishes slowly under bot-burst conditions, and
      // hammering it with 5s retries makes it worse. 30s for 403/429,
      // 5s for everything else (network, parse, etc.).
      const status = (error && error.status) || 0;
      const message = (error && error.message) || '';
      const isRateOrAuth = status === 403 || status === 429 || /\b(403|429)\b/.test(message);
      this._nextRetryEligibleAt = Date.now() + (isRateOrAuth ? 30000 : 5000);
      return false;
    }
  }

  /**
   * Pure Storefront API fetch — paginates through all variants of a product
   * and returns the raw sfVariant nodes. Stateless; does not mutate instance
   * state. Extracted so the promise can be shared across V2 instances via
   * inFlightStorefrontFetches.
   *
   * @param {number|string} productId - The product.id (numeric Shopify ID)
   * @returns {Promise<Array>} Array of Storefront API variant nodes
   * @private
   */
  async _doStorefrontApiFetch(productId) {
    const productGid = `gid://shopify/Product/${productId}`;
    // Note: quantityAvailable requires unauthenticated_read_product_inventory scope
    // which most Storefront API tokens don't have, so we omit it.
    // Note: metafield access requires the field to be exposed to Storefront API in Shopify admin
    // Settings > Custom data > Metafield definitions > Variants > [field] > Storefront API access.
    const query = `
      query getProductVariants($id: ID!, $first: Int!, $after: String) {
        product(id: $id) {
          variants(first: $first, after: $after) {
            pageInfo {
              hasNextPage
              endCursor
            }
            nodes {
              id
              sku
              title
              price {
                amount
                currencyCode
              }
              compareAtPrice {
                amount
              }
              availableForSale
              media: metafield(namespace: "custom", key: "media") {
                value
              }
              bestsellerRank: metafield(namespace: "custom", key: "bestseller_rank") {
                value
              }
            }
          }
        }
      }
    `;

    let allVariants = [];
    let hasNextPage = true;
    let cursor = null;

    // Paginate through all variants (250 per page max)
    while (hasNextPage) {
      const response = await fetch('/api/2026-04/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontApiToken
        },
        body: JSON.stringify({
          query,
          variables: {
            id: productGid,
            first: 250,
            after: cursor
          }
        })
      });

      if (!response.ok) {
        // Distinct .name so Sentry groups HTTP failures separately from
        // logical errors. 403/429 here is almost always Storefront API
        // rate limiting (60 cost-pts/sec for anonymous), usually bot traffic.
        const err = new Error(`Storefront API error: ${response.status}`);
        err.name = 'StorefrontApiHttpError';
        throw err;
      }

      const data = await response.json();

      // product:null means the product doesn't exist for this storefront
      // context (unpublished, market-restricted, deleted mid-session).
      // Return what we have rather than throw — caller proceeds with the
      // variants Liquid already provided. Short-circuits pagination too.
      const product = data.data?.product;
      if (!product) {
        return allVariants;
      }

      const variants = product.variants;

      // If the API surfaced errors alongside a missing variants edge, prefer
      // the API's message — it usually says exactly what went wrong (scope,
      // throttle, schema). Otherwise fall through to the malformed-response
      // branch below.
      if (!variants && data.errors) {
        throw new Error(data.errors.map(e => e.message).join(', '));
      }
      if (!variants) {
        // Product exists but variants edge missing — genuinely malformed.
        // Distinct .name so Sentry groups separately from HTTP failures.
        const err = new Error('No variants data in response');
        err.name = 'StorefrontApiMalformedResponse';
        throw err;
      }

      allVariants = allVariants.concat(variants.nodes);
      hasNextPage = variants.pageInfo.hasNextPage;
      cursor = variants.pageInfo.endCursor;
    }

    return allVariants;
  }

  /**
   * Merge Storefront API variant data into this instance's variantsBySku map.
   * Handles both the upgrade path (variants already from Liquid render — only
   * populate storefrontMedia without clobbering) and the add path (variants
   * beyond the 250-variant Liquid loop limit).
   *
   * @param {Array} allVariants - sfVariant nodes from _doStorefrontApiFetch
   * @returns {{newVariantsAdded: number, upgradedCount: number}}
   * @private
   */
  _mergeStorefrontVariants(allVariants) {
    let newVariantsAdded = 0;
    let upgradedCount = 0;

    allVariants.forEach(sfVariant => {
      const sku = sfVariant.sku;
      if (!sku) return;

      // Parse media metafield once (JSON array of {url, watermark} objects)
      let mediaData = null;
      if (sfVariant.media?.value) {
        try {
          mediaData = JSON.parse(sfVariant.media.value);
        } catch (e) {
        }
      }

      const existing = this.variantsBySku[sku];
      if (existing) {
        // Option B2 upgrade path: variant already in map from Liquid render (variants 1-250).
        // Populate storefrontMedia (Priority 0) without clobbering existing fields.
        if (mediaData && !existing.storefrontMedia) {
          existing.storefrontMedia = mediaData;
          upgradedCount++;
        }
      } else {
        // Add path: variant beyond 250-threshold, not in initial render
        const variantId = parseInt(sfVariant.id.replace('gid://shopify/ProductVariant/', ''));
        const variant = {
          id: variantId,
          sku: sku,
          title: sfVariant.title,
          price: Math.round(parseFloat(sfVariant.price.amount) * 100), // Convert to cents
          compare_at_price: sfVariant.compareAtPrice ? Math.round(parseFloat(sfVariant.compareAtPrice.amount) * 100) : null,
          available: sfVariant.availableForSale,
          // Note: quantityAvailable not available without unauthenticated_read_product_inventory scope
          // We use availableForSale as the source of truth for stock status
          inventory_quantity: sfVariant.availableForSale ? 1 : 0,
          bestseller_rank: sfVariant.bestsellerRank?.value ?? null,
          // Store media from Storefront API metafield
          storefrontMedia: mediaData,
          // Flag indicating variant was fetched from Storefront API (beyond initial 250)
          fromStorefrontApi: true
        };
        this.variantsBySku[sku] = variant;
        newVariantsAdded++;

        // Also index by vendor SKU
        const vendorSku = this.extractVendorSku(sku);
        if (vendorSku !== sku) {
          this.variantsBySku[vendorSku] = variant;
        }
      }
    });

    return { newVariantsAdded, upgradedCount };
  }

  /**
   * Option B2 prefetch: after the Storefront API fetch resolves, warm the
   * browser cache with each unique variant's first-image URL via
   * <link rel="prefetch" as="image"> so subsequent variant clicks don't pay
   * the preload-before-swap round-trip in media-gallery.js:705-745.
   *
   * This method does NOT touch variant-selector state or the gallery update
   * flow. It only appends <link> elements to document.head; the browser handles
   * fetching, prioritization, and throttling. Fire-and-forget via
   * requestIdleCallback; graceful degradation to setTimeout on older browsers.
   *
   * Capped at MAX_PREFETCH unique variants to avoid bandwidth saturation on
   * high-variant products. Module-level prefetchedImageUrls Set dedupes across
   * VariantSelectorV2 instances and session re-entries.
   * @private
   */
  _prefetchVariantHeroImages() {
    if (typeof document === 'undefined' || !document.head) return;

    const MAX_PREFETCH = 50;
    const seenVariantIds = new Set();
    const toPrefetch = [];

    for (const variant of Object.values(this.variantsBySku)) {
      if (toPrefetch.length >= MAX_PREFETCH) break;
      if (!variant || seenVariantIds.has(variant.id)) continue;
      seenVariantIds.add(variant.id);

      const media = variant.storefrontMedia;
      if (!Array.isArray(media) || media.length === 0) continue;
      const firstItem = media[0];

      // Support both wrapped shape [{ images: [{url, ...}] }] and flat shape
      // [{url, ...}]. See updateMediaGallery Priority 0 for detailed shape notes.
      let url = null;
      if (firstItem && Array.isArray(firstItem.images) && firstItem.images.length > 0) {
        const firstImage = firstItem.images[0];
        url = (firstImage && (firstImage.url || firstImage.external_url))
          || (typeof firstImage === 'string' ? firstImage : null);
      } else {
        url = (firstItem && firstItem.url)
          || (typeof firstItem === 'string' ? firstItem : null);
      }
      if (!url || typeof url !== 'string') continue;
      if (prefetchedImageUrls.has(url)) continue;

      // Defense in depth: run through the same origin allowlist every other
      // image consumer in this file uses. Without this, poisoned metafield
      // data could cause every PDP visitor's browser to silently prefetch
      // attacker-controlled URLs. validateImageUrl returns null on rejection.
      const validated = this.validateImageUrl(url);
      if (!validated) continue;

      toPrefetch.push(validated);
    }

    if (toPrefetch.length === 0) return;

    const doPrefetch = () => {
      const frag = document.createDocumentFragment();
      for (const url of toPrefetch) {
        prefetchedImageUrls.add(url);
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.as = 'image';
        link.href = url;
        frag.appendChild(link);
      }
      document.head.appendChild(frag);
    };

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(doPrefetch, { timeout: 10000 });
    } else {
      setTimeout(doPrefetch, 500);
    }
  }

  /**
   * Detect if user navigated from an internal listing/collection page
   * Used to determine preselection behavior:
   * - From listing: selections set but accordions stay OPEN (user confirms choices)
   * - Direct/external (Google Shopping, shared links): accordions COLLAPSED (ready to buy)
   *
   * Detection priority:
   * 1. sessionStorage (most reliable - set by listing-page-tracker.js)
   * 2. document.referrer (fallback - can be blocked by browser settings)
   */
  isFromInternalListingPage() {
    // Disabled: Always treat navigation as direct/external for consistent UX
    // All customers get pre-selected variant experience regardless of navigation source
    return false;
  }

  selectVariantFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    if (!variantId) return;

    const variant = this.product.variants.find(v => v.id === parseInt(variantId));

    // If variant not found in initial 250, try fetching from Storefront API
    if (!variant && this.storefrontApiToken && !this.hasFetchedAllVariants) {

      // Fetch all variants and retry selection
      this.fetchAllVariantsFromStorefront().then(() => {
        // After fetching, find the variant in our cache by looking through all combinations
        // Since SFAPI variants are keyed by SKU, we need to find which SKU maps to this variant ID
        for (const [sku, v] of Object.entries(this.variantsBySku)) {
          if (v.id === parseInt(variantId)) {
            this.selectVariantBySku(sku);
            return;
          }
        }

      });
      return; // Exit - async handler will complete selection
    }

    if (!variant) return;

    const vendorSku = this.extractVendorSku(variant.sku);
    const skuIndex = this.config.skus.indexOf(vendorSku) !== -1
      ? this.config.skus.indexOf(vendorSku)
      : this.config.skus.indexOf(variant.sku);

    if (skuIndex === -1) return;

    // Detect navigation source
    const fromListingPage = this.isFromInternalListingPage();

    // Find combination for this SKU
    for (const [combo, idx] of Object.entries(this.config.combinations)) {
      if (idx === skuIndex) {
        this.selectedIndices = combo.split(this.comboSeparator).map(i => parseInt(i));

        if (fromListingPage) {
          // From listing page: selections are set but user needs to confirm
          // Keep accordions OPEN so user can review/change selections
          this.hasPreselectedVariant = false;
          this.configuratorMode = 'guided';
          this.currentStep = 0; // First accordion expanded

          // Don't mark steps as completed - let user explicitly confirm
          // (completedSteps stays empty)
        } else {
          // Direct/external link (Google Shopping, shared URL, bookmark)
          // Treat as intentional variant selection - show as completed
          this.hasPreselectedVariant = true;
          this.configuratorMode = 'guided';
          this.currentStep = -1; // No accordion expanded (all collapsed)

          // Mark all visible dimensions as completed
          this.config.dimensions.forEach((dim, i) => {
            if (dim.options.length > 1) {
              this.completedSteps.add(i);
            }
          });
        }
        break;
      }
    }
  }

  /**
   * Select a variant by its SKU - used after Storefront API fetch
   * @param {string} sku - The SKU to select
   */
  selectVariantBySku(sku) {
    const vendorSku = this.extractVendorSku(sku);
    const skuIndex = this.config.skus.indexOf(vendorSku) !== -1
      ? this.config.skus.indexOf(vendorSku)
      : this.config.skus.indexOf(sku);

    if (skuIndex === -1) {
      return;
    }

    // Find combination for this SKU
    for (const [combo, idx] of Object.entries(this.config.combinations)) {
      if (idx === skuIndex) {
        this.selectedIndices = combo.split(this.comboSeparator).map(i => parseInt(i));

        // Treat as direct/external link - show as completed
        this.hasPreselectedVariant = true;
        this.configuratorMode = 'guided';
        this.currentStep = -1; // No accordion expanded (all collapsed)

        // Mark all visible dimensions as completed
        this.config.dimensions.forEach((dim, i) => {
          if (dim.options.length > 1) {
            this.completedSteps.add(i);
          }
        });

        // Re-render and update
        this.render();
        this.updateVariant();
        break;
      }
    }
  }

  render() {
    // Hybrid layout mode - inline swatches and dropdowns
    if (this.hybridLayout) {
      this.renderHybridMode();
      this.container.classList.add('v2-initialized', 'v2-variant-selector--hybrid');
      return;
    }

    // Check if modal exists and is currently open
    const existingModal = this.container.querySelector(`#v2-options-modal-${this.sectionId}`);
    const modalIsOpen = existingModal && existingModal.style.display === 'flex';

    if (modalIsOpen) {
      // Modal is open - only update dimensions section to prevent flickering
      const dimensionsContainer = this.container.querySelector('.v2-dimensions');
      if (dimensionsContainer) {
        dimensionsContainer.innerHTML = this.renderGuidedMode();
        // Re-attach event listeners only to dimension elements (modal listeners remain intact)
        this.attachDimensionEventListeners();
      }

      // Update modal selection state without re-rendering entire modal
      this.updateModalSelectionState(existingModal);
    } else {
      // Modal is closed - do full render
      const dimensionsHtml = this.renderGuidedMode();

      const html = `
        <div class="v2-dimensions v2-dimensions--guided">
          ${dimensionsHtml}
        </div>
        <div class="v2-modal-overlay" id="v2-options-modal-${this.sectionId}" style="display: none;">
          <div class="v2-modal">
            <div class="v2-modal-header">
              <h3 class="v2-modal-title"></h3>
              <button type="button" class="v2-modal-close" aria-label="Close">&times;</button>
            </div>
            <div class="v2-modal-selection-summary"></div>
            <div class="v2-modal-filters"></div>
            <div class="v2-modal-content"></div>
            <div class="v2-modal-footer"></div>
          </div>
        </div>
      `;
      this.container.innerHTML = html;
      this.attachEventListeners();
    }

    // Mark as initialized to prevent FOUC (CSS uses this to show content)
    this.container.classList.add('v2-initialized');
  }

  /**
   * Render guided mode - accordion steps
   */
  renderGuidedMode() {
    return this.config.dimensions.map((dim, dimIndex) => this.renderDimension(dim, dimIndex)).join('');
  }

  /**
   * Switch to guided mode from summary mode
   */
  enterGuidedMode() {
    this.configuratorMode = 'guided';
    this.currentStep = 0;
    this.completedSteps.clear();
    this.render();
  }

  /**
   * Toggle accordion step - expand clicked step, collapse others
   */
  toggleAccordionStep(step) {
    // Clear preselected state - user is now manually interacting with accordions
    this.hasPreselectedVariant = false;

    // Any step can be opened at any time - no sequential restrictions

    // If clicking the currently open step, collapse it
    if (this.currentStep === step) {
      this.currentStep = -1; // No step open
    } else {
      this.currentStep = step;
    }
    this.render();
  }

  renderDimension(dimension, dimIndex) {
    // Hide single-option dimensions completely (no UI needed for forced selections)
    if (dimension.options.length === 1) {
      return '';
    }

    const selectedIndex = this.selectedIndices[dimIndex];
    const selectedOption = selectedIndex >= 0 ? dimension.options[selectedIndex] : null;
    const isColor = this.isColorDimension(dimension);

    // Display vendor SKU (strip brand prefix) for cleaner labels
    const displayLabel = selectedOption ? this.extractVendorSku(selectedOption.label) : '';

    // Calculate visible step number (excluding single-option dimensions)
    const visibleStepIndex = this.getVisibleStepIndex(dimIndex);

    // Determine accordion state - collapsible but never locked
    const isCurrentStep = this.currentStep === visibleStepIndex;
    const isCompleted = this.completedSteps.has(dimIndex);
    const isLocked = false; // Never locked - all sections can be opened anytime

    // Build state classes
    const stateClasses = [
      isCurrentStep ? 'v2-accordion--current' : '',
      isCompleted ? 'v2-accordion--completed' : '',
      isLocked ? 'v2-accordion--locked' : ''
    ].filter(Boolean).join(' ');

    // Build the options content - dropdowns on mobile, buttons on desktop
    const moreOptionsHint = this.hideUnavailable ?
      this.renderUnavailableOptionsHint(dimension, dimIndex) : '';

    // Mobile: native select dropdown for familiar UX
    if (this.isMobile()) {
      const isUnselected = selectedIndex === -1;
      console.log('[V2] Mobile render dim', dimIndex, ':', { selectedIndex, isUnselected });

      // Add placeholder option if unselected
      const placeholderHtml = isUnselected
        ? `<option value="-1" selected disabled>Select ${this.escapeHtml(dimension.label)}...</option>`
        : '';

      const optionsHtml = this.sortedOptionEntries(dimension, dimIndex).map(({ optIndex, displayLabel: optLabel }) => {
        const isSelected = optIndex === selectedIndex;
        const isCompatible = dimIndex === 0 ? true : this.isOptionCompatible(dimIndex, optIndex);
        const disabledAttr = !isCompatible ? 'disabled' : '';

        return `<option value="${optIndex}" ${isSelected ? 'selected' : ''} ${disabledAttr}>${this.escapeHtml(optLabel)}${!isCompatible ? ' (unavailable)' : ''}</option>`;
      }).join('');

      // Add visual indicator when selection is required
      const requiresSelection = isUnselected ? ' v2-select-row--requires-selection' : '';

      return `
        <div class="v2-select-row${requiresSelection}" data-dimension="${dimIndex}">
          <label class="v2-select-row__label">
            <span class="v2-select-row__step">${visibleStepIndex + 1}</span>
            <span class="v2-select-row__title">${this.escapeHtml(dimension.label)}</span>
          </label>
          <div class="v2-select-row__input">
            <select class="v2-native-select${isUnselected ? ' v2-native-select--unselected' : ''}" data-dimension="${dimIndex}">
              ${placeholderHtml}
              ${optionsHtml}
            </select>
          </div>
        </div>
      `;
    }

    // Desktop: buttons in accordion content
    const optionsHtml = this.renderStandardButtons(dimension, dimIndex, selectedIndex);

    const optionsContent = `
      <div class="v2-dimension__options">
        ${optionsHtml}
        ${moreOptionsHint}
      </div>
    `;

    return `
      <div class="v2-accordion ${stateClasses}" data-dimension="${dimIndex}" data-step="${visibleStepIndex}">
        <button type="button" class="v2-accordion__header" ${isLocked ? 'disabled' : ''} data-action="toggle-accordion">
          <span class="v2-accordion__step">${visibleStepIndex + 1}</span>
          <span class="v2-accordion__title">${this.escapeHtml(dimension.label)}</span>
          ${isCompleted ? `
            <span class="v2-accordion__selection">
              <span class="v2-accordion__value">${this.escapeHtml(displayLabel)}</span>
            </span>
          ` : ''}
          <span class="v2-accordion__icon">${isCompleted ? '✓' : isLocked ? '🔒' : '▼'}</span>
        </button>
        <div class="v2-accordion__content" ${!isCurrentStep ? 'hidden' : ''}>
          <div class="v2-dimension v2-dimension--${isColor ? 'image-grid' : 'standard'}">
            ${optionsContent}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Count available options for a dimension based on current selections
   */
  getAvailableOptionCount(dimIndex) {
    const dimension = this.config.dimensions[dimIndex];
    let count = 0;
    for (let optIndex = 0; optIndex < dimension.options.length; optIndex++) {
      if (this.isOptionAvailable(dimIndex, optIndex)) {
        count++;
      }
    }
    return count;
  }

  /**
   * Get the visible step index for a dimension (excluding single-option dimensions)
   */
  getVisibleStepIndex(dimIndex) {
    let visibleIndex = 0;
    for (let i = 0; i < dimIndex; i++) {
      if (this.config.dimensions[i].options.length > 1) {
        visibleIndex++;
      }
    }
    return visibleIndex;
  }

  /**
   * Get total number of visible steps
   */
  getVisibleStepCount() {
    return this.config.dimensions.filter(dim => dim.options.length > 1).length;
  }

  /**
   * Renders unavailable options with clear "requires different X" labels
   */
  renderUnavailableOptionsHint(_dimension, _dimIndex) {
    // Disabled: All options now appear as regular buttons
    // For dimension 0-1: All options (available + unavailable) shown
    // For dimension 2+: Only available options shown (filtered out)
    // Clicking unavailable options triggers auto-adjustment
    return '';
  }

  /**
   * Get image URL for a specific option, searching through all possible variants
   */
  getImageForOption(dimIndex, optIndex) {
    // Try to find any variant that has this option selected
    // We'll iterate through all combinations to find one with an image
    for (const [comboKey, skuIndex] of Object.entries(this.config.combinations)) {
      const indices = comboKey.split(this.comboSeparator).map(i => parseInt(i));
      if (indices[dimIndex] !== optIndex) continue;

      const sku = this.config.skus[skuIndex];
      const variant = this.variantsBySku[sku];
      if (!variant) continue;

      // Try to get image from various sources
      const imageUrl = this.getVariantImageUrl(variant);
      if (imageUrl) return imageUrl;
    }
    return null;
  }

  /**
   * Get image for a modal option that matches current filter selections
   * This prioritizes variants that match the selected options in OTHER dimensions
   * @param {number} dimIndex - The dimension being displayed in the modal
   * @param {number} optIndex - The option index to get image for
   * @returns {string|null} - Image URL or null
   */
  getImageForModalOption(dimIndex, optIndex) {
    // First, try to find a variant that matches current selections in OTHER dimensions
    const testIndices = [...this.selectedIndices];
    testIndices[dimIndex] = optIndex;
    const exactComboKey = testIndices.join(this.comboSeparator);
    const exactSkuIndex = this.config.combinations[exactComboKey];

    if (exactSkuIndex !== undefined) {
      const sku = this.config.skus[exactSkuIndex];
      const variant = this.variantsBySku[sku];
      if (variant) {
        const imageUrl = this.getVariantImageUrl(variant);
        if (imageUrl) return imageUrl;
      }
    }

    // Fallback: find any variant with this option (original behavior)
    return this.getImageForOption(dimIndex, optIndex);
  }

  /**
   * Get image URL for a variant from various sources
   */
  getVariantImageUrl(variant) {
    const debug = false; // Set to true for development debugging
    const logPrefix = '[V2-ImageDebug]';

    if (!variant) {
      if (debug) console.log(`${logPrefix} No variant provided`);
      return null;
    }

    if (debug) {
      console.group(`${logPrefix} Finding image for variant`);
      console.log('Variant ID:', variant.id);
      console.log('Variant SKU:', variant.sku);
      console.log('Variant Title:', variant.title);
    }

    // 0. HIGHEST PRIORITY: Check storefrontMedia from Storefront API fetch
    // This is populated when variants are fetched via GraphQL for products with 250+ variants
    if (debug) {
      console.log('Step 0: Checking variant.storefrontMedia (from Storefront API)');
      console.log('  - storefrontMedia available:', !!variant.storefrontMedia);
      console.log('  - fromStorefrontApi flag:', variant.fromStorefrontApi);
    }
    if (variant.storefrontMedia && Array.isArray(variant.storefrontMedia) && variant.storefrontMedia.length > 0) {
      // See updateMediaGallery Priority 0 for shape discussion. The metafield is
      // typically [{ images: [{url, watermark}, ...] }]; support both wrapped and
      // flat. Here we only need the first image URL (this function returns a single
      // hero image for tile/summary display).
      const firstItem = variant.storefrontMedia[0];
      let mediaUrl = null;
      if (firstItem && Array.isArray(firstItem.images) && firstItem.images.length > 0) {
        const firstImage = firstItem.images[0];
        mediaUrl = (firstImage && (firstImage.url || firstImage.external_url))
          || (typeof firstImage === 'string' ? firstImage : null);
      } else {
        mediaUrl = (firstItem && firstItem.url)
          || (typeof firstItem === 'string' ? firstItem : null);
      }
      if (mediaUrl) {
        if (debug) {
          console.log('✅ Found image in variant.storefrontMedia:', mediaUrl);
          console.groupEnd();
        }
        return mediaUrl;
      }
    }

    // 1. Check custom-media-config element (for dynamically loaded custom media)
    if (debug) {
      console.log('Step 1: Checking custom-media-config element');
      console.log('  - Looking for element ID:', `custom-media-config-${variant.id}`);
    }
    const customMediaConfigEl = document.getElementById(`custom-media-config-${variant.id}`);
    if (debug) {
      console.log('  - Element found:', !!customMediaConfigEl);
    }
    if (customMediaConfigEl) {
      try {
        const customConfig = JSON.parse(customMediaConfigEl.textContent);
        if (debug) {
          console.log('  - hasCustomMedia:', customConfig.hasCustomMedia);
          console.log('  - mediaObjects:', customConfig.mediaObjects);
        }
        if (customConfig.hasCustomMedia && customConfig.mediaObjects?.length > 0) {
          const mediaUrl = customConfig.mediaObjects[0].external_url;
          if (debug) {
            console.log('✅ Found image in custom-media-config:', mediaUrl);
            console.groupEnd();
          }
          return mediaUrl;
        }
      } catch (e) {
        if (debug) console.log('  - Error parsing custom-media-config:', e.message);
        // Continue to next source
      }
    }

    // 2. Fallback: Check native featured_media
    if (debug) {
      console.log('Step 2: Checking variant.featured_media');
      console.log('  - featured_media exists:', !!variant.featured_media);
      console.log('  - preview_image exists:', !!variant.featured_media?.preview_image);
      console.log('  - src value:', variant.featured_media?.preview_image?.src || 'NOT FOUND');
    }
    if (variant.featured_media?.preview_image?.src) {
      const mediaUrl = variant.featured_media.preview_image.src;
      if (debug) {
        console.log('✅ Found image in featured_media:', mediaUrl);
        console.groupEnd();
      }
      return mediaUrl;
    }

    // 3. Fallback: Check featured_image (string URL from Liquid JSON)
    if (debug) {
      console.log('Step 3: Checking variant.featured_image');
      console.log('  - featured_image exists:', !!variant.featured_image);
      console.log('  - featured_image type:', typeof variant.featured_image);
      console.log('  - featured_image value:', variant.featured_image);
    }
    if (variant.featured_image) {
      // Handle both string and object formats
      const imgUrl = typeof variant.featured_image === 'string'
        ? variant.featured_image
        : variant.featured_image.src;
      if (imgUrl) {
        const finalUrl = imgUrl.startsWith('//') ? 'https:' + imgUrl : imgUrl;
        if (debug) {
          console.log('✅ Found image in featured_image:', finalUrl);
          console.groupEnd();
        }
        return finalUrl;
      }
    }

    if (debug) {
      console.log('❌ No image found for variant', variant.id);
      console.log('Full variant object:', variant);
      console.groupEnd();
    }
    return null;
  }

  isColorDimension(dimension) {
    const key = dimension.key.toLowerCase();
    const label = dimension.label.toLowerCase();
    const text = `${key} ${label}`;

    // Exclude Color Temp / Temperature
    if (text.includes('temp') || text.includes('temperature')) {
      return false;
    }

    // Exclude size dimensions - they should render as buttons, not product cards
    if (this.isSizeDimension(dimension)) {
      return false;
    }

    return key.includes('color') || key.includes('finish') ||
           label.includes('color') || label.includes('finish');
  }

  isSizeDimension(dimension) {
    const key = dimension.key.toLowerCase();
    const label = dimension.label.toLowerCase();
    return key.includes('size') || key.includes('span') || key.includes('width') ||
           label.includes('size') || label.includes('span') || label.includes('width');
  }

  /**
   * Determine if dimension should render as dropdown based on option count.
   * Uses separate thresholds for color/swatch dimensions vs text button dimensions.
   * @param {Object} dimension - The dimension object
   * @returns {boolean} - True if should render as dropdown
   */
  shouldRenderAsDropdown(dimension) {
    const optionCount = dimension.options.length;
    const isColor = this.isColorDimension(dimension);
    const threshold = isColor ? this.maxSwatches : this.maxTextButtons;

    return optionCount > threshold;
  }

  /**
   * Open modal with all options for a dimension
   */
  openOptionsModal(dimIndex) {
    const dimension = this.config.dimensions[dimIndex];
    const selectedIndex = this.selectedIndices[dimIndex];

    // Get all options with availability info
    let optionsWithAvailability = dimension.options.map((option, optIndex) => ({
      option,
      optIndex,
      isAvailable: this.isOptionAvailable(dimIndex, optIndex),
      displayLabel: this.extractVendorSku(option.label)
    }));

    // Apply progressive filtering - dimension 1+ filtered based on prior selections
    if (dimIndex >= 1) {
      optionsWithAvailability = optionsWithAvailability.map(opt => ({
        ...opt,
        isCompatible: this.isOptionCompatible(dimIndex, opt.optIndex)
      })).filter(opt => opt.isCompatible);
    } else if (this.hideUnavailable) {
      optionsWithAvailability = optionsWithAvailability.filter(opt => opt.isAvailable);
    }

    optionsWithAvailability.sort((a, b) => this.compareOptions(dimIndex, a, b));

    // Render modal content
    const modal = this.container.querySelector(`#v2-options-modal-${this.sectionId}`);
    if (!modal) {
      console.error('Modal not found:', `#v2-options-modal-${this.sectionId}`);
      return;
    }

    const modalTitle = modal.querySelector('.v2-modal-title');
    const modalSummary = modal.querySelector('.v2-modal-selection-summary');
    const modalFilters = modal.querySelector('.v2-modal-filters');
    const modalContent = modal.querySelector('.v2-modal-content');
    const modalFooter = modal.querySelector('.v2-modal-footer');

    if (modalTitle) modalTitle.textContent = `Select ${dimension.label}`;

    // Render selection summary at top
    if (modalSummary) this.renderModalSelectionSummary(modalSummary);

    // Render dimension filters as pills
    if (modalFilters) this.renderModalFilters(modalFilters, dimIndex);

    // Render footer with CTA
    if (modalFooter) this.renderModalFooter(modalFooter);

    // Store data for search functionality
    modal.dataset.dimension = dimIndex;
    modal.dataset.allOptions = JSON.stringify(optionsWithAvailability);

    // Render options grid
    const renderModalOption = ({ option, optIndex, isAvailable }, index) => {
      const isSelected = optIndex === selectedIndex;
      const imageUrl = this.getImageForModalOption(dimIndex, optIndex);
      const displayLabel = this.extractVendorSku(option.label);
      const isPopular = index < 6; // First 6 are popular

      // Get variant to display price (hide if single dimension with uniform pricing)
      const variant = this.getVariantForOption(dimIndex, optIndex);
      const priceHtml = (variant && !this.shouldHideOptionPrices) ? `<div class="v2-modal-option__price">${this.formatMoney(variant.price)}</div>` : '';

      // Get compatible dimension 0 options if viewing dimension 1+
      const compatibleDim0 = this.getCompatibleDim0Options(dimIndex, optIndex);
      const dim0Label = this.config.dimensions[0]?.label || 'Finish';
      const compatibleHtml = compatibleDim0.length > 0
        ? `<div class="v2-modal-option__available">Available in ${compatibleDim0.length} ${dim0Label.toLowerCase()}${compatibleDim0.length !== 1 ? 'es' : ''}</div>`
        : '';

      // Always show labels in the modal for better UX
      const hideLabel = false;

      // Include dimension name in label for clarity (e.g., "Housing Finish: Black")
      const dimension = this.config.dimensions[dimIndex];
      const labelWithDimension = `${this.escapeHtml(dimension.label)}: ${this.escapeHtml(displayLabel)}`;

      const showUnavailable = dimIndex > 1 && !isAvailable;

      return `
        <button
          class="v2-modal-option ${isSelected ? 'v2-modal-option--selected' : ''} ${showUnavailable ? 'v2-modal-option--unavailable' : ''}"
          data-dimension="${dimIndex}"
          data-option="${optIndex}"
          type="button"
        >
          <div class="v2-modal-option__image" style="background-image: url('${imageUrl || ''}')"></div>
          ${!hideLabel ? `<div class="v2-modal-option__label">${labelWithDimension}</div>` : ''}
          ${compatibleHtml}
          ${priceHtml}
          ${isPopular ? '<span class="v2-modal-option__badge">Popular</span>' : ''}
        </button>
      `;
    };

    if (modalContent) {
      modalContent.innerHTML = `
        <div class="v2-modal-grid">
          ${optionsWithAvailability.map(renderModalOption).join('')}
        </div>
      `;
    }

    // Show modal
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden'; // Prevent background scroll

    // Track which dimension is open so we can restore it after render
    this.lastOpenedDimension = dimIndex;
  }

  /**
   * Close options modal
   */
  closeOptionsModal() {
    const modal = this.container.querySelector(`#v2-options-modal-${this.sectionId}`);
    if (modal) {
      modal.style.display = 'none';
      document.body.style.overflow = ''; // Restore scroll
      this.lastOpenedDimension = undefined; // Clear tracking
      this.modalFilters = {}; // Clear filters
    }
  }

  /**
   * Add current variant to cart
   */
  addToCart() {
    const variant = this.getCurrentVariant();
    if (!variant || !variant.available) return;

    // Find the product form and submit it, or use fetch API
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      // Update the variant input and submit the form
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variant.id;
      }
      productForm.requestSubmit();
    } else {
      // Fallback: Use Shopify Cart API directly
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variant.id, quantity: 1 })
      })
      .then(response => response.json())
      .then(() => {
        // Trigger cart drawer or redirect to cart
        window.location.href = '/cart';
      })
      .catch(error => {
        console.error('Error adding to cart:', error);
      });
    }
  }

  /**
   * Render current selection summary at top of modal
   */
  renderModalSelectionSummary(container) {
    const variant = this.getCurrentVariant();
    if (!variant) {
      container.innerHTML = '';
      return;
    }

    // Build selections summary text
    const selectionsText = this.config.dimensions.map((dim, dimIndex) => {
      const selectedOpt = dim.options[this.selectedIndices[dimIndex]];
      if (!selectedOpt) return null;
      return this.extractVendorSku(selectedOpt.label);
    }).filter(Boolean).join(' · ');

    const imageUrl = variant.featured_image?.src || this.product.featured_image;
    const isAvailable = variant.available;
    const stockStatus = isAvailable ? 'In Stock' : 'Sold Out';
    const stockClass = isAvailable ? 'in-stock' : 'out-of-stock';

    container.innerHTML = `
      <div class="v2-modal-summary">
        <div class="v2-modal-summary__image" style="background-image: url('${imageUrl}')"></div>
        <div class="v2-modal-summary__details">
          <div class="v2-modal-summary__selections">${this.escapeHtml(selectionsText)}</div>
          <div class="v2-modal-summary__price">${this.formatMoney(variant.price)}</div>
          <div class="v2-modal-summary__stock v2-modal-summary__stock--${stockClass}">
            <span class="v2-modal-summary__stock-dot"></span>
            ${stockStatus}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render sticky footer with Add to Cart CTA
   */
  renderModalFooter(container) {
    const variant = this.getCurrentVariant();
    const variantExists = variant !== null;
    const isAvailable = variant?.available;

    // Different messaging for "not offered" vs "sold out"
    let buttonText = 'Add to Cart';
    if (!variantExists) {
      buttonText = 'Not Offered in This Size';
    } else if (!isAvailable) {
      buttonText = 'Sold Out';
    }
    const buttonDisabled = !isAvailable ? 'disabled' : '';

    container.innerHTML = `
      <button type="button" class="v2-modal-add-to-cart" ${buttonDisabled}>
        ${buttonText}
      </button>
    `;
  }

  /**
   * Render compact dropdown filters (image-first design)
   * Minimal footprint to maximize space for product images
   */
  renderModalFilters(container, _activeDimIndex) {
    // Only show filters if there are multiple dimensions
    if (this.config.dimensions.length <= 1) {
      container.innerHTML = '';
      return;
    }

    // Custom dropdown filters (matching v2 selector style)
    const filtersHtml = this.config.dimensions.map((dim, dimIndex) => {
      // Skip single-option dimensions
      if (dim.options.length <= 1) return '';

      const selectedIndex = this.selectedIndices[dimIndex];
      const selectedOption = dim.options[selectedIndex];
      const selectedLabel = selectedOption ? this.extractVendorSku(selectedOption.label) : 'Select';

      // Build custom dropdown options with availability checking
      const optionsHtml = this.sortedOptionEntries(dim, dimIndex).map(({ optIndex, displayLabel: label }) => {
        const isSelected = optIndex === selectedIndex;
        const isAvailable = this.isOptionAvailableForModalFilter(dimIndex, optIndex);
        const unavailableReason = !isAvailable ? this.getUnavailabilityReason(dimIndex, optIndex) : null;

        return `
          <button type="button"
            class="v2-custom-dropdown__option ${isSelected ? 'v2-custom-dropdown__option--selected' : ''} ${!isAvailable ? 'v2-custom-dropdown__option--unavailable' : ''}"
            data-option-index="${optIndex}"
            ${!isAvailable ? 'disabled' : ''}>
            <svg class="v2-custom-dropdown__check" viewBox="0 0 16 16" fill="currentColor">
              <path d="M13.78 4.22a.75.75 0 0 1 0 1.06l-7.25 7.25a.75.75 0 0 1-1.06 0L2.22 9.28a.75.75 0 0 1 1.06-1.06L6 10.94l6.72-6.72a.75.75 0 0 1 1.06 0Z"/>
            </svg>
            <span class="v2-custom-dropdown__option-content">
              <span class="v2-custom-dropdown__option-label">${this.escapeHtml(label)}</span>
              ${unavailableReason ? `<span class="v2-custom-dropdown__option-notice">Not available in ${this.escapeHtml(unavailableReason)}</span>` : ''}
            </span>
          </button>
        `;
      }).join('');

      return `
        <div class="v2-modal-filter-compact">
          <label class="v2-modal-filter-compact__label">${this.escapeHtml(dim.label)}</label>
          <div class="v2-custom-dropdown v2-custom-dropdown--modal" data-dimension="${dimIndex}">
            <button type="button" class="v2-custom-dropdown__trigger" aria-haspopup="listbox" aria-expanded="false">
              <span class="v2-custom-dropdown__value">${this.escapeHtml(selectedLabel)}</span>
              <span class="v2-custom-dropdown__arrow"></span>
            </button>
            <div class="v2-custom-dropdown__panel" role="listbox">
              ${optionsHtml}
            </div>
          </div>
        </div>
      `;
    }).filter(Boolean).join('');

    container.innerHTML = `<div class="v2-modal-filters-row">${filtersHtml}</div>`;

    // Attach event listeners for custom dropdowns in modal
    this.attachModalDropdownListeners(container);
  }

  /**
   * Attach event listeners for custom dropdowns in modal
   */
  attachModalDropdownListeners(container) {
    container.querySelectorAll('.v2-custom-dropdown--modal').forEach(dropdown => {
      const trigger = dropdown.querySelector('.v2-custom-dropdown__trigger');
      const panel = dropdown.querySelector('.v2-custom-dropdown__panel');
      const dimIndex = parseInt(dropdown.dataset.dimension, 10);

      // Toggle dropdown on trigger click
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const isOpen = panel.classList.contains('v2-custom-dropdown__panel--open');

        // Close all other dropdowns in modal first
        container.querySelectorAll('.v2-custom-dropdown__panel--open').forEach(p => {
          p.classList.remove('v2-custom-dropdown__panel--open');
          p.closest('.v2-custom-dropdown').querySelector('.v2-custom-dropdown__trigger').classList.remove('v2-custom-dropdown__trigger--open');
          p.closest('.v2-custom-dropdown').querySelector('.v2-custom-dropdown__trigger').setAttribute('aria-expanded', 'false');
        });

        if (!isOpen) {
          panel.classList.add('v2-custom-dropdown__panel--open');
          trigger.classList.add('v2-custom-dropdown__trigger--open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });

      // Handle option selection
      panel.querySelectorAll('.v2-custom-dropdown__option').forEach(optBtn => {
        optBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const optIndex = parseInt(optBtn.dataset.optionIndex, 10);

          // Close dropdown
          panel.classList.remove('v2-custom-dropdown__panel--open');
          trigger.classList.remove('v2-custom-dropdown__trigger--open');
          trigger.setAttribute('aria-expanded', 'false');

          // Update selection
          this.handleModalFilterChange(dimIndex, optIndex);
        });
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.v2-custom-dropdown--modal')) {
        container.querySelectorAll('.v2-custom-dropdown__panel--open').forEach(panel => {
          panel.classList.remove('v2-custom-dropdown__panel--open');
          const dropdown = panel.closest('.v2-custom-dropdown');
          if (dropdown) {
            dropdown.querySelector('.v2-custom-dropdown__trigger').classList.remove('v2-custom-dropdown__trigger--open');
            dropdown.querySelector('.v2-custom-dropdown__trigger').setAttribute('aria-expanded', 'false');
          }
        });
      }
    }, { once: false, capture: true });
  }

  /**
   * Handle filter change in modal custom dropdowns
   * @param {number} dimIndex - The dimension index that changed
   * @param {number} optIndex - The option index that was selected
   */
  handleModalFilterChange(dimIndex, optIndex) {
    // Get the active dimension from the modal
    const modal = this.container.querySelector('.v2-options-modal');
    const activeDim = modal ? parseInt(modal.dataset.dimension) : 0;

    // Update the selection
    this.selectOption(dimIndex, optIndex);

    // Re-open to refresh the image grid with new filter
    this.openOptionsModal(activeDim);
  }

  /**
   * Check if an option is available for modal filter dropdown
   * (considering selections in OTHER dimensions only)
   * @param {number} dimIndex - The dimension index being checked
   * @param {number} optIndex - The option index to check availability for
   * @returns {boolean} - Whether the option is available
   */
  isOptionAvailableForModalFilter(dimIndex, optIndex) {
    // Check if combination exists in metafield config
    const testIndices = [...this.selectedIndices];
    testIndices[dimIndex] = optIndex;
    const comboKey = testIndices.join(this.comboSeparator);

    // If combination exists in config.combinations, it's allowed
    return this.config.combinations[comboKey] !== undefined;
  }

  /**
   * Get the reason why an option is unavailable (which selection is blocking it)
   * @param {number} dimIndex - The dimension index of the unavailable option
   * @param {number} optIndex - The option index that is unavailable
   * @returns {string|null} - The label of the conflicting selection, or null
   */
  getUnavailabilityReason(dimIndex, optIndex) {
    // Show all prior selections with dimension labels (works for guided, hybrid, or any mode)
    // This tells the user exactly which selections are blocking this option
    if (dimIndex >= 1) {
      const priorSelections = [];
      for (let i = 0; i < dimIndex; i++) {
        const dim = this.config.dimensions[i];
        const selectedOptIndex = this.selectedIndices[i];
        if (selectedOptIndex === -1) continue; // Skip unselected dimensions
        const selectedOption = dim.options[selectedOptIndex];
        if (selectedOption) {
          const dimLabel = dim.label;
          const optionValue = this.extractVendorSku(selectedOption.label);
          priorSelections.push(`${dimLabel}: ${optionValue}`);
        }
      }
      if (priorSelections.length > 0) {
        return priorSelections.join(', ');
      }
    }

    // For non-guided mode: find which single dimension change would help
    for (let otherDimIndex = 0; otherDimIndex < this.config.dimensions.length; otherDimIndex++) {
      if (otherDimIndex === dimIndex) continue;

      // Get current selection in other dimension
      const currentOtherOptIndex = this.selectedIndices[otherDimIndex];
      const otherDim = this.config.dimensions[otherDimIndex];

      // Check if there's ANY other option in this other dimension that would make it available
      let anyOtherWorks = false;
      for (let testOtherOptIndex = 0; testOtherOptIndex < otherDim.options.length; testOtherOptIndex++) {
        if (testOtherOptIndex === currentOtherOptIndex) continue;

        const testIndices = [...this.selectedIndices];
        testIndices[dimIndex] = optIndex;
        testIndices[otherDimIndex] = testOtherOptIndex;

        const comboKey = testIndices.join(this.comboSeparator);
        // If combination exists in config, it works
        if (this.config.combinations[comboKey] !== undefined) {
          anyOtherWorks = true;
          break;
        }
      }

      // If changing the other dimension would help, the current selection in that dimension is the problem
      if (anyOtherWorks) {
        const currentOption = otherDim.options[currentOtherOptIndex];
        return this.extractVendorSku(currentOption.label);
      }
    }

    // If no single dimension change helps, it's likely out of stock entirely
    return 'this combination';
  }

  /**
   * Get the selections that are BLOCKING the hovered/clicked option
   * Evaluates each current selection to see if it's incompatible with the target option
   * @param {number} targetDimIndex - Dimension of the option being hovered/clicked
   * @param {number} targetOptIndex - Option index being hovered/clicked
   * @returns {string} - Formatted string of blocking selections
   */
  getBlockingSelectionsText(targetDimIndex, targetOptIndex) {
    const blockingSelections = [];

    // For each OTHER dimension with a selection, check if it blocks the target option
    for (let i = 0; i < this.config.dimensions.length; i++) {
      if (i === targetDimIndex) continue; // Skip the target dimension

      const selectedOptIndex = this.selectedIndices[i];
      if (selectedOptIndex === -1) continue; // Skip unselected dimensions

      // Check if ANY combination exists with both the target option AND this current selection
      const isBlocking = !this.canCombineOptions(targetDimIndex, targetOptIndex, i, selectedOptIndex);

      if (isBlocking) {
        const dim = this.config.dimensions[i];
        const selectedOption = dim.options[selectedOptIndex];
        if (selectedOption) {
          const optionValue = this.extractVendorSku(selectedOption.label);
          blockingSelections.push(`${dim.label}: ${optionValue}`);
        }
      }
    }

    return blockingSelections.length > 0 ? blockingSelections.join(', ') : 'current selection';
  }

  /**
   * Check if two specific options from different dimensions can exist together in any combination
   * @param {number} dimA - First dimension index
   * @param {number} optA - First option index
   * @param {number} dimB - Second dimension index
   * @param {number} optB - Second option index
   * @returns {boolean} - True if any combination exists with both options
   */
  canCombineOptions(dimA, optA, dimB, optB) {
    // Search through all combinations to see if any has both options
    for (const comboKey in this.config.combinations) {
      const indices = comboKey.split(this.comboSeparator).map(Number);

      // Check if this combination has both specified options
      if (indices[dimA] === optA && indices[dimB] === optB) {
        return true; // Found at least one valid combination
      }
    }
    return false; // No combination exists with both options
  }

  /**
   * Update modal selection state without re-rendering entire modal
   * Called after selectOption() when modal is open
   */
  updateModalSelectionState(modal) {
    const dimIndex = parseInt(modal.dataset.dimension);
    if (isNaN(dimIndex)) return;

    const selectedIndex = this.selectedIndices[dimIndex];
    const modalOptions = modal.querySelectorAll('.v2-modal-option');

    modalOptions.forEach(option => {
      const optIndex = parseInt(option.dataset.option);
      const isSelected = optIndex === selectedIndex;
      option.classList.toggle('v2-modal-option--selected', isSelected);
    });

    // Update selection summary
    const modalSummary = modal.querySelector('.v2-modal-selection-summary');
    if (modalSummary) {
      this.renderModalSelectionSummary(modalSummary);
    }

    // Update filters to reflect current selections
    const modalFilters = modal.querySelector('.v2-modal-filters');
    if (modalFilters) {
      this.renderModalFilters(modalFilters, dimIndex);
    }

    // Update footer CTA
    const modalFooter = modal.querySelector('.v2-modal-footer');
    if (modalFooter) {
      this.renderModalFooter(modalFooter);
    }
  }

  /**
   * Get localStorage key for recent selections (product-specific)
   */
  getRecentSelectionsKey() {
    return `v2-recent-selections-${this.product.id}`;
  }

  /**
   * Load recent selections from localStorage
   * Returns object: { dimIndex: [optIndex1, optIndex2, ...], ... }
   */
  loadRecentSelections() {
    try {
      const stored = localStorage.getItem(this.getRecentSelectionsKey());
      return stored ? JSON.parse(stored) : {};
    } catch (_e) {
      return {};
    }
  }

  /**
   * Save recent selections to localStorage
   */
  saveRecentSelections() {
    try {
      localStorage.setItem(this.getRecentSelectionsKey(), JSON.stringify(this.recentSelections));
    } catch (_e) {
      // localStorage might be full or unavailable
    }
  }

  /**
   * Track a selection as recent for a dimension
   * Keeps last 6 selections per dimension, most recent first
   */
  trackRecentSelection(dimIndex, optIndex) {
    if (!this.recentSelections[dimIndex]) {
      this.recentSelections[dimIndex] = [];
    }

    const recent = this.recentSelections[dimIndex];

    // Remove if already in list (will re-add at front)
    const existingIdx = recent.indexOf(optIndex);
    if (existingIdx !== -1) {
      recent.splice(existingIdx, 1);
    }

    // Add to front
    recent.unshift(optIndex);

    // Keep only last 6
    if (recent.length > 6) {
      recent.pop();
    }

    this.saveRecentSelections();
  }

  /**
   * Get compatible dimension 0 options for a given option in dimension dimIndex
   * Used to show "Available in: Finish1, Finish2" in modals
   */
  getCompatibleDim0Options(dimIndex, optIndex) {
    // Only applicable for dimension 1+
    if (dimIndex === 0) return [];

    const dim0 = this.config.dimensions[0];
    const compatibleOptions = [];

    // Test each dimension 0 option to see if it works with this option
    for (let dim0Index = 0; dim0Index < dim0.options.length; dim0Index++) {
      // Build test indices: [dim0Index, optIndex (if dimIndex=1), ...]
      const testIndices = [dim0Index];

      if (dimIndex === 1) {
        // We're checking dimension 1 options - test [dim0, thisOption]
        testIndices.push(optIndex);
      } else if (dimIndex === 2) {
        // We're checking dimension 2 options - test with current dim1 selection
        testIndices.push(this.selectedIndices[1], optIndex);
      }
      // Add more dimensions if needed...

      // Check if this combination exists and is available
      if (this.hasValidCombinationForPrefix(testIndices, testIndices.length)) {
        compatibleOptions.push({
          optIndex: dim0Index,
          label: this.extractVendorSku(dim0.options[dim0Index].label)
        });
      }
    }

    return compatibleOptions;
  }

  renderStandardButtons(dimension, dimIndex, selectedIndex) {
    // Create array with all options and check compatibility
    const allOptions = dimension.options.map((option, optIndex) => ({
      option,
      optIndex,
      displayLabel: this.extractVendorSku(option.label),
      isCompatible: dimIndex === 0 ? true : this.isOptionCompatible(dimIndex, optIndex)
    }));

    allOptions.sort((a, b) => this.compareOptions(dimIndex, a, b));

    // Find first compatible option index for roving tabindex fallback
    const firstCompatibleIndex = allOptions.findIndex(opt => opt.isCompatible);

    // Render a single option button with ARIA roles (WCAG 2.1 AA)
    const renderOption = ({ optIndex, displayLabel, isCompatible }, arrayIndex) => {
      const isSelected = optIndex === selectedIndex;
      const unavailableClass = !isCompatible ? 'v2-option--unavailable' : '';
      const isDisabled = !isCompatible && dimIndex >= 1;

      // Roving tabindex: selected gets 0, else first compatible gets 0, else first option
      const shouldBeTabFocusable = isSelected ||
        (selectedIndex === -1 && arrayIndex === Math.max(0, firstCompatibleIndex));

      return `
        <button
          type="button"
          role="radio"
          class="v2-option ${isSelected ? 'v2-option--selected' : ''} ${unavailableClass}"
          data-dimension="${dimIndex}"
          data-option="${optIndex}"
          aria-label="${this.escapeHtml(displayLabel)}"
          aria-checked="${isSelected}"
          ${isDisabled ? 'aria-disabled="true"' : ''}
          tabindex="${shouldBeTabFocusable ? '0' : '-1'}"
        >
          <span class="v2-option__label">${this.escapeHtml(displayLabel)}</span>
        </button>
      `;
    };

    // Render all options wrapped in radiogroup
    const buttonsHtml = allOptions.map(renderOption).join('');
    return `<div class="v2-option-group" role="radiogroup" aria-label="${this.escapeHtml(dimension.label)}" aria-required="true">${buttonsHtml}</div>`;
  }

  isOptionAvailable(dimIndex, optIndex) {
    // First dimension always available
    if (dimIndex === 0) return true;

    // Check if combination exists in metafield config - that's the ONLY source of truth
    const testIndices = [...this.selectedIndices];
    testIndices[dimIndex] = optIndex;
    const comboKey = testIndices.join(this.comboSeparator);

    // If combination exists in config.combinations, it's allowed. Period.
    // Stock status is separate - handled by getVariantStockStatus()
    return this.config.combinations[comboKey] !== undefined;
  }

  /**
   * Check if an option is compatible with PREVIOUS selections only
   * (ignoring selections in current and subsequent dimensions)
   *
   * This is the Magento-style compatibility check:
   * Does ANY variant exist with previous selections + this option?
   *
   * @param {number} dimIndex - Dimension index to check
   * @param {number} optIndex - Option index within that dimension
   * @returns {boolean} - True if any valid combination exists with this option
   */
  isOptionCompatible(dimIndex, optIndex) {
    // Dimension 0: always compatible (first dimension, no prior selections)
    if (dimIndex === 0) return true;

    // Dimension 1+: filter based on prior selections
    // Build partial indices: previous selections + this option
    const partialIndices = this.selectedIndices.slice(0, dimIndex);
    partialIndices.push(optIndex);

    // Check if ANY combination EXISTS for subsequent dimensions (ignore stock status)
    // This allows users to see ALL valid options, even if currently out of stock
    // Availability (in-stock) is handled separately by isOptionAvailable()
    const result = this.hasCombinationExistsForPrefix(partialIndices, dimIndex + 1);

    // Debug logging for compatibility checks

    return result;
  }

  /**
   * Get all compatible option indices for a dimension based on current prior selections.
   * Used for auto-selection when only one option is available.
   *
   * @param {number} dimIndex - Dimension index to check
   * @returns {Array<number>} - Array of compatible option indices
   */
  getCompatibleOptionsForDimension(dimIndex) {
    const dimension = this.config.dimensions[dimIndex];
    if (!dimension) return [];

    const compatibleOptions = [];
    for (let optIndex = 0; optIndex < dimension.options.length; optIndex++) {
      if (this.isOptionCompatible(dimIndex, optIndex)) {
        compatibleOptions.push(optIndex);
      }
    }
    return compatibleOptions;
  }

  /**
   * Find what prior dimension selection would make an incompatible option available.
   * Returns info about which dimension/option to change, plus price difference.
   *
   * @param {number} dimIndex - The dimension index of the incompatible option
   * @param {number} optIndex - The option index that is incompatible
   * @returns {Object|null} - { dimLabel, optionLabel, priceDiff } or null if no change found
   */
  findRequiredChangeForOption(dimIndex, optIndex) {
    // Only applies to dimension 1+ (dimension 0 has no prior dependencies)
    if (dimIndex < 1) return null;

    // Get current cheapest price for reference
    const currentCheapest = this.getCheapestPriceForSelection(this.selectedIndices.slice(0, dimIndex));

    // Check each prior dimension to find which one could enable this option
    for (let priorDim = dimIndex - 1; priorDim >= 0; priorDim--) {
      const priorDimension = this.config.dimensions[priorDim];

      // Try each option in the prior dimension
      for (let priorOptIndex = 0; priorOptIndex < priorDimension.options.length; priorOptIndex++) {
        // Skip if it's already selected (since it doesn't work)
        if (priorOptIndex === this.selectedIndices[priorDim]) continue;

        // Build test indices with this alternative prior selection
        const testIndices = [...this.selectedIndices.slice(0, priorDim), priorOptIndex];

        // Add the target option
        for (let i = priorDim + 1; i < dimIndex; i++) {
          testIndices.push(this.selectedIndices[i]);
        }
        testIndices.push(optIndex);

        // Check if this combination would be valid
        if (this.hasValidCombinationForPrefix(testIndices, dimIndex + 1)) {
          // Calculate price difference
          const newCheapest = this.getCheapestPriceForSelection(testIndices);
          const priceDiff = newCheapest !== null && currentCheapest !== null
            ? newCheapest - currentCheapest
            : null;

          return {
            dimLabel: priorDimension.label,
            optionLabel: this.extractVendorSku(priorDimension.options[priorOptIndex].label),
            priceDiff: priceDiff
          };
        }
      }
    }

    return null;
  }

  /**
   * Get the cheapest available variant price for a given selection prefix.
   * Searches all valid combinations that start with the given indices.
   *
   * @param {Array} prefixIndices - Selection indices to search from
   * @returns {number|null} - Cheapest price in cents, or null if no valid variants
   */
  getCheapestPriceForSelection(prefixIndices) {
    const range = this.getPriceRangeForSelection(prefixIndices);
    return range ? range.min : null;
  }

  /**
   * Get the price range (min and max) for a given selection prefix.
   * Searches all valid combinations that start with the given indices.
   *
   * @param {Array} prefixIndices - Selection indices to search from
   * @returns {Object|null} - { min, max } prices in cents, or null if no valid variants
   */
  getPriceRangeForSelection(prefixIndices) {
    let minPrice = null;
    let maxPrice = null;

    const searchCombinations = (indices, dimIndex) => {
      // Base case: we have a complete combination
      if (dimIndex >= this.config.dimensions.length) {
        const comboKey = indices.join(this.comboSeparator);
        const skuIndex = this.config.combinations[comboKey];
        if (skuIndex === undefined) return;

        const sku = this.config.skus[skuIndex];
        const variant = this.variantsBySku[sku];
        if (variant && variant.available && variant.price) {
          if (minPrice === null || variant.price < minPrice) {
            minPrice = variant.price;
          }
          if (maxPrice === null || variant.price > maxPrice) {
            maxPrice = variant.price;
          }
        }
        return;
      }

      // Recursive case: try each option for this dimension
      const dimension = this.config.dimensions[dimIndex];
      for (let optIndex = 0; optIndex < dimension.options.length; optIndex++) {
        searchCombinations([...indices, optIndex], dimIndex + 1);
      }
    };

    searchCombinations(prefixIndices, prefixIndices.length);
    return minPrice !== null ? { min: minPrice, max: maxPrice } : null;
  }

  /**
   * Recursively check if a valid available combination exists
   * starting from the given dimension index.
   *
   * Magento equivalent: Check if allowedProducts.size() > 0
   *
   * @param {Array} prefixIndices - Already selected indices (0..currentDim-1)
   * @param {number} currentDim - Dimension to fill
   * @returns {boolean}
   */
  hasValidCombinationForPrefix(prefixIndices, currentDim) {
    // Base case: we've selected all dimensions
    if (currentDim >= this.config.dimensions.length) {
      const comboKey = prefixIndices.join(this.comboSeparator);
      // If combination exists in config, it's valid
      return this.config.combinations[comboKey] !== undefined;
    }

    // Recursive case: try each option for current dimension
    const dimension = this.config.dimensions[currentDim];
    for (let optIndex = 0; optIndex < dimension.options.length; optIndex++) {
      const testIndices = [...prefixIndices, optIndex];
      if (this.hasValidCombinationForPrefix(testIndices, currentDim + 1)) {
        return true; // Early exit on first valid combination
      }
    }

    return false;
  }

  /**
   * Check if ANY combination EXISTS for a given prefix (ignores stock/availability)
   * This is used for "compatibility" checks - does the option even exist in the product config?
   * Unlike hasValidCombinationForPrefix, this doesn't require variant.available = true
   *
   * @param {Array<number>} prefixIndices - Array of selected indices for dimensions 0 to currentDim-1
   * @param {number} currentDim - Dimension to fill
   * @returns {boolean} - True if any combination exists (regardless of availability)
   */
  hasCombinationExistsForPrefix(prefixIndices, currentDim) {
    // Base case: we've selected all dimensions
    if (currentDim >= this.config.dimensions.length) {
      const comboKey = prefixIndices.join(this.comboSeparator);
      // If combination exists in config, it's valid - that's the source of truth
      return this.config.combinations[comboKey] !== undefined;
    }

    // Recursive case: try each option for current dimension
    const dimension = this.config.dimensions[currentDim];
    for (let optIndex = 0; optIndex < dimension.options.length; optIndex++) {
      const testIndices = [...prefixIndices, optIndex];
      if (this.hasCombinationExistsForPrefix(testIndices, currentDim + 1)) {
        return true; // Early exit on first existing combination
      }
    }

    return false;
  }

  getVariantForOption(dimIndex, optIndex) {
    const testIndices = [...this.selectedIndices];
    testIndices[dimIndex] = optIndex;
    const comboKey = testIndices.join(this.comboSeparator);
    const skuIndex = this.config.combinations[comboKey];
    if (skuIndex === undefined) return null;

    const sku = this.config.skus[skuIndex];
    return this.variantsBySku[sku] || null;
  }

  /**
   * Get the currently selected variant based on selectedIndices
   */
  getCurrentVariant() {
    const comboKey = this.selectedIndices.join(this.comboSeparator);
    const skuIndex = this.config.combinations[comboKey];
    if (skuIndex === undefined) return null;

    const sku = this.config.skus[skuIndex];
    return this.variantsBySku[sku] || null;
  }

  /**
   * Attach event listeners only to dimension elements (not modal)
   * Used during selective rendering when modal is open
   */
  attachDimensionEventListeners() {
    const dimensionsContainer = this.container.querySelector('.v2-dimensions');
    if (!dimensionsContainer) return;

    // Attach listeners only to option buttons within dimensions (not modal)
    dimensionsContainer.querySelectorAll('[data-option]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const dimIndex = parseInt(e.currentTarget.dataset.dimension);
        const optIndex = parseInt(e.currentTarget.dataset.option);
        this.selectOption(dimIndex, optIndex);
      });

      // Only add hover preview on devices that support hover (not touch)
      if (this.supportsHover()) {
        // Add hover preview for main product image and dimension label
        btn.addEventListener('mouseenter', (e) => {
          const dimIndex = parseInt(e.currentTarget.dataset.dimension);
          const optIndex = parseInt(e.currentTarget.dataset.option);
          this.previewVariantImage(dimIndex, optIndex);
          this.previewDimensionLabel(dimIndex, optIndex);
          this.showTooltip(e.currentTarget);
          this.scheduleHoverPrefetch(dimIndex, optIndex);
        });

        btn.addEventListener('mouseleave', (e) => {
          const dimIndex = parseInt(e.currentTarget.dataset.dimension);
          this.restoreSelectedVariantImage();
          this.restoreDimensionLabel(dimIndex);
          this.hideTooltip(e.currentTarget);
          this.cancelHoverPrefetch();
        });
      }
    });

    // Dropdown change events for color/finish dimensions without images
    dimensionsContainer.querySelectorAll('.v2-dropdown').forEach(dropdown => {
      dropdown.addEventListener('change', (e) => {
        const dimIndex = parseInt(e.currentTarget.dataset.dimension);
        const optIndex = parseInt(e.currentTarget.value);
        this.selectOption(dimIndex, optIndex);
      });
    });

    // Native select change events (mobile)
    dimensionsContainer.querySelectorAll('.v2-native-select').forEach(select => {
      select.addEventListener('change', (e) => {
        const dimIndex = parseInt(e.currentTarget.dataset.dimension);
        const optIndex = parseInt(e.currentTarget.value);
        this.selectOption(dimIndex, optIndex);
      });
    });

    // "Show more options" button - reveals unavailable options for that dimension
    dimensionsContainer.querySelectorAll('[data-show-all-dimension]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const dimIndex = e.currentTarget.dataset.showAllDimension;
        const unavailableSection = this.container.querySelector(`[data-dimension-unavailable="${dimIndex}"]`);
        if (unavailableSection) {
          unavailableSection.hidden = false;
          e.currentTarget.hidden = true;
        }
      });
    });

    // Only add hover highlighting on devices that support hover (not touch)
    if (this.supportsHover()) {
      // Hover on "other" options highlights the affected option in another dimension
      dimensionsContainer.querySelectorAll('.v2-option--other[data-affects-dim]').forEach(btn => {
        btn.addEventListener('mouseenter', (e) => {
          const affectedDim = e.currentTarget.dataset.affectsDim;
          const affectedOpt = e.currentTarget.dataset.affectsOpt;
          // Find and highlight the button that would be selected
          const targetBtn = this.container.querySelector(
            `[data-dimension="${affectedDim}"][data-option="${affectedOpt}"]:not(.v2-option--other)`
          );
          if (targetBtn) {
            targetBtn.classList.add('v2-option--will-change');
          }
        });

        btn.addEventListener('mouseleave', (_e) => {
          // Remove all highlights
          this.container.querySelectorAll('.v2-option--will-change').forEach(el => {
            el.classList.remove('v2-option--will-change');
          });
        });
      });
    }

    // "Show less" button - hides the unavailable options again
    dimensionsContainer.querySelectorAll('[data-hide-dimension]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const dimIndex = e.currentTarget.dataset.hideDimension;
        const unavailableSection = this.container.querySelector(`[data-dimension-unavailable="${dimIndex}"]`);
        const showBtn = this.container.querySelector(`[data-show-all-dimension="${dimIndex}"]`);
        if (unavailableSection) {
          unavailableSection.hidden = true;
        }
        if (showBtn) {
          showBtn.hidden = false;
        }
      });
    });

    // "View all" button - opens modal with all options
    dimensionsContainer.querySelectorAll('.v2-view-all-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent bubbling that might close the modal
        const dimIndex = parseInt(e.currentTarget.dataset.dimension);
        this.openOptionsModal(dimIndex);
      });
    });
  }

  attachEventListeners() {
    this.container.querySelectorAll('[data-option]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const dimIndex = parseInt(e.currentTarget.dataset.dimension);
        const optIndex = parseInt(e.currentTarget.dataset.option);
        this.selectOption(dimIndex, optIndex);
      });

      // Only add hover preview on devices that support hover (not touch)
      if (this.supportsHover()) {
        // Add hover preview for main product image and dimension label
        btn.addEventListener('mouseenter', (e) => {
          const dimIndex = parseInt(e.currentTarget.dataset.dimension);
          const optIndex = parseInt(e.currentTarget.dataset.option);
          this.previewVariantImage(dimIndex, optIndex);
          this.previewDimensionLabel(dimIndex, optIndex);
          this.showTooltip(e.currentTarget);
          this.scheduleHoverPrefetch(dimIndex, optIndex);
        });

        btn.addEventListener('mouseleave', (e) => {
          const dimIndex = parseInt(e.currentTarget.dataset.dimension);
          this.restoreSelectedVariantImage();
          this.restoreDimensionLabel(dimIndex);
          this.hideTooltip(e.currentTarget);
          this.cancelHoverPrefetch();
        });
      }
    });

    // Hide tooltips when tapping elsewhere on mobile
    // Store reference for cleanup to prevent memory leaks
    this.documentTouchHandler = (e) => {
      if (!e.target.closest('.v2-option, .v2-size, .v2-product-card')) {
        this.container.querySelectorAll('.v2-tooltip--visible').forEach(t => {
          t.classList.remove('v2-tooltip--visible');
        });
      }
    };
    document.addEventListener('touchstart', this.documentTouchHandler, { passive: true });

    // Native select change events (mobile)
    this.container.querySelectorAll('.v2-native-select').forEach(select => {
      select.addEventListener('change', (e) => {
        const dimIndex = parseInt(e.currentTarget.dataset.dimension);
        const optIndex = parseInt(e.currentTarget.value);
        this.selectOption(dimIndex, optIndex);
      });
    });

    // "Show more options" button - reveals unavailable options for that dimension
    this.container.querySelectorAll('[data-show-all-dimension]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const dimIndex = e.currentTarget.dataset.showAllDimension;
        const unavailableSection = this.container.querySelector(`[data-dimension-unavailable="${dimIndex}"]`);
        if (unavailableSection) {
          unavailableSection.hidden = false;
          e.currentTarget.hidden = true;
        }
      });
    });

    // Only add hover highlighting on devices that support hover (not touch)
    if (this.supportsHover()) {
      // Hover on "other" options highlights the affected option in another dimension
      this.container.querySelectorAll('.v2-option--other[data-affects-dim]').forEach(btn => {
        btn.addEventListener('mouseenter', (e) => {
          const affectedDim = e.currentTarget.dataset.affectsDim;
          const affectedOpt = e.currentTarget.dataset.affectsOpt;
          // Find and highlight the button that would be selected
          const targetBtn = this.container.querySelector(
            `[data-dimension="${affectedDim}"][data-option="${affectedOpt}"]:not(.v2-option--other)`
          );
          if (targetBtn) {
            targetBtn.classList.add('v2-option--will-change');
          }
        });

        btn.addEventListener('mouseleave', (_e) => {
          // Remove all highlights
          this.container.querySelectorAll('.v2-option--will-change').forEach(el => {
            el.classList.remove('v2-option--will-change');
          });
        });
      });
    }

    // "Show less" button - hides the unavailable options again
    this.container.querySelectorAll('[data-hide-dimension]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const dimIndex = e.currentTarget.dataset.hideDimension;
        const unavailableSection = this.container.querySelector(`[data-dimension-unavailable="${dimIndex}"]`);
        const showBtn = this.container.querySelector(`[data-show-all-dimension="${dimIndex}"]`);
        if (unavailableSection) {
          unavailableSection.hidden = true;
        }
        if (showBtn) {
          showBtn.hidden = false;
        }
      });
    });

    // "View all" button - opens modal with all options
    this.container.querySelectorAll('.v2-view-all-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent bubbling that might close the modal
        const dimIndex = parseInt(e.currentTarget.dataset.dimension);
        this.openOptionsModal(dimIndex);
      });
    });

    // Modal close button
    const modal = this.container.querySelector(`#v2-options-modal-${this.sectionId}`);
    if (modal) {
      const closeBtn = modal.querySelector('.v2-modal-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          this.closeOptionsModal();
        });
      }

      // Close on overlay click (outside panel)
      modal.addEventListener('click', (e) => {
        // Only close if clicking directly on the overlay (dark background)
        // not on the panel or any of its children
        if (e.target.classList.contains('v2-modal-overlay')) {
          this.closeOptionsModal();
        }
      });

      // Modal filter pill clicks (delegated)
      modal.addEventListener('click', (e) => {
        const pillBtn = e.target.closest('.v2-modal-pill');
        if (pillBtn && !pillBtn.disabled) {
          e.preventDefault();
          const dimIndex = parseInt(pillBtn.dataset.dimension);
          const optIndex = parseInt(pillBtn.dataset.option);
          const activeDim = parseInt(modal.dataset.dimension);

          // Select the option
          this.selectOption(dimIndex, optIndex);

          // Re-open the modal to refresh the grid
          this.openOptionsModal(activeDim);
        }

        // Handle Add to Cart button click
        const addToCartBtn = e.target.closest('.v2-modal-add-to-cart');
        if (addToCartBtn && !addToCartBtn.disabled) {
          e.preventDefault();
          this.addToCart();
          this.closeOptionsModal();
        }
      });

      // Modal option clicks (delegated) - update variant without closing
      modal.addEventListener('click', (e) => {
        const optionBtn = e.target.closest('.v2-modal-option');
        if (optionBtn && !optionBtn.classList.contains('v2-modal-option--unavailable')) {
          e.preventDefault(); // Prevent form submission/page refresh
          const dimIndex = parseInt(optionBtn.dataset.dimension);
          const optIndex = parseInt(optionBtn.dataset.option);
          this.selectOption(dimIndex, optIndex);
          // Don't close panel - let user keep browsing options
        }
      });

      // Close on Escape key (store reference for cleanup)
      this.modalEscHandler = (e) => {
        if (e.key === 'Escape' && modal.style.display === 'flex') {
          this.closeOptionsModal();
        }
      };
      document.addEventListener('keydown', this.modalEscHandler);
    }

    // Custom dropdown event listeners
    this.container.querySelectorAll('.v2-custom-dropdown').forEach(dropdown => {
      const trigger = dropdown.querySelector('.v2-custom-dropdown__trigger');
      const panel = dropdown.querySelector('.v2-custom-dropdown__panel');

      if (trigger && panel) {
        // Toggle dropdown on trigger click
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          const isOpen = panel.classList.contains('v2-custom-dropdown__panel--open');

          // Close all other dropdowns first
          this.container.querySelectorAll('.v2-custom-dropdown__panel--open').forEach(p => {
            p.classList.remove('v2-custom-dropdown__panel--open');
            p.closest('.v2-custom-dropdown').querySelector('.v2-custom-dropdown__trigger').classList.remove('v2-custom-dropdown__trigger--open');
            p.closest('.v2-custom-dropdown').querySelector('.v2-custom-dropdown__trigger').setAttribute('aria-expanded', 'false');
          });

          if (!isOpen) {
            panel.classList.add('v2-custom-dropdown__panel--open');
            trigger.classList.add('v2-custom-dropdown__trigger--open');
            trigger.setAttribute('aria-expanded', 'true');
          }
        });

        // Handle option clicks within dropdown
        panel.querySelectorAll('.v2-custom-dropdown__option').forEach(optBtn => {
          optBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            const dimIndex = parseInt(optBtn.dataset.dimension);
            const optIndex = parseInt(optBtn.dataset.option);

            // Close dropdown
            panel.classList.remove('v2-custom-dropdown__panel--open');
            trigger.classList.remove('v2-custom-dropdown__trigger--open');
            trigger.setAttribute('aria-expanded', 'false');

            // Select the option
            this.selectOption(dimIndex, optIndex);
          });

          // Only add hover preview on devices that support hover (not touch)
          if (this.supportsHover()) {
            // Add hover preview for dropdown options
            optBtn.addEventListener('mouseenter', (_e) => {
              const dimIndex = parseInt(optBtn.dataset.dimension);
              const optIndex = parseInt(optBtn.dataset.option);
              this.previewVariantImage(dimIndex, optIndex);
            });

            optBtn.addEventListener('mouseleave', (_e) => {
              this.restoreSelectedVariantImage();
            });
          }
        });
      }
    });

    // Close dropdowns when clicking outside
    this.documentClickHandler = (e) => {
      if (!e.target.closest('.v2-custom-dropdown')) {
        this.container.querySelectorAll('.v2-custom-dropdown__panel--open').forEach(panel => {
          panel.classList.remove('v2-custom-dropdown__panel--open');
          const dropdown = panel.closest('.v2-custom-dropdown');
          dropdown.querySelector('.v2-custom-dropdown__trigger').classList.remove('v2-custom-dropdown__trigger--open');
          dropdown.querySelector('.v2-custom-dropdown__trigger').setAttribute('aria-expanded', 'false');
        });
      }
    };
    document.addEventListener('click', this.documentClickHandler);

    // Close dropdowns on Escape key
    this.documentEscHandler = (e) => {
      if (e.key === 'Escape') {
        this.container.querySelectorAll('.v2-custom-dropdown__panel--open').forEach(panel => {
          panel.classList.remove('v2-custom-dropdown__panel--open');
          const dropdown = panel.closest('.v2-custom-dropdown');
          dropdown.querySelector('.v2-custom-dropdown__trigger').classList.remove('v2-custom-dropdown__trigger--open');
          dropdown.querySelector('.v2-custom-dropdown__trigger').setAttribute('aria-expanded', 'false');
        });
      }
    };
    document.addEventListener('keydown', this.documentEscHandler);

    // Accordion header click - toggle step
    this.container.querySelectorAll('[data-action="toggle-accordion"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const accordion = e.currentTarget.closest('.v2-accordion');
        const step = parseInt(accordion.dataset.step);
        this.toggleAccordionStep(step);
      });
    });

    // Next step button - advance to next accordion
    this.container.querySelectorAll('[data-action="next-step"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const dimIndex = parseInt(e.currentTarget.dataset.dimension);
        this.advanceToNextStep(dimIndex);
      });
    });

    // Back step button - go to previous accordion
    this.container.querySelectorAll('[data-action="prev-step"]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const dimIndex = parseInt(e.currentTarget.dataset.dimension);
        this.goToPreviousStep(dimIndex);
      });
    });

    // Summary mode "Change" button
    this.container.querySelectorAll('[data-action="enter-guided-mode"]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.enterGuidedMode();
      });
    });

    // Attach keyboard navigation for all radiogroups (WCAG 2.1 AA)
    this.attachKeyboardNavigation();
  }

  /**
   * Clean up event listeners to prevent memory leaks
   * Call this method when the component is being destroyed
   */
  destroy() {
    if (this.documentTouchHandler) {
      document.removeEventListener('touchstart', this.documentTouchHandler);
      this.documentTouchHandler = null;
    }
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler);
      this.documentClickHandler = null;
    }
    if (this.documentEscHandler) {
      document.removeEventListener('keydown', this.documentEscHandler);
      this.documentEscHandler = null;
    }
    if (this.modalEscHandler) {
      document.removeEventListener('keydown', this.modalEscHandler);
      this.modalEscHandler = null;
    }
    this.cancelHoverPrefetch();
    this.mainImageElement = null;
  }

  showTooltip(btn) {
    const tooltip = btn.querySelector('.v2-tooltip');
    if (tooltip) {
      tooltip.classList.add('v2-tooltip--visible');
    }
  }

  hideTooltip(btn) {
    const tooltip = btn.querySelector('.v2-tooltip');
    if (tooltip) {
      tooltip.classList.remove('v2-tooltip--visible');
    }
  }

  selectOption(dimIndex, optIndex) {
    // Track if this option was already selected (for double-click to advance)
    const wasAlreadySelected = this.selectedIndices[dimIndex] === optIndex;

    // CLICKABILITY RULES:
    // - Dimension 0: Always clickable (let users explore freely)
    // - Dimensions 1+: Block click if option is not compatible, show reason why
    if (dimIndex >= 1) {
      const isCompatible = this.isOptionCompatible(dimIndex, optIndex);
      if (!isCompatible) {
        // Show overlay with the specific selections that block this option
        const reason = this.getBlockingSelectionsText(dimIndex, optIndex);
        const clickedOptionName = this.extractVendorSku(this.config.dimensions[dimIndex]?.options[optIndex]?.label || '');
        this.showUnavailableOverlay(reason, clickedOptionName);

        // Pulse the compatible options to guide the user
        this.pulseCompatibleOptions(dimIndex, optIndex);

        // Announce unavailability to screen readers
        const dimLabel = this.config.dimensions[dimIndex]?.label;
        this.announceSelection(dimLabel, clickedOptionName, true);

        return; // Don't allow selection of incompatible options in dimensions 2+
      }
    }

    // Valid selection - hide any previous unavailability overlay
    this.hideUnavailableOverlay();

    // In guided mode, be more lenient - if we can find ANY valid combination, allow it
    // This handles cases where subsequent dimensions need to cascade
    const isAvailable = this.isOptionAvailable(dimIndex, optIndex);
    const validCombo = !isAvailable ? this.findValidCombinationWith(dimIndex, optIndex) : null;

    if (!isAvailable && !validCombo) {
      // No valid combination exists at all for this option
      return;
    }

    if (!isAvailable && validCombo) {
      // User selected an option that requires changes to other dimensions.
      // Honor the user's click and auto-select downstream dims so they aren't
      // forced to re-confirm choices the UI can make deterministically.

      // Find which DOWNSTREAM dimensions would need to change
      // Only check dims AFTER the clicked one - upstream dims should never be reset
      const affectedDims = [];
      for (let i = dimIndex + 1; i < this.config.dimensions.length; i++) {
        if (validCombo[i] !== this.selectedIndices[i]) {
          affectedDims.push(i);
        }
      }

      // Set the user's requested selection
      this.selectedIndices[dimIndex] = optIndex;

      // Smart cascade: always auto-select. Prefer validCombo's suggestion (known to form
      // a fully valid combination); fall back to the first compatible option.
      // Note: Since we only iterate downstream dims (dimIndex + 1), upstream dims are already protected
      for (const affectedDim of affectedDims) {
        const compatibleOptions = this.getCompatibleOptionsForDimension(affectedDim);
        if (compatibleOptions.length === 0) continue; // safety: nothing to pick
        const suggested = validCombo[affectedDim];
        const pick = (typeof suggested === 'number' && compatibleOptions.includes(suggested))
          ? suggested
          : compatibleOptions[0];
        this.selectedIndices[affectedDim] = pick;
        this.completedSteps.add(affectedDim); // Mark as completed for UI
      }

      // Announce the user's primary selection to screen readers
      const userDimLabel = this.config.dimensions[dimIndex]?.label;
      const userOptLabel = this.extractVendorSku(this.config.dimensions[dimIndex]?.options[optIndex]?.label || '');
      this.announceSelection(userDimLabel, userOptLabel);

      // Track this as a recent selection
      this.trackRecentSelection(dimIndex, optIndex);

      // All downstream dims auto-selected; no accordion needs to be re-opened.
      if (this.configuratorMode === 'guided') {
        // INP fast path: hybrid layout can update button states in-place
        // without rebuilding the entire picker DOM + re-attaching listeners.
        if (this.hybridLayout) {
          this.updateHybridButtonStates();
        } else if (this.isMobile()) {
          // Mobile non-hybrid: must re-render to update native selects
          this.currentStep = -1;
          this.render();
        } else {
          this.updateSelectionVisual(dimIndex, optIndex);
          this.render();
        }
      } else if (this.hybridLayout) {
        this.updateHybridButtonStates();
      } else {
        this.render();
      }
      this.updateVariant();
      return;
    }

    // Standard selection for available options
    this.selectedIndices[dimIndex] = optIndex;

    // Announce selection to screen readers
    const dimLabel = this.config.dimensions[dimIndex]?.label;
    const optLabel = this.extractVendorSku(this.config.dimensions[dimIndex]?.options[optIndex]?.label || '');
    this.announceSelection(dimLabel, optLabel);

    // Track this as a recent selection
    this.trackRecentSelection(dimIndex, optIndex);

    // CASCADING AUTO-SELECT: Check if subsequent selections are still compatible.
    // IMPORTANT: Dims 0 & 1 are PROTECTED - never touch them (users can freely explore).
    // Dims 2+ are auto-selected to the first compatible option on incompatibility so
    // users never have to re-confirm a choice the UI can make deterministically.
    let foundIncompatible = false;
    let firstResetDim = null;
    // Start at dim 1 minimum - never cascade-reset dim 0
    for (let i = Math.max(1, dimIndex + 1); i < this.config.dimensions.length; i++) {
      const currentSelection = this.selectedIndices[i];
      // Skip if already unselected
      if (currentSelection === -1) {
        continue;
      }

      // If we already found an incompatible dimension, reset all subsequent ones too
      // because their compatibility checks would use invalid (-1) prior selections
      const isCompatible = foundIncompatible ? false : this.isOptionCompatible(i, currentSelection);

      if (!isCompatible) {
        // Smart cascade: always auto-select the first compatible option
        const compatibleOptions = this.getCompatibleOptionsForDimension(i);
        if (compatibleOptions.length === 0) {
          // No valid option at all — mark unselected and stop cascading to prevent a bad state
          this.selectedIndices[i] = -1;
          this.completedSteps.delete(i);
          if (firstResetDim === null) firstResetDim = i;
          foundIncompatible = true;
        } else {
          this.selectedIndices[i] = compatibleOptions[0];
          this.completedSteps.add(i); // Mark as completed for UI
          foundIncompatible = false; // Valid selection made; downstream checks can continue normally
        }
      }
    }

    // In guided mode, update visual and handle accordion navigation
    if (this.configuratorMode === 'guided') {
      // Check if this is first confirmation BEFORE updateSelectionVisual marks it complete
      const isFirstConfirmation = !this.completedSteps.has(dimIndex);

      this.updateSelectionVisual(dimIndex, optIndex);

      // HYBRID MODE: Skip full render for simple selection changes
      // Full render causes DOM rebuild which can trigger spurious click events
      // when cursor position changes relative to new elements
      if (this.hybridLayout) {
        // For hybrid mode, just update button states in-place without full render
        this.updateHybridButtonStates();
      }
      // Mobile: collapse accordion after selection (acts like a select dropdown)
      else if (this.isMobile()) {
        this.currentStep = -1;
        this.render();
      }
      // Desktop: If a dimension was reset, open it so user can make a choice
      else if (firstResetDim !== null) {
        this.currentStep = this.getVisibleStepIndex(firstResetDim);
        this.render();
      }
      // Desktop: Advance to next step if:
      // 1. Clicking same option twice (standard double-click to confirm), OR
      // 2. This is a first confirmation (step wasn't completed before) - for "from listing" UX
      else if (wasAlreadySelected || isFirstConfirmation) {
        this.advanceToNextStep(dimIndex);
      }
    } else if (this.hybridLayout) {
      this.updateHybridButtonStates();
    } else {
      this.render();
    }
    this.updateVariant();
  }

  /**
   * Advance to the next accordion step, or collapse if on last step
   */
  advanceToNextStep(currentDimIndex) {
    const currentStepIndex = this.getVisibleStepIndex(currentDimIndex);
    const totalSteps = this.getVisibleStepCount();

    if (currentStepIndex < totalSteps - 1) {
      // Open next step
      this.currentStep = currentStepIndex + 1;
      this.render();
    } else {
      // Last step completed
      // If only 1 visible dimension, keep it open (match PDP UX)
      // Otherwise collapse all accordions
      if (totalSteps === 1) {
        // Single dimension - keep accordion open, just update visuals
        this.render();
      } else {
        // Multiple dimensions - collapse all
        this.currentStep = -1;
        this.render();
      }
    }
  }

  /**
   * Go back to the previous accordion step
   */
  goToPreviousStep(currentDimIndex) {
    const currentStepIndex = this.getVisibleStepIndex(currentDimIndex);

    if (currentStepIndex > 0) {
      // Open previous step
      this.currentStep = currentStepIndex - 1;
      this.render();
    }
  }

  /**
   * Update visual state of selected option in accordion
   */
  updateSelectionVisual(dimIndex, optIndex) {
    const dimensionsContainer = this.container.querySelector('.v2-dimensions');
    if (!dimensionsContainer) return;

    const stepIndex = this.getVisibleStepIndex(dimIndex);
    const accordionContent = dimensionsContainer.querySelector(`.v2-accordion[data-step="${stepIndex}"] .v2-accordion__content`);
    if (!accordionContent) return;

    // Remove selected class from all options in this dimension
    accordionContent.querySelectorAll('.v2-product-card--selected, .v2-option--selected, .v2-size--selected, .v2-image-card--selected').forEach(el => {
      el.classList.remove('v2-product-card--selected', 'v2-option--selected', 'v2-size--selected', 'v2-image-card--selected');
    });

    // Add selected class to clicked option
    const clickedOption = accordionContent.querySelector(`[data-option="${optIndex}"]`);
    if (clickedOption) {
      if (clickedOption.classList.contains('v2-product-card')) {
        clickedOption.classList.add('v2-product-card--selected');
      } else if (clickedOption.classList.contains('v2-size')) {
        clickedOption.classList.add('v2-size--selected');
      } else if (clickedOption.classList.contains('v2-image-card')) {
        clickedOption.classList.add('v2-image-card--selected');
      } else {
        clickedOption.classList.add('v2-option--selected');
      }
    }

    // Mark current step as completed so next step can be opened
    this.completedSteps.add(dimIndex);

    // Update the accordion header label with the new selection
    this.updateAccordionHeaderLabel(dimIndex, optIndex);

    // Unlock the next section visually
    this.unlockNextSection(dimIndex);
  }

  /**
   * Update the accordion header to show the selected option label
   */
  updateAccordionHeaderLabel(dimIndex, optIndex) {
    const dimensionsContainer = this.container.querySelector('.v2-dimensions');
    if (!dimensionsContainer) return;

    const stepIndex = this.getVisibleStepIndex(dimIndex);
    const accordion = dimensionsContainer.querySelector(`.v2-accordion[data-step="${stepIndex}"]`);
    if (!accordion) return;

    const dimension = this.config.dimensions[dimIndex];
    const selectedOption = dimension.options[optIndex];
    if (!selectedOption) return;

    const displayLabel = this.extractVendorSku(selectedOption.label);

    // Find or create the selection display in the header
    const header = accordion.querySelector('.v2-accordion__header');
    let selectionSpan = header.querySelector('.v2-accordion__selection');

    if (!selectionSpan) {
      // Create the selection span if it doesn't exist
      selectionSpan = document.createElement('span');
      selectionSpan.className = 'v2-accordion__selection';
      selectionSpan.innerHTML = `<span class="v2-accordion__value"></span>`;
      // Insert before the icon
      const icon = header.querySelector('.v2-accordion__icon');
      header.insertBefore(selectionSpan, icon);
    }

    // Update the label
    const valueSpan = selectionSpan.querySelector('.v2-accordion__value');
    if (valueSpan) {
      valueSpan.textContent = displayLabel;
    }
  }

  /**
   * Unlock the next accordion section after a selection is made
   */
  unlockNextSection(currentDimIndex) {
    const dimensionsContainer = this.container.querySelector('.v2-dimensions');
    if (!dimensionsContainer) return;

    const currentStepIndex = this.getVisibleStepIndex(currentDimIndex);
    const nextStepIndex = currentStepIndex + 1;
    const totalSteps = this.getVisibleStepCount();

    // No next section to unlock
    if (nextStepIndex >= totalSteps) return;

    const nextAccordion = dimensionsContainer.querySelector(`.v2-accordion[data-step="${nextStepIndex}"]`);
    if (!nextAccordion) return;

    // Remove locked state and add ready state
    if (nextAccordion.classList.contains('v2-accordion--locked')) {
      nextAccordion.classList.remove('v2-accordion--locked');
      nextAccordion.classList.add('v2-accordion--ready');

      // Enable the header button
      const header = nextAccordion.querySelector('.v2-accordion__header');
      if (header) {
        header.removeAttribute('disabled');
      }
    }
  }

  findValidCombinationWith(dimIndex, optIndex) {
    // Try to find a valid combination that includes the selected option
    // Strategy: Keep the selected option fixed and try different combinations for other dimensions
    const testIndices = [...this.selectedIndices];
    testIndices[dimIndex] = optIndex;

    // First, try with current selections for other dimensions
    const comboKey = testIndices.join(this.comboSeparator);
    // If combination exists in config, it's valid
    if (this.config.combinations[comboKey] !== undefined) {
      return testIndices;
    }

    // If that doesn't work, try all combinations of other dimensions
    // We'll use a recursive approach to try all possibilities
    return this.findCombinationRecursive(testIndices, dimIndex, 0);
  }

  /**
   * Find the default combination for hybrid mode pre-selection
   * Prefers the best-ranked in-stock variant, then the first in-stock one,
   * then the first combination in config (which exists and is valid by definition)
   * Returns array of indices for each dimension
   */
  findFirstValidCombination() {
    const numDimensions = this.config.dimensions.length;

    // First, try to find an in-stock variant from the variants we have loaded.
    // Ties keep the earlier combination, so unranked products open as before.
    let firstAvailable = null;
    let bestRanked = null;
    let bestRank = Infinity;
    for (const [comboKey, skuIndex] of Object.entries(this.config.combinations)) {
      const sku = this.config.skus[skuIndex];
      const variant = this.variantsBySku[sku];
      // Only check stock if variant is in cache
      if (variant && variant.available) {
        const indices = comboKey.split(this.comboSeparator).map(i => parseInt(i));
        if (indices.length === numDimensions) {
          if (!firstAvailable) firstAvailable = indices;
          const rank = this.getVariantRank(variant);
          if (rank !== null && rank < bestRank) {
            bestRank = rank;
            bestRanked = indices;
          }
        }
      }
    }
    if (bestRanked || firstAvailable) {
      return bestRanked || firstAvailable;
    }

    // Fallback: Use first combination from config (it's valid by definition)
    for (const [comboKey] of Object.entries(this.config.combinations)) {
      const indices = comboKey.split(this.comboSeparator).map(i => parseInt(i));
      if (indices.length === numDimensions) {
        return indices;
      }
    }

    // Ultimate fallback
    console.warn('[V2] No valid combination found, using default zeros');
    return this.config.dimensions.map(() => 0);
  }

  findCombinationRecursive(indices, lockedDim, currentDim) {
    // Skip the locked dimension
    if (currentDim === lockedDim) {
      if (currentDim + 1 >= this.config.dimensions.length) {
        // We've set all dimensions, check if this combo exists in config
        const comboKey = indices.join(this.comboSeparator);
        if (this.config.combinations[comboKey] !== undefined) {
          return indices;
        }
        return null;
      }
      return this.findCombinationRecursive(indices, lockedDim, currentDim + 1);
    }

    // We've checked all dimensions
    if (currentDim >= this.config.dimensions.length) {
      const comboKey = indices.join(this.comboSeparator);
      // If combination exists in config, it's valid
      if (this.config.combinations[comboKey] !== undefined) {
        return indices;
      }
      return null;
    }

    // Try each option for this dimension
    const dimension = this.config.dimensions[currentDim];
    for (let optIndex = 0; optIndex < dimension.options.length; optIndex++) {
      const testIndices = [...indices];
      testIndices[currentDim] = optIndex;

      const result = this.findCombinationRecursive(testIndices, lockedDim, currentDim + 1);
      if (result) {
        return result;
      }
    }

    return null;
  }

  /**
   * Find the first available variant that matches the current partial selection.
   * Used to preview images when user hasn't completed all selections.
   * @returns {Object|null} - First matching variant or null
   */
  findFirstMatchingVariant() {
    // Build a template from selected indices, preserving non-(-1) values
    // We want to find a variant that matches ALL user selections, filling in only the gaps
    const template = [...this.selectedIndices];

    // Count how many valid selections we have
    const validCount = template.filter(idx => idx !== -1).length;
    if (validCount === 0) return null;

    // Recursively find first valid combination, respecting existing selections
    return this.findFirstVariantWithTemplate(template, 0);
  }

  /**
   * Recursively find first variant matching a template (for image preview)
   * Template contains user selections (numbers) and gaps (-1)
   * Returns variant if in cache, null otherwise
   */
  findFirstVariantWithTemplate(template, currentDim) {
    // Base case: all dimensions filled
    if (currentDim >= this.config.dimensions.length) {
      const comboKey = template.join(this.comboSeparator);
      const skuIndex = this.config.combinations[comboKey];
      if (skuIndex === undefined) return null;

      const sku = this.config.skus[skuIndex];
      // Return variant if in cache (for preview), null if not
      return this.variantsBySku[sku] || null;
    }

    // If this dimension already has a selection, use it and recurse
    if (template[currentDim] !== -1) {
      return this.findFirstVariantWithTemplate(template, currentDim + 1);
    }

    // This dimension needs to be filled - try each option
    const dimension = this.config.dimensions[currentDim];
    for (let optIndex = 0; optIndex < dimension.options.length; optIndex++) {
      const testTemplate = [...template];
      testTemplate[currentDim] = optIndex;
      const variant = this.findFirstVariantWithTemplate(testTemplate, currentDim + 1);
      if (variant) return variant;
    }

    return null;
  }

  /**
   * Find a similar variant in the cache for preview when exact variant isn't cached.
   * Tries to match as many user selections as possible, relaxing from the last dimension.
   * @returns {Object|null} - Similar cached variant or null
   */
  findSimilarCachedVariant() {
    const numDimensions = this.config.dimensions.length;

    // Start with all selections, then progressively relax from the end
    // This prioritizes matching the first dimensions (e.g., blade span, housing finish)
    // over the last dimensions (e.g., light kit)
    for (let relaxFrom = numDimensions; relaxFrom >= 1; relaxFrom--) {
      // Create template with selections up to relaxFrom, -1 after
      const template = this.selectedIndices.map((idx, i) => (i < relaxFrom ? idx : -1));

      // Try to find a cached variant matching this template
      const variant = this.findFirstVariantWithTemplate(template, 0);
      if (variant) {
        return variant;
      }
    }

    return null;
  }

  updateVariant() {
    // Check for incomplete selections (any dimension set to -1)
    const hasIncompleteSelection = this.selectedIndices.some(idx => idx === -1);
    if (hasIncompleteSelection) {
      this.updateVariantInput(null);
      this.publishVariantChange(null);
      this.setIncomplete();

      // CRO: Still update media gallery with first matching variant for preview
      const previewVariant = this.findFirstMatchingVariant();
      if (previewVariant) {
        this.updateMediaGallery(previewVariant);
      }
      return;
    }

    const comboKey = this.selectedIndices.join(this.comboSeparator);
    const skuIndex = this.config.combinations[comboKey];

    if (skuIndex === undefined) {
      this.updateVariantInput(null);
      this.publishVariantChange(null);
      this.setUnavailable();
      return;
    }

    const sku = this.config.skus[skuIndex];
    const variant = this.variantsBySku[sku];

    if (!variant) {
      // Variant not in cache - try fetching from Storefront API if we haven't already
      if (!this.hasFetchedAllVariants && this.storefrontApiToken && !this.isFetchingVariants) {

        // Show loading state
        this.setLoadingState(true);

        // CRO: Try to show a preview image from a similar cached variant while loading
        const previewVariant = this.findSimilarCachedVariant();
        if (previewVariant) {
          this.updateMediaGallery(previewVariant);
        }

        // Fetch all variants and retry
        this.fetchAllVariantsFromStorefront().then(foundNewVariants => {
          this.setLoadingState(false);

          if (foundNewVariants) {
            // Retry the lookup now that we have more variants
            const retryVariant = this.variantsBySku[sku];

            if (retryVariant) {
              this.currentVariant = retryVariant;
              this.updateVariantInput(retryVariant);
              this.publishVariantChange(retryVariant);
              this.fetchAndUpdateProductInfo(retryVariant);
              return;
            }
          }

          // Still not found after fetch - mark as unavailable
          this.updateVariantInput(null);
          this.publishVariantChange(null);
          this.setUnavailable();
        });
        return; // Exit early - async handler will complete the update
      }

      // No Storefront API available or already fetched - mark as unavailable
      this.updateVariantInput(null);
      this.publishVariantChange(null);
      this.setUnavailable();
      return;
    }

    this.currentVariant = variant;
    this.updateVariantInput(variant);
    this.publishVariantChange(variant);
    this.fetchAndUpdateProductInfo(variant);
  }

  fetchAndUpdateProductInfo(variant) {
    if (!variant) return;

    // Skip fetching on initial page load - the server already rendered the correct variant data.
    // This prevents an infinite loop where: fetch → Analyzify triggers → Clear-Site-Data → page reload → fetch again
    // The exception is a bare URL that opens on the top seller: the server
    // rendered Shopify's first available variant, so price, SKU and ATC need
    // the normal update.
    const serverVariantId = this.product.selected_or_first_available_variant?.id;
    if (this.isInitialLoad && (!serverVariantId || variant.id === serverVariantId)) {
      // Still update media gallery for the initial variant
      this.updateMediaGallery(variant);
      return;
    }

    // ============================================
    // HYBRID PARALLEL LOADING STRATEGY
    // ============================================
    // Phase 1 (0ms): Instant update from cache - price, ATC button
    // Phase 2 (parallel): GraphQL for authoritative price/media + Section Rendering for Liquid data
    // Phase 3: Update remaining elements from Section Rendering
    //
    // This gives perceived instant response while ensuring data accuracy
    // ============================================

    // Abort any pending requests from previous variant selection
    if (this.fetchAbortController) {
      this.fetchAbortController.abort();
    }
    this.fetchAbortController = new AbortController();
    const { signal } = this.fetchAbortController;

    // PHASE 1: Instant update from cached variant data (no network, ~0ms)
    this.updatePriceInstant(variant);
    this.updateAvailabilityInstant(variant);
    this.updateMediaGallery(variant); // Use cached media data

    // Show shimmer loading states for elements that need Section Rendering
    this.showShimmerForLiquidElements();

    // Build URL to fetch section with selected variant
    // Include customer_type in URL for Cloudflare cache keying (B2B vs B2C have different content)
    // vo=1 (variant-only) signals Liquid to skip bulk variant media pre-rendering for smaller response
    const productUrl = this.container.closest('product-info')?.dataset?.url || window.location.pathname;
    // Alternate-template pages (view= URL param, e.g. Shoplift template tests)
    // must re-send view= here: Section Rendering resolves the template from the
    // request URL, so without it the section comes back from the default template.
    const templateView = new URLSearchParams(window.location.search).get('view');
    const requestUrl = `${productUrl}?variant=${variant.id}&section_id=${this.sectionId}&ct=${this.customerType}&vo=1${templateView ? `&view=${encodeURIComponent(templateView)}` : ''}`;

    // Check section cache first (keyed by variant ID + customer type)
    const cacheKey = `${this.product.id}-${variant.id}-${this.customerType}`;
    const cached = sectionRenderingCache.get(cacheKey);
    const now = Date.now();

    if (cached && (now - cached.timestamp) < this.sectionCacheTTL) {
      // Use cached HTML directly - skip parallel GraphQL since we have fresh data
      this.hideShimmerForLiquidElements();
      this.processSectionHtml(cached.html, variant);
      return;
    }

    // PHASE 2: Fire parallel requests
    // - GraphQL: Fast, authoritative price/availability/media
    // - Section Rendering: Slower, but has Liquid-dependent data (inventory status, badges, promos)
    const graphqlPromise = this.fetchVariantDataParallel(variant.id, signal);

    // A hover prefetch for this variant may already be in flight: take it over
    // rather than requesting the same section twice. Any other pending hover
    // prefetch is now stale.
    let sectionPromise;
    const prefetch = this.hoverPrefetch;
    if (prefetch?.cacheKey === cacheKey) {
      this.hoverPrefetch = null;
      clearTimeout(this.prefetchHoverTimer);
      signal.addEventListener('abort', () => prefetch.controller.abort(), { once: true });
      sectionPromise = prefetch.promise;
    } else {
      this.cancelHoverPrefetch();
      sectionPromise = fetch(requestUrl, { signal }).then(r => r.text());
    }

    // Handle GraphQL result (usually faster)
    // Note: Only use GraphQL for price/availability - media comes from custom metafield
    graphqlPromise.then(graphqlData => {
      if (graphqlData) {
        // Update price with authoritative API data
        this.updatePriceFromApi(graphqlData);
        // Media is NOT updated from GraphQL - we use the custom.media metafield instead
        // GraphQL returns variant.featured_image which may differ from curated media

      }
    }).catch(err => {
      // Ignore abort errors - they're expected when user switches variants quickly
      if (err.name === 'AbortError') return;
      // GraphQL failure is non-fatal - Section Rendering will provide the data
    });

    // Handle Section Rendering result (has Liquid-dependent data)
    sectionPromise
      .then(responseText => {
        // Cache the response
        sectionRenderingCache.set(cacheKey, {
          html: responseText,
          timestamp: Date.now()
        });

        // Clean up old cache entries (keep max 50 to prevent memory bloat)
        if (sectionRenderingCache.size > 50) {
          const oldestKey = sectionRenderingCache.keys().next().value;
          sectionRenderingCache.delete(oldestKey);
        }

        // Hide shimmers before processing
        this.hideShimmerForLiquidElements();

        // Process the section HTML (updates inventory, badges, promos, specs, etc.)
        this.processSectionHtml(responseText, variant);

      })
      .catch(error => {
        // Ignore abort errors - they're expected when user switches variants quickly
        if (error.name === 'AbortError') {
          return;
        }
        console.error('[V2] Error fetching product info:', error);
        // Hide shimmers on error
        this.hideShimmerForLiquidElements();
        // Clear loading state on error
        this.setLoadingState(false);
      });
  }

  processSectionHtml(responseText, variant) {
    const html = new DOMParser().parseFromString(responseText, 'text/html');

    // For quickview, the content is inside a <template> tag - extract it
    const templateEl = html.querySelector('#MainProduct-quick-view__content');
    let sourceDoc = html;
    if (templateEl) {
      // Create a new document from the template content so getElementById works
      const tempDiv = document.createElement('div');
      tempDiv.appendChild(templateEl.content.cloneNode(true));
      sourceDoc = new DOMParser().parseFromString(tempDiv.innerHTML, 'text/html');
    }

    this.updateProductElements(sourceDoc, variant);
  }

  /**
   * Prefetch section HTML for a variant on hover intent
   * Silently fetches and caches without updating UI
   * @param {number} dimIndex - Dimension index being hovered
   * @param {number} optIndex - Option index being hovered
   */
  prefetchVariantSection(dimIndex, optIndex) {
    // Build test indices for what variant this option would select
    const testIndices = [...this.selectedIndices];
    testIndices[dimIndex] = optIndex;
    const comboKey = testIndices.join(this.comboSeparator);
    const skuIndex = this.config.combinations[comboKey];

    if (skuIndex === undefined) return; // Invalid combination

    const sku = this.config.skus[skuIndex];
    const variant = this.variantsBySku[sku];

    // If variant not in cache yet, we can't prefetch (need Storefront API first)
    if (!variant) return;

    // Check if already cached (include customer type in cache key)
    const cacheKey = `${this.product.id}-${variant.id}-${this.customerType}`;
    const cached = sectionRenderingCache.get(cacheKey);
    const now = Date.now();

    if (cached && (now - cached.timestamp) < this.sectionCacheTTL) {
      // Already cached, nothing to do
      return;
    }

    // Prefetch silently (no loading state)
    // Include customer_type in URL for Cloudflare cache keying
    // vo=1 (variant-only) signals Liquid to skip bulk variant media pre-rendering for smaller response
    const productUrl = this.container.closest('product-info')?.dataset?.url || window.location.pathname;
    // Alternate-template pages (view= URL param, e.g. Shoplift template tests)
    // must re-send view= here: Section Rendering resolves the template from the
    // request URL, so without it the section comes back from the default template.
    const templateView = new URLSearchParams(window.location.search).get('view');
    const requestUrl = `${productUrl}?variant=${variant.id}&section_id=${this.sectionId}&ct=${this.customerType}&vo=1${templateView ? `&view=${encodeURIComponent(templateView)}` : ''}`;

    if (this.hoverPrefetch?.cacheKey === cacheKey) return;
    this.cancelHoverPrefetch();

    const controller = new AbortController();
    const promise = fetch(requestUrl, { signal: controller.signal })
      .then(response => response.text())
      .then(responseText => {
        sectionRenderingCache.set(cacheKey, {
          html: responseText,
          timestamp: Date.now()
        });

        // Clean up old cache entries
        if (sectionRenderingCache.size > 50) {
          const oldestKey = sectionRenderingCache.keys().next().value;
          sectionRenderingCache.delete(oldestKey);
        }
        return responseText;
      })
      .finally(() => {
        if (this.hoverPrefetch?.promise === promise) this.hoverPrefetch = null;
      });
    // Silent fail - prefetch is best-effort. A click that adopts the promise
    // handles its own rejection.
    promise.catch(() => {});
    this.hoverPrefetch = { cacheKey, controller, promise };
  }

  scheduleHoverPrefetch(dimIndex, optIndex) {
    clearTimeout(this.prefetchHoverTimer);
    this.prefetchHoverTimer = setTimeout(() => {
      this.prefetchVariantSection(dimIndex, optIndex);
    }, this.prefetchHoverDelay);
  }

  // Drop a hover prefetch the visitor moved away from. A prefetch a click has
  // already adopted is no longer in this.hoverPrefetch, so it keeps running.
  cancelHoverPrefetch() {
    clearTimeout(this.prefetchHoverTimer);
    if (this.hoverPrefetch) {
      this.hoverPrefetch.controller.abort();
      this.hoverPrefetch = null;
    }
  }

  updateProductElements(html, variant) {
    // Helper function to update element from fetched HTML
    const updateElement = (elementId, shouldHide = (_source) => false, shouldPulse = false) => {
      const source = html.getElementById(`${elementId}-${this.sectionId}`);
      const destination = document.getElementById(`${elementId}-${this.sectionId}`);

      if (source && destination) {
        destination.innerHTML = source.innerHTML;
        destination.classList.toggle('hidden', shouldHide(source));

        // Add fade animation for visual feedback on key elements
        if (shouldPulse && !shouldHide(source)) {
          destination.classList.remove('v2-content-updated');
          // Force reflow to restart animation
          void destination.offsetWidth;
          destination.classList.add('v2-content-updated');
          // Clean up after animation
          setTimeout(() => destination.classList.remove('v2-content-updated'), 400);
        }

      }
    };

    // Update product title (may change if variant has different short_name metafield)
    updateElement('ProductTitle');
    updateElement('ProductTitleMobile');

    // Update all product information elements (price and inventory get pulse animation)
    updateElement('price', () => false, true);
    updateElement('Sku', ({ classList }) => classList.contains('hidden'));
    updateElement('MainSku');
    updateElement('MobileSku');
    updateElement('MainBrand');
    updateElement('MobileVendor');
    updateElement('Vendor');
    updateElement('Barcode', ({ classList }) => classList.contains('hidden'));
    updateElement('Inventory', ({ innerText }) => innerText === '', true);
    updateElement('Badges', ({ classList }) => classList.contains('hidden'));
    updateElement('PricePerItem', ({ classList }) => classList.contains('hidden'));
    updateElement('Volume');

    // Update promotion alert (variant-specific promotions)
    updateElement('PromotionAlert', ({ classList }) => classList.contains('hidden'));

    // Featured bullets — per-variant "Key Features" list. Renders twice
    // (Slot B desktop / Slot D mobile) with CSS controlling which slot is
    // visible. Swap innerHTML from the fetched section so products with
    // >250 variants (which exceed Liquid's product.variants cap) still
    // update on variant change.
    updateElement('FeaturedBullets-Slot-B');
    updateElement('FeaturedBullets-Slot-D');

    // Restructure template renders Key Features through a separate snippet
    // (product-restructure-info-column.liquid → .rs-keyfeatures), which
    // doesn't share the FeaturedBullets-Slot-* IDs above. Update its wrapper
    // independently so variant changes refresh the bullets there too.
    updateElement('RsKeyFeatures');

    // Restructure template's per-variant short description
    // (product-restructure-info-column.liquid). v2 deliberately doesn't
    // publish optionValueSelectionChange, so product-info.js's
    // updateSourceFromDestination('ShortDescription', ...) never fires
    // for v2 products — update the wrapper directly here instead.
    updateElement('ShortDescription');

    // Per-variant disclaimer (custom.disclaimer_text). Same story as
    // ShortDescription: product-info.js only swaps it on
    // optionValueSelectionChange. An empty wrapper collapses via
    // .rs-variant-disclaimer:empty in pdp-restructure.css.
    updateElement('VariantDisclaimer');

    // Update product specifications
    this.updateProductSpecs(html);

    // Update add to cart button
    this.updateAddToCartButton(html, variant);

    // Update custom media config from fetched HTML (for variants beyond initial 250)
    // This ensures updateMediaGallery can find the config in the main document
    this.updateCustomMediaConfig(html, variant);

    // Update media gallery to show variant image
    this.updateMediaGallery(variant);

    // Update view details link with selected variant (for quick view)
    this.updateViewDetailsLink(variant);

    // Publish variant change event for other components (sticky ATC bar, etc.)
    if (window.FoxTheme && window.FoxTheme.pubsub) {
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: this.sectionId,
          variant: variant
        }
      });
    }

    // Clear loading state after all updates complete
    this.setLoadingState(false);

    // CRO: Update sticky add-to-cart bar with valid variant
    this.updateStickyBar(variant);

  }

  updateAddToCartButton(html, variant) {
    const sourceButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
    const targetButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

    if (!targetButton) return;

    const isDisabled = sourceButton ? sourceButton.hasAttribute('disabled') : !variant.available;
    const buttonText = targetButton.querySelector('span');

    // CRO: Remove the select-options styling and onclick handler when variant is valid
    targetButton.classList.remove('btn--select-options');
    targetButton.onclick = null;

    if (isDisabled) {
      targetButton.setAttribute('disabled', 'disabled');
      if (buttonText) {
        buttonText.textContent = variant.available ? 'Sold out' : 'Unavailable';
      }
    } else {
      targetButton.removeAttribute('disabled');
      if (buttonText && window.FoxTheme?.variantStrings?.addToCart) {
        buttonText.innerHTML = window.FoxTheme.variantStrings.addToCart;
      }
    }

  }

  /**
   * Extract and update custom media config from fetched HTML
   * For products with 250+ variants, the custom-media-config elements may not exist
   * in the initial page. This method extracts them from the fetched section HTML.
   * @param {Document} html - The parsed HTML document from section rendering
   * @param {Object} variant - The variant object
   */
  updateCustomMediaConfig(html, variant) {
    if (!variant) return;

    const configId = `custom-media-config-${variant.id}`;
    const sourceConfig = html.getElementById(configId);

    if (!sourceConfig) {
      return;
    }

    // Check if config already exists in main document
    let destConfig = document.getElementById(configId);

    if (destConfig) {
      // Update existing config
      destConfig.textContent = sourceConfig.textContent;
    } else {
      // Clone and append the config to the document body
      destConfig = sourceConfig.cloneNode(true);
      document.body.appendChild(destConfig);
    }
  }

  updateMediaGallery(variant) {
    if (!variant) return;

    const productInfo = this.container.closest('product-info');
    if (!productInfo) return;

    const mediaGallery = productInfo.querySelector('[id^="MediaGallery-"]');
    if (!mediaGallery) return;

    // Use the gallery's built-in setActiveMedia method if available
    if (typeof mediaGallery.setActiveMedia !== 'function') {
      return;
    }

    // Priority 0: Use storefrontMedia from Storefront API (for variants fetched via GraphQL).
    //
    // Shape note: variant.metafields.custom.media.value is the parsed metafield value.
    // In production it's typically [{ images: [{url, watermark}, ...] }, ...] — matching
    // what product-media-parser.liquid unwraps in the Priority 1 path. The previous
    // implementation here assumed a flat [{url, watermark}] array and silently broke the
    // gallery (reduced a 4-image variant to 1 empty wrapper object) when Priority 0 fired.
    //
    // We support both shapes: prefer .images when present (wrapped), fall back to flat.
    if (variant.storefrontMedia && Array.isArray(variant.storefrontMedia) && variant.storefrontMedia.length > 0) {
      let mediaUrls = [];

      // Wrapped shape: [{ images: [{url, watermark, ...}, ...] }]
      const firstItem = variant.storefrontMedia[0];
      if (firstItem && Array.isArray(firstItem.images) && firstItem.images.length > 0) {
        mediaUrls = firstItem.images
          .map(img => (img && (img.url || img.external_url)) || (typeof img === 'string' ? img : null))
          .filter(Boolean);
      } else {
        // Flat shape: [{url, watermark}, ...] or ["url", ...]
        mediaUrls = variant.storefrontMedia
          .map(item => (item && item.url) || (typeof item === 'string' ? item : null))
          .filter(Boolean);
      }

      if (mediaUrls.length > 0) {
        mediaGallery.setActiveMedia(variant, mediaUrls);
        return;
      }
    }

    // Priority 1: Get full media array from custom-media-config (variant metafield)
    const customMediaConfigEl = document.getElementById(`custom-media-config-${variant.id}`);
    if (customMediaConfigEl) {
      try {
        const customConfig = JSON.parse(customMediaConfigEl.textContent);
        if (customConfig.hasCustomMedia && customConfig.mediaObjects?.length > 0) {
          // Extract URLs from media objects
          const mediaUrls = customConfig.mediaObjects.map(obj => obj.external_url);
          if (mediaUrls.length > 0) {
            mediaGallery.setActiveMedia(variant, mediaUrls);
            return;
          }
        }
      } catch (e) {
        console.warn('[V2] Failed to parse custom media config:', e);
      }
    }

    // Priority 2: Use native featured_media
    if (variant.featured_media) {
      mediaGallery.setActiveMedia(variant, null);
    }
  }

  updateViewDetailsLink(variant) {
    if (!variant) return;

    const viewDetailsLink = document.getElementById(`ViewDetailsLink-${this.sectionId}`);
    if (!viewDetailsLink) return;

    const baseUrl = viewDetailsLink.dataset.baseUrl;
    if (!baseUrl) return;

    const newUrl = `${baseUrl}?variant=${variant.id}`;
    viewDetailsLink.href = newUrl;

  }

  updateProductSpecs(html) {
    const specsId = `ProductSpecs-${this.sectionId}`;
    const newSpecs = html.getElementById(specsId);
    const currentSpecs = document.getElementById(specsId);

    if (!currentSpecs) return;

    if (newSpecs && newSpecs.innerHTML.trim()) {
      // Capture accordion states before DOM swap
      const openAccordions = [];
      currentSpecs.querySelectorAll('details[data-category][open]').forEach((details) => {
        openAccordions.push(details.getAttribute('data-category'));
      });

      // Instant swap with new specs content
      currentSpecs.innerHTML = newSpecs.innerHTML;
      currentSpecs.style.display = '';

      // Restore accordion states after DOM swap
      if (openAccordions.length > 0) {
        openAccordions.forEach((category) => {
          const details = currentSpecs.querySelector(`details[data-category="${category}"]`);
          if (details) {
            details.setAttribute('open', '');
          }
        });
      }

    } else {
      // Hide specs section if variant has no specifications
      currentSpecs.style.display = 'none';

    }
  }

  // Loading spinner HTML for injection
  get loadingSpinnerHtml() {
    return `<div class="loading__spinner">
      <svg aria-hidden="true" focusable="false" class="spinner icon icon--extra-medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
        <circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle>
      </svg>
    </div>`;
  }

  setLoadingState(isLoading) {
    const price = document.getElementById(`price-${this.sectionId}`);
    const inventory = document.getElementById(`Inventory-${this.sectionId}`);

    if (isLoading) {
      // Add loading classes and inject spinners
      if (price) {
        price.classList.add('product__price-loading');
        if (!price.querySelector('.loading__spinner')) {
          price.insertAdjacentHTML('beforeend', this.loadingSpinnerHtml);
        }
      }
      if (inventory) {
        inventory.classList.add('product__inventory-loading');
        if (!inventory.querySelector('.loading__spinner')) {
          inventory.insertAdjacentHTML('beforeend', this.loadingSpinnerHtml);
        }
      }
    } else {
      // Remove loading classes and spinners
      if (price) {
        price.classList.remove('product__price-loading');
        price.querySelector('.loading__spinner')?.remove();
      }
      if (inventory) {
        inventory.classList.remove('product__inventory-loading');
        inventory.querySelector('.loading__spinner')?.remove();
      }
    }
  }

  setUnavailable() {
    // Clear any loading state first
    this.setLoadingState(false);

    // When no valid variant exists, show price range and guide user to select options
    const price = document.getElementById(`price-${this.sectionId}`);
    const inventory = document.getElementById(`Inventory-${this.sectionId}`);
    const sku = document.getElementById(`Sku-${this.sectionId}`);

    // On a ?variant= landing the server already rendered that variant's real
    // price, but this method can run mid-init before the variant resolves —
    // variants beyond the inline 250 arrive via an async Storefront fetch.
    // Keep the server render rather than flashing the placeholder at
    // paid-traffic and email landings; the async path re-renders on resolve.
    const keepServerRender = this.isInitialLoad
      && new URLSearchParams(window.location.search).has('variant');

    // CRO IMPROVEMENT: Show price range instead of hiding price
    // This reduces anxiety and helps users evaluate the product
    if (price && !keepServerRender) {
      price.classList.remove('hidden');

      // Get price range from product data
      const priceMin = this.product.price_min;
      const priceVaries = this.product.price_varies;

      if (priceMin && priceVaries) {
        // Format price (convert cents to dollars with currency symbol)
        const formattedPrice = this.formatMoney(priceMin);
        const fromText = window.FoxTheme?.variantStrings?.priceFrom || 'From';
        price.innerHTML = `
          <div class="f-price f-price--large">
            <div class="f-price__regular">
              <span class="f-price-item f-price-item--regular f-price-item--from">
                ${fromText} ${formattedPrice}
              </span>
            </div>
          </div>
        `;
      } else if (priceMin) {
        // Single price (no variation)
        const formattedPrice = this.formatMoney(priceMin);
        price.innerHTML = `
          <div class="f-price f-price--large">
            <div class="f-price__regular">
              <span class="f-price-item f-price-item--regular">${formattedPrice}</span>
            </div>
          </div>
        `;
      } else {
        // Fallback to placeholder
        const selectOptionsText = window.FoxTheme?.variantStrings?.selectOptionsForPrice || 'Select options to see pricing';
        price.innerHTML = `<span class="f-price__placeholder text-subtext">${selectOptionsText}</span>`;
      }
    }

    // Show placeholder for inventory instead of hiding
    if (inventory && !keepServerRender) {
      inventory.classList.remove('hidden');
      const inventoryElement = inventory.querySelector('.product__inventory') || inventory;
      const selectOptionsText = window.FoxTheme?.variantStrings?.selectOptionsForAvailability || 'Select options to see availability';
      inventoryElement.innerHTML = `<span class="product__inventory-placeholder text-subtext">${selectOptionsText}</span>`;
    }

    // Hide SKU when no valid variant
    if (sku && !keepServerRender) sku.classList.add('hidden');

    // CRO IMPROVEMENT: Dynamic button text showing which option to select
    // This guides users through the selection process and reduces confusion
    const addButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    if (addButton) {
      // Find the first dimension that needs selection
      const nextDimensionToSelect = this.getFirstUnselectedDimension();

      // Keep button enabled but with guiding text
      // This feels more interactive and less like a dead-end
      if (nextDimensionToSelect) {
        addButton.removeAttribute('disabled');
        addButton.classList.add('btn--select-options');
        const buttonText = addButton.querySelector('span');
        if (buttonText) {
          const selectText = window.FoxTheme?.variantStrings?.selectToAdd || 'Select';
          buttonText.textContent = `${selectText} ${nextDimensionToSelect}`;
        }

        // Make button scroll to the variant selector when clicked
        addButton.onclick = (e) => {
          e.preventDefault();
          const variantSelector = document.getElementById(`v2-variant-selector-${this.sectionId}`);
          if (variantSelector) {
            variantSelector.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Flash the dimension that needs selection
            const firstDimension = variantSelector.querySelector('.v2-dimension');
            if (firstDimension) {
              firstDimension.classList.add('v2-dimension--highlight');
              setTimeout(() => firstDimension.classList.remove('v2-dimension--highlight'), 2000);
            }
          }
        };
      } else {
        addButton.setAttribute('disabled', 'disabled');
        addButton.classList.remove('btn--select-options');
        const buttonText = addButton.querySelector('span');
        if (buttonText) {
          buttonText.textContent = window.FoxTheme?.variantStrings?.selectOptions || 'Select options';
        }
        addButton.onclick = null;
      }
    }

    // Update sticky add-to-cart bar if present
    this.updateStickyBar(null);

  }

  /**
   * Get the first dimension that hasn't been explicitly selected by the user
   * This helps guide users through the selection process
   */
  getFirstUnselectedDimension() {
    // Check each dimension - if it only has one option, it's auto-selected
    // Otherwise, we need to check if user has made a selection
    for (let i = 0; i < this.config.dimensions.length; i++) {
      const dim = this.config.dimensions[i];

      // Skip single-option dimensions (auto-selected)
      if (dim.options.length === 1) continue;

      // Check if this dimension is explicitly unselected (-1)
      if (this.selectedIndices[i] === -1) {
        return dim.label;
      }
    }
    return null;
  }

  /**
   * Set incomplete state - user needs to make additional selections
   * Delegates to setUnavailable which handles the UI appropriately
   */
  setIncomplete() {
    this.setUnavailable();
  }

  // ============================================
  // INSTANT UPDATE METHODS (Phase 1 of hybrid parallel loading)
  // These provide immediate UI feedback from cached variant data
  // while Section Rendering and GraphQL queries run in background
  // ============================================

  /**
   * Instantly update price from cached variant data (no network required)
   * Called immediately when variant changes for perceived instant response
   * @param {Object} variant - The variant object from variantsBySku cache
   */
  updatePriceInstant(variant) {
    if (!variant) return;

    const priceContainer = document.getElementById(`price-${this.sectionId}`);
    if (!priceContainer) return;

    // Format prices from cached variant
    const formattedPrice = this.formatMoney(variant.price);
    const hasComparePrice = variant.compare_at_price && variant.compare_at_price > variant.price;
    const formattedComparePrice = hasComparePrice ? this.formatMoney(variant.compare_at_price) : null;

    // Restructure A/B template uses a different price markup shape:
    //   .p-price-row > .p-price + .p-compare + .p-save-tag
    // (vs the standard .f-price wrapper used by everything else).
    // Injecting standard .f-price markup into the restructure container
    // briefly renders unstyled (no flex gap, no SAVE pill background)
    // until the SRA round-trip replaces it — visible flicker on every
    // variant change. Detect restructure and update its specific
    // elements by ID instead, so the chrome stays stable.
    const isRestructure = priceContainer.closest('.pdp--restructure') != null;
    if (isRestructure) {
      this.updatePriceInstantRestructure(variant, formattedPrice, hasComparePrice, formattedComparePrice);
      return;
    }

    // Build price HTML matching theme structure
    let priceHtml;
    if (hasComparePrice) {
      priceHtml = `
        <div class="f-price f-price--large f-price--on-sale">
          <div class="f-price__sale">
            <span class="visually-hidden">Sale price</span>
            <span class="f-price-item f-price-item--sale">${formattedPrice}</span>
          </div>
          <div class="f-price__compare">
            <span class="visually-hidden">Original price</span>
            <s class="f-price-item f-price-item--compare">${formattedComparePrice}</s>
          </div>
        </div>
      `;
    } else {
      priceHtml = `
        <div class="f-price f-price--large">
          <div class="f-price__regular">
            <span class="f-price-item f-price-item--regular">${formattedPrice}</span>
          </div>
        </div>
      `;
    }

    // Find the f-price element (may have promo badge after it)
    const existingPriceEl = priceContainer.querySelector('.f-price');
    if (existingPriceEl) {
      // Replace just the price element, preserving promo badge
      existingPriceEl.outerHTML = priceHtml.trim();
    } else {
      // Fallback: set entire container (rare case)
      const promoBadge = priceContainer.querySelector('.promo-badge');
      if (promoBadge) {
        priceContainer.innerHTML = priceHtml + promoBadge.outerHTML;
      } else {
        priceContainer.innerHTML = priceHtml;
      }
    }

  }

  /**
   * Restructure-specific instant price update: mutates the existing
   * .p-price / .p-compare / .p-save-tag elements by ID instead of
   * swapping the wrapper, so the existing CSS chrome stays applied
   * and there's no flicker.
   *
   * The campaign line (.campaign-line) is intentionally left alone —
   * it depends on promotion metaobject end_date which the cached
   * variant doesn't carry. The SRA round-trip refreshes it.
   */
  updatePriceInstantRestructure(variant, formattedPrice, hasComparePrice, formattedComparePrice) {
    const priceMain = document.getElementById(`priceMain-${this.sectionId}`);
    const priceCompare = document.getElementById(`priceCompare-${this.sectionId}`);
    const priceSave = document.getElementById(`priceSave-${this.sectionId}`);

    if (priceMain) {
      priceMain.textContent = formattedPrice;
      // Toggle .reg modifier: only when NOT on sale and NOT B2B
      // (the b2b-price class is set server-side and persists).
      const isB2b = priceMain.classList.contains('b2b-price');
      priceMain.classList.toggle('reg', !hasComparePrice && !isB2b);
    }

    if (priceCompare) {
      if (hasComparePrice) {
        priceCompare.textContent = formattedComparePrice;
        priceCompare.style.display = '';
      } else {
        priceCompare.style.display = 'none';
      }
    }

    if (priceSave) {
      if (hasComparePrice) {
        // Berger Rule of 100: render whichever is larger between
        // whole-dollar savings and integer percent savings. B2B
        // always shows dollars (.b2b-price persisted on priceMain).
        const isB2b = priceMain && priceMain.classList.contains('b2b-price');
        const saveCents = variant.compare_at_price - variant.price;
        const saveDollars = Math.floor(saveCents / 100);
        const savePct = Math.floor((saveCents * 100) / variant.compare_at_price);
        const usePctPrimary = !isB2b && savePct > saveDollars;
        const formattedSaveDollars = this.formatMoney(saveCents).replace(/\.00($|[^\d])/, '$1');
        const primaryCopy = usePctPrimary
          ? `Save ${savePct}%`
          : `Save ${formattedSaveDollars}`;
        priceSave.innerHTML = `<span class="save-cluster__primary">${primaryCopy}</span>`;
        priceSave.style.display = '';
      } else {
        priceSave.style.display = 'none';
      }
    }

  }

  /**
   * Instantly update ATC button availability from cached variant data
   * @param {Object} variant - The variant object from variantsBySku cache
   */
  updateAvailabilityInstant(variant) {
    if (!variant) return;

    const addButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    if (!addButton) return;

    const buttonText = addButton.querySelector('span');

    // Clear any previous select-options state
    addButton.classList.remove('btn--select-options');
    addButton.onclick = null;

    if (variant.available) {
      addButton.removeAttribute('disabled');
      if (buttonText && window.FoxTheme?.variantStrings?.addToCart) {
        buttonText.innerHTML = window.FoxTheme.variantStrings.addToCart;
      }
    } else {
      addButton.setAttribute('disabled', 'disabled');
      if (buttonText) {
        buttonText.textContent = window.FoxTheme?.variantStrings?.soldOut || 'Sold out';
      }
    }

  }

  /**
   * Show shimmer loading states for elements that require Section Rendering
   * (inventory, badges, promotions, specs - these need Liquid data)
   */
  showShimmerForLiquidElements() {
    // Inventory - show shimmer placeholder
    const inventory = document.getElementById(`Inventory-${this.sectionId}`);
    if (inventory) {
      inventory.classList.add('v2-shimmer-loading');
    }

    // Badges - show shimmer placeholder
    const badges = document.getElementById(`Badges-${this.sectionId}`);
    if (badges) {
      badges.classList.add('v2-shimmer-loading');
    }

    // Promotion alert - show shimmer placeholder
    const promoAlert = document.getElementById(`PromotionAlert-${this.sectionId}`);
    if (promoAlert) {
      promoAlert.classList.add('v2-shimmer-loading');
    }

    // Product specs - show shimmer placeholder
    const specs = document.getElementById(`ProductSpecs-${this.sectionId}`);
    if (specs) {
      specs.classList.add('v2-shimmer-loading');
    }

  }

  /**
   * Hide shimmer loading states after Section Rendering completes
   */
  hideShimmerForLiquidElements() {
    const elements = [
      `Inventory-${this.sectionId}`,
      `Badges-${this.sectionId}`,
      `PromotionAlert-${this.sectionId}`,
      `ProductSpecs-${this.sectionId}`
    ];

    elements.forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        el.classList.remove('v2-shimmer-loading');
      }
    });

  }

  // ============================================
  // PARALLEL GRAPHQL QUERIES (Phase 2 of hybrid parallel loading)
  // Fire these in parallel with Section Rendering for faster media updates
  // ============================================

  /**
   * Fetch variant data from Storefront API in parallel
   * Returns authoritative price/availability + media data
   * @param {string} variantGid - The variant's GraphQL ID (gid://shopify/ProductVariant/...)
   * @returns {Promise<Object>} - Object with price, availability, and media data
   */
  async fetchVariantDataParallel(variantGid, signal = null) {
    if (!this.storefrontApiToken) {
      return null;
    }

    // Convert numeric ID to GID format if needed
    const gid = variantGid.toString().startsWith('gid://')
      ? variantGid
      : `gid://shopify/ProductVariant/${variantGid}`;

    // Query only price/availability - media comes from custom metafield, not GraphQL
    const query = `
      query getVariantData($id: ID!) {
        node(id: $id) {
          ... on ProductVariant {
            id
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            availableForSale
            quantityAvailable
          }
        }
      }
    `;

    try {
      const fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontApiToken
        },
        body: JSON.stringify({
          query,
          variables: { id: gid }
        })
      };
      // Add abort signal if provided
      if (signal) {
        fetchOptions.signal = signal;
      }
      const response = await fetch(`https://${window.Shopify.shop}/api/2026-04/graphql.json`, fetchOptions);

      if (!response.ok) {
        throw new Error(`GraphQL request failed: ${response.status}`);
      }

      const data = await response.json();

      return data?.data?.node || null;
    } catch (error) {
      console.warn('[V2 GraphQL] Parallel fetch failed:', error);
      return null;
    }
  }

  /**
   * Update price from GraphQL API data (authoritative source)
   * Called after parallel GraphQL fetch completes
   * @param {Object} graphqlData - Data from fetchVariantDataParallel
   */
  updatePriceFromApi(graphqlData) {
    if (!graphqlData?.price) return;

    const priceContainer = document.getElementById(`price-${this.sectionId}`);
    if (!priceContainer) return;

    // Convert from dollars (API) to cents (theme format)
    const priceCents = Math.round(parseFloat(graphqlData.price.amount) * 100);
    const compareAtCents = graphqlData.compareAtPrice
      ? Math.round(parseFloat(graphqlData.compareAtPrice.amount) * 100)
      : null;

    const formattedPrice = this.formatMoney(priceCents);
    const hasComparePrice = compareAtCents && compareAtCents > priceCents;
    const formattedComparePrice = hasComparePrice ? this.formatMoney(compareAtCents) : null;

    // Build price HTML
    let priceHtml;
    if (hasComparePrice) {
      priceHtml = `
        <div class="f-price f-price--large f-price--on-sale">
          <div class="f-price__sale">
            <span class="visually-hidden">Sale price</span>
            <span class="f-price-item f-price-item--sale">${formattedPrice}</span>
          </div>
          <div class="f-price__compare">
            <span class="visually-hidden">Original price</span>
            <s class="f-price-item f-price-item--compare">${formattedComparePrice}</s>
          </div>
        </div>
      `;
    } else {
      priceHtml = `
        <div class="f-price f-price--large">
          <div class="f-price__regular">
            <span class="f-price-item f-price-item--regular">${formattedPrice}</span>
          </div>
        </div>
      `;
    }

    // Update price element
    const existingPriceEl = priceContainer.querySelector('.f-price');
    if (existingPriceEl) {
      existingPriceEl.outerHTML = priceHtml.trim();
    }

  }

  /**
   * Format money value from cents to currency string
   */
  formatMoney(cents) {
    if (!cents && cents !== 0) return '';

    // Use Shopify's money format if available
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, window.FoxTheme?.moneyFormat || '${{amount}}');
    }

    // Fallback formatting with proper comma separators
    const dollars = cents / 100;
    return dollars.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  }

  /**
   * Update sticky add-to-cart bar with current variant info
   */
  updateStickyBar(variant) {
    const stickyBar = document.querySelector('.sticky-add-to-cart');
    if (!stickyBar) return;

    const priceEl = stickyBar.querySelector('.sticky-add-to-cart__price');
    const buttonEl = stickyBar.querySelector('.sticky-add-to-cart__button');
    const variantEl = stickyBar.querySelector('.sticky-add-to-cart__variant');

    if (variant) {
      // Valid variant selected
      if (priceEl) {
        priceEl.textContent = this.formatMoney(variant.price);
        priceEl.classList.remove('sticky-add-to-cart__price--from');
      }
      if (buttonEl) {
        buttonEl.removeAttribute('disabled');
        buttonEl.textContent = window.FoxTheme?.variantStrings?.addToCart || 'Add to Cart';
        buttonEl.onclick = () => {
          document.getElementById(`ProductSubmitButton-${this.sectionId}`)?.click();
        };
      }
      if (variantEl) {
        variantEl.textContent = variant.title !== 'Default Title' ? variant.title : '';
      }
    } else {
      // No valid variant - show price range and guide text
      if (priceEl) {
        const priceMin = this.product.price_min;
        if (priceMin && this.product.price_varies) {
          const fromText = window.FoxTheme?.variantStrings?.priceFrom || 'From';
          priceEl.textContent = `${fromText} ${this.formatMoney(priceMin)}`;
          priceEl.classList.add('sticky-add-to-cart__price--from');
        } else if (priceMin) {
          priceEl.textContent = this.formatMoney(priceMin);
          priceEl.classList.remove('sticky-add-to-cart__price--from');
        }
      }
      if (buttonEl) {
        const nextDim = this.getFirstUnselectedDimension();
        if (nextDim) {
          buttonEl.removeAttribute('disabled');
          const selectText = window.FoxTheme?.variantStrings?.selectToAdd || 'Select';
          buttonEl.textContent = `${selectText} ${nextDim}`;
          buttonEl.onclick = (e) => {
            e.preventDefault();
            const variantSelector = document.getElementById(`v2-variant-selector-${this.sectionId}`);
            if (variantSelector) {
              variantSelector.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          };
        } else {
          buttonEl.setAttribute('disabled', 'disabled');
          buttonEl.textContent = window.FoxTheme?.variantStrings?.selectOptions || 'Select Options';
        }
      }
      if (variantEl) {
        variantEl.textContent = '';
      }
    }
  }

  updateVariantInput(variant) {
    const variantInput = document.querySelector(`#${this.productFormId} input[name="id"]`);
    if (variantInput) {
      variantInput.value = variant ? variant.id : '';
    }

    // Shop Pay Installments reads its own form's hidden id, not the main product
    // form's. The fast path never round-trips through product-info.js's
    // updateVariantInputs, so without this the banner keeps quoting the
    // previously selected variant — a financing figure against the wrong price.
    // <shopify-payment-terms> re-renders off the change event, not the value.
    const installmentInput = document.querySelector(
      `#product-form-installment-${this.sectionId} input[name="id"]`
    );
    if (installmentInput) {
      installmentInput.value = variant ? variant.id : '';
      installmentInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (variant) {
      const url = new URL(window.location);
      const hadVariantInUrl = url.searchParams.has('variant');

      // SEO: don't force ?variant= into the URL on initial page load when the
      // visitor (or Googlebot) didn't ask for one. Otherwise replaceState +
      // canonical-rewrite makes the bare URL look like a redirect to Google
      // ("Page with redirect" in Search Console) and the bare URL won't index.
      if (!this.isInitialLoad || hadVariantInUrl) {
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
        this.updateVariantMeta(url.href, variant);
      }
    }
  }

  /**
   * Update SEO meta tags when variant changes
   * Best practice: canonical and og:url should include variant param for distinct variants
   * @param {string} variantUrl - Full URL with variant param
   * @param {Object} variant - The selected variant
   */
  updateVariantMeta(variantUrl, variant) {
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.href = variantUrl;
    }

    // Update Open Graph URL
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.content = variantUrl;
    }

    // Update Open Graph image if variant has a featured image
    if (variant.featured_image?.src) {
      const ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage) {
        ogImage.content = variant.featured_image.src;
      }
    }

    // Update Twitter image if variant has a featured image
    if (variant.featured_image?.src) {
      const twitterImage = document.querySelector('meta[name="twitter:image"]');
      if (twitterImage) {
        twitterImage.content = variant.featured_image.src;
      }
    }
  }

  publishVariantChange(variant) {
    if (!window.FoxTheme || !window.FoxTheme.pubsub) return;

    // NOTE: We intentionally do NOT publish optionValueSelectionChange here.
    // The V2 variant selector handles its own fetching and DOM updates via
    // fetchAndUpdateProductInfo(). Publishing optionValueSelectionChange would
    // trigger product-info.js to also fetch and update, causing a race condition
    // and infinite refresh loop on products with many variants.
    //
    // Instead, we only dispatch the custom v2:variantChange event for any
    // components that specifically need to listen to V2 changes.

    const customEvent = new CustomEvent('v2:variantChange', {
      detail: { variant: variant },
      bubbles: true
    });
    this.container.dispatchEvent(customEvent);
  }

  previewVariantImage(dimIndex, optIndex) {
    // HOVER PREVIEW:
    // - Dim 0: Always preview cleanly, no overlay (users can freely explore)
    // - Dims 1+: Show overlay with all current selections if combination doesn't exist
    if (dimIndex >= 1) {
      const isCompatible = this.isOptionCompatible(dimIndex, optIndex);

      if (!isCompatible) {
        // Show the specific selections that block this option, including the hovered option name
        const reason = this.getBlockingSelectionsText(dimIndex, optIndex);
        const hoveredOption = this.config.dimensions[dimIndex]?.options[optIndex];
        const hoveredOptionName = hoveredOption ? this.extractVendorSku(hoveredOption.label) : null;
        this.showUnavailableOverlay(reason, hoveredOptionName);
      } else {
        this.hideUnavailableOverlay();
      }
    } else {
      // Dim 0: never show overlay
      this.hideUnavailableOverlay();
    }

    let variant = this.getVariantForOption(dimIndex, optIndex);

    // If no exact match, find the closest valid combination
    if (!variant) {
      const closestCombo = this.findValidCombinationWith(dimIndex, optIndex);
      if (closestCombo) {
        const comboKey = closestCombo.join(this.comboSeparator);
        const skuIndex = this.config.combinations[comboKey];
        if (skuIndex !== undefined) {
          const sku = this.config.skus[skuIndex];
          variant = this.variantsBySku[sku];
        }
      }
    }

    if (!variant) {
      // Variant not in cache (likely beyond 250 limit) and Storefront API hasn't
      // hydrated yet — no preview source available, bail.
      return;
    }

    // Skip preview if hovering over the already-selected variant
    if (this.currentVariant && this.currentVariant.id === variant.id) {
      return;
    }

    // Store current variant for restore
    this.previewingVariant = variant;

    // Try to update all gallery images using custom media config
    const customMediaConfigEl = document.getElementById(`custom-media-config-${variant.id}`);
    if (customMediaConfigEl) {
      try {
        const customConfig = JSON.parse(customMediaConfigEl.textContent);
        if (customConfig.hasCustomMedia && customConfig.mediaObjects?.length > 0) {
          const mediaUrls = customConfig.mediaObjects.map(obj => obj.external_url);
          if (mediaUrls.length > 0) {
            // Update full gallery on hover
            const productInfo = this.container.closest('product-info');
            const mediaGallery = productInfo?.querySelector('[id^="MediaGallery-"]');
            if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
              mediaGallery.setActiveMedia(variant, mediaUrls);
              return;
            }
          }
        }
      } catch (e) {
        console.warn('[V2] Failed to parse custom media config for preview:', e);
      }
    }

    // Option B2 preview path: if the Storefront API fetch has populated
    // storefrontMedia for this variant (non-selected variants don't have a
    // custom-media-config-${id} element on V2 products since Liquid only emits
    // one for the selected variant), use it for the hover preview.
    // Supports both wrapped {images:[...]} and flat [{url}] shapes.
    if (Array.isArray(variant.storefrontMedia) && variant.storefrontMedia.length > 0) {
      let previewUrls = [];
      const firstItem = variant.storefrontMedia[0];
      if (firstItem && Array.isArray(firstItem.images) && firstItem.images.length > 0) {
        previewUrls = firstItem.images
          .map(img => (img && (img.url || img.external_url)) || (typeof img === 'string' ? img : null))
          .filter(Boolean);
      } else {
        previewUrls = variant.storefrontMedia
          .map(item => (item && item.url) || (typeof item === 'string' ? item : null))
          .filter(Boolean);
      }

      if (previewUrls.length > 0) {
        const productInfo = this.container.closest('product-info');
        const mediaGallery = productInfo?.querySelector('[id^="MediaGallery-"]');
        if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
          mediaGallery.setActiveMedia(variant, previewUrls);
          return;
        }
      }
    }

    // Fallback: Update just the first image using cached element
    const mainImage = this.getMainImageElement();

    if (!mainImage) {
      return;
    }

    // Fallback to featured_media (native Shopify media)
    if (variant.featured_media && variant.featured_media.preview_image) {
      const previewImage = variant.featured_media.preview_image;
      let imageUrl = previewImage.src;
      if (imageUrl.startsWith('//')) {
        imageUrl = 'https:' + imageUrl;
      }

      // Validate Shopify CDN URL
      imageUrl = this.validateImageUrl(imageUrl);
      if (!imageUrl) {
        return;
      }

      mainImage.src = imageUrl;
      if (previewImage.srcset) {
        mainImage.srcset = previewImage.srcset;
      } else {
        mainImage.removeAttribute('srcset');
      }
    } else {
    }
  }

  /**
   * Get or cache the main product image element for hover previews
   * Caches the reference for performance on repeated hover events
   * @returns {HTMLImageElement|null}
   */
  getMainImageElement() {
    // Return cached element if still in DOM
    if (this.mainImageElement && document.contains(this.mainImageElement)) {
      return this.mainImageElement;
    }

    // Query and cache the main image element
    this.mainImageElement = document.querySelector('.swiper-slide-active .product__media img') ||
                            document.querySelector('.product__media-gallery-viewer .swiper-slide-active img') ||
                            document.querySelector('.product__media-gallery-viewer .product__media img') ||
                            document.querySelector('.product__media-list .swiper-slide:first-child img') ||
                            document.querySelector('.product__media img');

    return this.mainImageElement;
  }

  /**
   * Show the unavailability overlay on the media gallery
   * Called when hovering over an unavailable variant option
   */
  showUnavailableOverlay() {
    const productInfo = this.container.closest('product-info');
    const overlay = productInfo?.querySelector('.product__media-unavailable-overlay');
    if (overlay) {
      // Use the configurable message from the theme editor (data attribute on overlay)
      const textEl = overlay.querySelector('.product__media-unavailable-text');
      if (textEl) {
        textEl.textContent = overlay.dataset.unavailableText || 'This combination isn\'t available from the manufacturer. Try another finish or size.';
      }
      overlay.classList.add('is-visible');
    }
  }

  /**
   * Hide the unavailability overlay on the media gallery
   * Called when mouse leaves variant option or on restore
   */
  hideUnavailableOverlay() {
    const productInfo = this.container.closest('product-info');
    const overlay = productInfo?.querySelector('.product__media-unavailable-overlay');
    if (overlay) {
      overlay.classList.remove('is-visible');
      // Reset the text to the configured default
      const textEl = overlay.querySelector('.product__media-unavailable-text');
      if (textEl) {
        textEl.textContent = overlay.dataset.unavailableText || 'This combination isn\'t available from the manufacturer. Try another finish or size.';
      }
    }
  }

  /**
   * Pulse options in OTHER dimensions that would make the clicked option compatible
   * Called when user clicks an unavailable option - guides them to valid alternatives
   * @param {number} clickedDimIndex - The dimension of the clicked option
   * @param {number} clickedOptIndex - The option index that was clicked
   */
  pulseCompatibleOptions(clickedDimIndex, clickedOptIndex) {

    const allPulsedButtons = [];

    // First, find which dimensions are actually BLOCKING the clicked option
    const blockingDimensions = [];
    for (let i = 0; i < this.config.dimensions.length; i++) {
      if (i === clickedDimIndex) continue;
      const selectedOptIndex = this.selectedIndices[i];
      if (selectedOptIndex === -1) continue; // Skip unselected dimensions

      // Check if this dimension's current selection blocks the clicked option
      const canCombine = this.canCombineOptions(clickedDimIndex, clickedOptIndex, i, selectedOptIndex);
      if (!canCombine) {
        blockingDimensions.push(i);
      }
    }

    // Only pulse options in the BLOCKING dimensions
    for (const blockingDimIndex of blockingDimensions) {
      const blockingDim = this.config.dimensions[blockingDimIndex];
      if (!blockingDim) continue;

      // Find which options in this blocking dimension WOULD work with the clicked option
      for (let otherOptIndex = 0; otherOptIndex < blockingDim.options.length; otherOptIndex++) {
        // Check if this option can combine with the clicked option
        if (this.canCombineOptions(clickedDimIndex, clickedOptIndex, blockingDimIndex, otherOptIndex)) {
          const btn = this.container.querySelector(
            `[data-dimension="${blockingDimIndex}"][data-option="${otherOptIndex}"]`
          );
          if (btn) {
            btn.classList.add('v2-pulse-available');
            allPulsedButtons.push(btn);
          }
        }
      }
    }

    // Remove pulse class after animation completes
    if (allPulsedButtons.length > 0) {
      setTimeout(() => {
        allPulsedButtons.forEach(btn => {
          btn.classList.remove('v2-pulse-available');
        });
      }, 1500);
    }
  }

  restoreSelectedVariantImage() {
    // Always hide the unavailability overlay on mouse leave
    this.hideUnavailableOverlay();

    // Skip restore if no preview was happening
    if (!this.previewingVariant) {
      return;
    }

    const comboKey = this.selectedIndices.join(this.comboSeparator);
    const skuIndex = this.config.combinations[comboKey];
    if (skuIndex === undefined) return;

    const sku = this.config.skus[skuIndex];
    const variant = this.variantsBySku[sku];

    // Clear previewing variant
    this.previewingVariant = null;

    // If the hovered variant is the same as selected, no need to restore
    if (!variant) return;

    // Try to restore all gallery images using custom media config
    const customMediaConfigEl = document.getElementById(`custom-media-config-${variant.id}`);
    if (customMediaConfigEl) {
      try {
        const customConfig = JSON.parse(customMediaConfigEl.textContent);
        if (customConfig.hasCustomMedia && customConfig.mediaObjects?.length > 0) {
          const mediaUrls = customConfig.mediaObjects.map(obj => obj.external_url);
          if (mediaUrls.length > 0) {
            const productInfo = this.container.closest('product-info');
            const mediaGallery = productInfo?.querySelector('[id^="MediaGallery-"]');
            if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
              mediaGallery.setActiveMedia(variant, mediaUrls);
              return;
            }
          }
        }
      } catch (e) {
        console.warn('[V2] Failed to parse custom media config for restore:', e);
      }
    }

    // Option B2 restore path: mirror the preview addition — if the selected
    // variant has storefrontMedia populated but no custom-media-config element
    // (can happen when the selected variant was reconciled from the Storefront
    // API on preselect rather than Liquid-rendered), restore the full gallery
    // from storefrontMedia instead of falling through to single-image swap
    // (which would leave the gallery tiles stuck on the hovered variant).
    // Supports both wrapped {images:[...]} and flat [{url}] shapes.
    if (Array.isArray(variant.storefrontMedia) && variant.storefrontMedia.length > 0) {
      let restoreUrls = [];
      const firstItem = variant.storefrontMedia[0];
      if (firstItem && Array.isArray(firstItem.images) && firstItem.images.length > 0) {
        restoreUrls = firstItem.images
          .map(img => (img && (img.url || img.external_url)) || (typeof img === 'string' ? img : null))
          .filter(Boolean);
      } else {
        restoreUrls = variant.storefrontMedia
          .map(item => (item && item.url) || (typeof item === 'string' ? item : null))
          .filter(Boolean);
      }

      if (restoreUrls.length > 0) {
        const productInfo = this.container.closest('product-info');
        const mediaGallery = productInfo?.querySelector('[id^="MediaGallery-"]');
        if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
          mediaGallery.setActiveMedia(variant, restoreUrls);
          return;
        }
      }
    }

    // Fallback: Update just the first image using cached element
    const mainImage = this.getMainImageElement();

    if (!mainImage) {
      return;
    }

    // Fallback to featured_media (native Shopify media)
    if (variant && variant.featured_media && variant.featured_media.preview_image) {
      const previewImage = variant.featured_media.preview_image;
      let imageUrl = previewImage.src;
      if (imageUrl.startsWith('//')) {
        imageUrl = 'https:' + imageUrl;
      }

      // Validate Shopify CDN URL
      imageUrl = this.validateImageUrl(imageUrl);
      if (!imageUrl) {
        return;
      }

      mainImage.src = imageUrl;
      if (previewImage.srcset) {
        mainImage.srcset = previewImage.srcset;
      } else {
        mainImage.removeAttribute('srcset');
      }
    }
  }

  /**
   * Preview the dimension label on hover (shows what value would be selected)
   */
  previewDimensionLabel(dimIndex, optIndex) {
    const dimension = this.config.dimensions[dimIndex];
    if (!dimension) return;

    const option = dimension.options[optIndex];
    if (!option) return;

    // Find the label element for this dimension by searching for option buttons
    // For hybrid mode, the container has v2-dimension--inline class
    const dimContainer = this.container.querySelector(`[data-dimension="${dimIndex}"]`);
    if (dimContainer) {
      const labelEl = dimContainer.querySelector('.v2-dimension__selected-value');
      if (labelEl) {
        // Store original value if not already stored
        if (!labelEl.dataset.originalValue) {
          labelEl.dataset.originalValue = labelEl.textContent;
        }
        // Update label with hovered option
        labelEl.textContent = this.extractVendorSku(option.label);
      }
    }
  }

  /**
   * Restore the dimension label after hover ends
   */
  restoreDimensionLabel(dimIndex) {
    // For hybrid mode, the container has v2-dimension--inline class with data-dimension attribute
    const dimContainer = this.container.querySelector(`[data-dimension="${dimIndex}"]`);
    if (dimContainer) {
      const labelEl = dimContainer.querySelector('.v2-dimension__selected-value');
      if (labelEl && labelEl.dataset.originalValue) {
        labelEl.textContent = labelEl.dataset.originalValue;
        delete labelEl.dataset.originalValue;
      }
    }
  }

  showError(message) {
    if (!this.container) return;
    this.container.innerHTML = `
      <div class="v2-error">
        <p><strong>⚠️ Configuration Error</strong></p>
        <p>${this.escapeHtml(message)}</p>
      </div>
    `;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Escape + fit a swatch/text-button subtitle for narrow pills:
   * compact spaced ranges ("225 – 350" → "225–350") so the common
   * room-size strings hold one line at 3-per-row mobile widths, and
   * make the last space non-breaking so a trailing unit ("sq ft")
   * can never wrap into an orphan line.
   */
  formatSubtitle(text) {
    let s = this.escapeHtml(text).replace(/\s*–\s*|\s+-\s+/g, '–⁠');
    // Glue the trailing unit only on 3+ word strings — a two-word
    // subtitle ('Large Rooms') glued into one unbreakable run clips
    // inside the swatch subtitle's two-line clamp instead of wrapping.
    if (s.split(' ').length > 2) s = s.replace(/ (?=\S+$)/, ' ');
    return s;
  }

  /**
   * Validate and sanitize image URLs
   * Only allows URLs from trusted origins (Shopify CDN, Cloudflare, etc.)
   * @param {string} url - The URL to validate
   * @returns {string|null} - The validated URL or null if invalid
   */
  validateImageUrl(url) {
    if (!url || typeof url !== 'string') return null;

    // Allowed origins for image URLs
    const allowedOrigins = [
      'cdn.shopify.com',
      'cdn.shopifycdn.net',
      'images.ctfassets.net',
      'imagedelivery.net',
      'res.cloudinary.com',
    ];

    // Allow Cloudflare proxy URLs if configured
    const cfConfig = this.config?.cloudflareImages || window.algoliaConfig?.cloudflareImages;
    if (cfConfig?.proxyDomain) {
      allowedOrigins.push(cfConfig.proxyDomain);
    }

    try {
      // Handle protocol-relative URLs
      const normalizedUrl = url.startsWith('//') ? 'https:' + url : url;
      const parsed = new URL(normalizedUrl);

      // Check against allowed origins
      const isAllowed = allowedOrigins.some(origin =>
        parsed.hostname === origin || parsed.hostname.endsWith('.' + origin)
      );

      if (!isAllowed) {
        return null;
      }

      return normalizedUrl;
    } catch (_e) {
      return null;
    }
  }

  /* ============================================
     HYBRID LAYOUT METHODS
     Lumens-style inline swatches + dropdowns
     ============================================ */

  /**
   * Render hybrid mode - inline layout with swatches and dropdowns
   * No price display here (existing product info handles that)
   */
  renderHybridMode() {
    // Debug: Log hybrid mode render with full state

    const dimensionsHtml = this.config.dimensions
      .map((dim, dimIndex) => this.renderHybridDimension(dim, dimIndex))
      .join('');
    const disclaimerHtml = this.renderImageDisclaimer();

    const html = `
      <div class="v2-dimensions v2-dimensions--hybrid">
        ${dimensionsHtml}
      </div>
      ${disclaimerHtml}
    `;

    this.container.innerHTML = html;
    this.attachHybridEventListeners();
  }

  /**
   * Render a single dimension in hybrid mode
   */
  renderHybridDimension(dimension, dimIndex) {
    // Hide single-option dimensions
    if (dimension.options.length === 1) {
      return '';
    }

    const selectedIndex = this.selectedIndices[dimIndex];
    const selectedOption = selectedIndex >= 0 ? dimension.options[selectedIndex] : null;
    const selectedLabel = selectedOption ? this.extractVendorSku(selectedOption.label) : '';
    const isColorDim = this.isColorDimension(dimension);

    // Check if should render as dropdown due to exceeding threshold
    let controlsHtml;
    if (this.shouldRenderAsDropdown(dimension)) {
      controlsHtml = this.renderHybridDropdownGroup(dimension, dimIndex, selectedIndex);
    } else if (isColorDim) {
      controlsHtml = this.renderSwatchGroup(dimension, dimIndex, selectedIndex);
    } else {
      controlsHtml = this.renderTextButtonGroup(dimension, dimIndex, selectedIndex);
    }

    return `
      <div class="v2-dimension--inline" data-dimension="${dimIndex}">
        <div class="v2-dimension__label">
          ${this.escapeHtml(dimension.label)}:
          <span class="v2-dimension__selected-value">${selectedLabel ? this.escapeHtml(selectedLabel) : ''}</span>
        </div>
        ${controlsHtml}
      </div>
    `;
  }

  /**
   * Render swatch buttons for a color/finish dimension
   */
  renderSwatchGroup(dimension, dimIndex, selectedIndex) {
    // Check if any prior dimensions are unselected - affects compatibility checks
    const hasPriorUnselected = this.selectedIndices.slice(0, dimIndex).some(idx => idx === -1);

    const swatches = this.sortedOptionEntries(dimension, dimIndex).map(({ option, optIndex }) => {
      const isSelected = optIndex === selectedIndex;

      // For swatch dimensions: always show all options (Lumens-style UX)
      // - If PRIOR dimensions unselected: can't determine, assume compatible
      // - If LATER dimensions unselected: only use isCompatible (checks if ANY valid combo exists)
      // - If ALL dimensions selected: use isCompatible (checks if combination exists)
      const isCompatible = hasPriorUnselected ? true : this.isOptionCompatible(dimIndex, optIndex);
      const label = this.extractVendorSku(option.label);

      // CLICKABILITY RULES:
      // - Dim 0: Always clickable, NO unavailable styling (let users explore freely)
      // - Dims 1+: Show unavailable styling for incompatible options (click blocked in JS)
      // Only check compatibility (combination exists), not stock status
      const showUnavailable = dimIndex >= 1 && !isCompatible;

      const classes = [
        'v2-swatch-btn',
        isSelected ? 'v2-swatch-btn--selected' : '',
        showUnavailable ? 'v2-swatch-btn--unavailable' : ''
      ].filter(Boolean).join(' ');

      // Priority 1: Use explicit hex color from swatch.color in config
      // Priority 2: Fall back to getColorFromLabel() for legacy configs
      const colorStyle = option.swatch?.color
        ? `background: ${option.swatch.color}`
        : this.getColorFromLabel(label);
      const contentHtml = `<span class="v2-swatch-btn__color" style="${colorStyle}"></span>`;

      // Use tooltip from config if available, otherwise use label
      const tooltipText = option.tooltip || label;

      // Subtitle: persistent text below swatch (e.g. room size for blade span)
      const subtitleText = option.subtitle || '';

      // Roving tabindex: selected option gets tabindex="0", others get "-1"
      // If nothing selected in this dimension, first compatible option gets "0"
      const shouldBeTabFocusable = isSelected || (selectedIndex === -1 && isCompatible && optIndex === dimension.options.findIndex((_, i) => hasPriorUnselected ? true : this.isOptionCompatible(dimIndex, i)));

      // Include "(unavailable)" in aria-label for screen readers
      const ariaLabel = showUnavailable
        ? `${this.escapeHtml(label)} (unavailable)`
        : this.escapeHtml(label);

      // Wrap button + subtitle in a container so subtitle renders below the fixed-size swatch
      const subtitleId = subtitleText ? `v2-swatch-subtitle-${this.sectionId}-${dimIndex}-${optIndex}` : '';
      const subtitleHtml = subtitleText
        ? `<span class="v2-swatch-btn__subtitle" id="${subtitleId}">${this.formatSubtitle(subtitleText)}</span>`
        : '';

      return `
        <div class="v2-swatch-item${subtitleText ? ' v2-swatch-item--has-subtitle' : ''}" role="none">
          <button
            type="button"
            role="radio"
            class="${classes}"
            data-dimension="${dimIndex}"
            data-option="${optIndex}"
            aria-label="${ariaLabel}"
            aria-checked="${isSelected}"
            ${subtitleText ? `aria-describedby="${subtitleId}"` : ''}
            ${!isCompatible && dimIndex >= 1 ? 'aria-disabled="true"' : ''}
            tabindex="${shouldBeTabFocusable ? '0' : '-1'}"
          >
            ${contentHtml}
            <span class="v2-swatch-btn__tooltip">${this.escapeHtml(tooltipText)}</span>
          </button>
          ${subtitleHtml}
        </div>
      `;
    }).join('');

    // Add disclaimer below group if provided in config
    const disclaimerText = dimension.disclaimer || '';
    const disclaimerId = disclaimerText ? `v2-swatch-disclaimer-${this.sectionId}-${dimIndex}` : '';
    const disclaimerHtml = disclaimerText
      ? `<p class="v2-subtitle-disclaimer" id="${disclaimerId}">${this.escapeHtml(disclaimerText)}</p>`
      : '';

    return `<div class="v2-swatch-group" role="radiogroup" aria-label="${this.escapeHtml(dimension.label)}" aria-required="true" ${disclaimerId ? `aria-describedby="${disclaimerId}"` : ''}>${swatches}</div>${disclaimerHtml}`;
  }

  /**
   * Get a background color/gradient style from a finish label
   * Supports solid colors and split colors (e.g., "Silver/American Walnut")
   */
  getColorFromLabel(label) {
    const labelLower = label.toLowerCase();

    // Common finish color mappings
    const colorMap = {
      // Blacks
      'matte black': '#1a1a1a',
      'black': '#1a1a1a',
      'midnight black': '#0d0d0d',
      // Whites
      'white': '#f5f5f5',
      'matte white': '#f0f0f0',
      // Silvers/Steels
      'polished nickel': '#d4d4d4',
      'brushed nickel': '#c4c4c4',
      'satin nickel': '#c8c8c8',
      'nickel': '#c0c0c0',
      'silver': '#c0c0c0',
      'brushed steel': '#a8a8a8',
      'painted brushed steel': '#9a9a9a',
      'stainless': '#c8c8c8',
      'chrome': '#e8e8e8',
      'polished chrome': '#e0e0e0',
      // Pewters/Grays
      'aged pewter': '#8b8682',
      'pewter': '#8b8878',
      'antique pewter': '#7d7872',
      'gray': '#808080',
      'grey': '#808080',
      'light grey weathered oak': '#a9a9a9',
      'weathered oak': '#9a8b7a',
      // Bronzes
      'roman bronze': '#6b5344',
      'bronze': '#8b6914',
      'oil rubbed bronze': '#5c4033',
      'antique bronze': '#665544',
      'dark bronze': '#4a3728',
      // Brass/Gold
      'brass': '#d4af37',
      'antique brass': '#c9a227',
      'gold': '#ffd700',
      'aged brass': '#b5a642',
      'burnished brass': '#aa8833',
      // Woods/Browns
      'walnut': '#5d432c',
      'american walnut': '#5d432c',
      'dark walnut': '#4a3520',
      'natural': '#deb887',
      'wood': '#8b4513',
      'oak': '#c4a35a',
      'mahogany': '#c04000',
      'cherry': '#990000',
      // Copper
      'copper': '#b87333',
      'antique copper': '#a66d3c'
    };

    // Check for split color labels (e.g., "Silver/American Walnut", "Bronze/American Walnut")
    if (labelLower.includes('/')) {
      const parts = labelLower.split('/');
      if (parts.length === 2) {
        const color1 = this.findColorMatch(parts[0].trim(), colorMap);
        const color2 = this.findColorMatch(parts[1].trim(), colorMap);
        if (color1 && color2) {
          // Create a diagonal split gradient
          return `background: linear-gradient(135deg, ${color1} 50%, ${color2} 50%)`;
        }
      }
    }

    // Try to find a matching color
    const matchedColor = this.findColorMatch(labelLower, colorMap);
    if (matchedColor) {
      return `background-color: ${matchedColor}`;
    }

    // Default gray for unknown colors
    return 'background-color: #cccccc';
  }

  /**
   * Find the best color match for a label part
   */
  findColorMatch(labelPart, colorMap) {
    // First try exact match
    if (colorMap[labelPart]) {
      return colorMap[labelPart];
    }

    // Then try partial match (longer keys first for specificity)
    const sortedKeys = Object.keys(colorMap).sort((a, b) => b.length - a.length);
    for (const key of sortedKeys) {
      if (labelPart.includes(key)) {
        return colorMap[key];
      }
    }

    return null;
  }

  /**
   * Render text button group for non-color dimensions (replaces dropdown)
   * Shows value with optional tooltip below
   */
  renderTextButtonGroup(dimension, dimIndex, selectedIndex) {
    // Check if any prior dimensions are unselected - affects compatibility checks
    const hasPriorUnselected = this.selectedIndices.slice(0, dimIndex).some(idx => idx === -1);

    const buttons = this.sortedOptionEntries(dimension, dimIndex).map(({ option, optIndex }) => {
      const isSelected = optIndex === selectedIndex;

      // - If PRIOR dimensions unselected: can't determine, assume compatible
      // - If LATER dimensions unselected: only use isCompatible (checks if ANY valid combo exists)
      // - If ALL dimensions selected: use isCompatible (checks if combination exists)
      const isCompatible = hasPriorUnselected ? true : this.isOptionCompatible(dimIndex, optIndex);
      const label = this.extractVendorSku(option.label);
      const tooltip = option.tooltip || '';  // From new config format
      const subtitleText = option.subtitle || '';

      // CLICKABILITY RULES:
      // - Dim 0: Always clickable, NO unavailable styling (let users explore freely)
      // - Dims 1+: Show unavailable styling for incompatible options (click blocked in JS)
      // Only check compatibility (combination exists), not stock status
      const showUnavailable = dimIndex >= 1 && !isCompatible;

      const classes = [
        'v2-text-btn',
        isSelected ? 'v2-text-btn--selected' : '',
        showUnavailable ? 'v2-text-btn--unavailable' : ''
      ].filter(Boolean).join(' ');

      // Roving tabindex: selected option gets tabindex="0", others get "-1"
      // If nothing selected in this dimension, first compatible option gets "0"
      const shouldBeTabFocusable = isSelected || (selectedIndex === -1 && isCompatible && optIndex === dimension.options.findIndex((_, i) => hasPriorUnselected ? true : this.isOptionCompatible(dimIndex, i)));

      // Include "(unavailable)" in aria-label for screen readers
      const ariaLabel = showUnavailable
        ? `${this.escapeHtml(label)} (unavailable)`
        : this.escapeHtml(label);

      const subtitleHtml = subtitleText
        ? `<span class="v2-text-btn__subtitle">${this.formatSubtitle(subtitleText)}</span>`
        : '';

      return `
        <button
          type="button"
          role="radio"
          class="${classes}${subtitleText ? ' v2-text-btn--has-subtitle' : ''}"
          data-dimension="${dimIndex}"
          data-option="${optIndex}"
          aria-label="${ariaLabel}"
          aria-checked="${isSelected}"
          ${showUnavailable ? 'aria-disabled="true"' : ''}
          tabindex="${shouldBeTabFocusable ? '0' : '-1'}"
        >
          <span class="v2-text-btn__value">${this.escapeHtml(label)}</span>
          ${tooltip ? `<span class="v2-text-btn__tooltip">${this.escapeHtml(tooltip)}</span>` : ''}
          ${subtitleHtml}
        </button>
      `;
    }).join('');

    // Add disclaimer below group if provided in config
    const disclaimerText = dimension.disclaimer || '';
    const disclaimerId = disclaimerText ? `v2-disclaimer-${this.sectionId}-${dimIndex}` : '';
    const disclaimerHtml = disclaimerText
      ? `<p class="v2-subtitle-disclaimer" id="${disclaimerId}">${this.escapeHtml(disclaimerText)}</p>`
      : '';

    return `<div class="v2-text-btn-group" role="radiogroup" aria-label="${this.escapeHtml(dimension.label)}" aria-required="true" ${disclaimerId ? `aria-describedby="${disclaimerId}"` : ''}>${buttons}</div>${disclaimerHtml}`;
  }

  /**
   * Render inline dropdown for dimensions exceeding swatch/button threshold.
   * Styled to match hybrid mode aesthetics with unavailability indicators.
   */
  renderHybridDropdownGroup(dimension, dimIndex, selectedIndex) {
    // Check if any prior dimensions are unselected - affects compatibility checks
    const hasPriorUnselected = this.selectedIndices.slice(0, dimIndex).some(idx => idx === -1);

    const options = this.sortedOptionEntries(dimension, dimIndex).map(({ option, optIndex, displayLabel: label }) => {
      const isSelected = optIndex === selectedIndex;

      // Determine compatibility based on dimension position
      let isCompatible;
      if (dimIndex === 0) {
        // For dim 0: check if this option has ANY valid combinations downstream
        // This prevents users from selecting "dead end" options (e.g., finishes with no sizes)
        isCompatible = this.hasCombinationExistsForPrefix([optIndex], 1);
      } else if (hasPriorUnselected) {
        // If prior dims unselected, can't determine compatibility - assume true
        isCompatible = true;
      } else {
        // For dims 1+: check compatibility with current prior selections
        isCompatible = this.isOptionCompatible(dimIndex, optIndex);
      }

      // Show unavailability in option text for screen readers and visual clarity
      // NEVER mark the selected option as unavailable - user must be able to change selection
      const showUnavailable = !isCompatible && !isSelected;
      const unavailableSuffix = showUnavailable ? ' (unavailable)' : '';
      const subtitleSuffix = option.subtitle ? ` (${this.escapeHtml(option.subtitle)})` : '';

      // Disable incompatible options (selected option always enabled to prevent soft-lock)
      const disabledAttr = showUnavailable ? 'disabled' : '';

      return `
        <option
          value="${optIndex}"
          ${isSelected ? 'selected' : ''}
          ${disabledAttr}
          ${showUnavailable ? 'data-unavailable="true"' : ''}
        >${this.escapeHtml(label)}${subtitleSuffix}${unavailableSuffix}</option>
      `;
    }).join('');

    // Add placeholder if nothing selected
    const placeholderOption = selectedIndex === -1
      ? `<option value="-1" selected disabled>Select ${this.escapeHtml(dimension.label)}...</option>`
      : '';

    // Add disclaimer below dropdown if provided in config
    const disclaimerText = dimension.disclaimer || '';
    const disclaimerId = disclaimerText ? `v2-hybrid-disclaimer-${this.sectionId}-${dimIndex}` : '';
    const disclaimerHtml = disclaimerText
      ? `<p class="v2-subtitle-disclaimer" id="${disclaimerId}">${this.escapeHtml(disclaimerText)}</p>`
      : '';

    return `
      <div class="v2-hybrid-dropdown-wrapper">
        <select
          class="v2-hybrid-dropdown"
          data-dimension="${dimIndex}"
          aria-label="${this.escapeHtml(dimension.label)}"
          aria-required="true"
          ${disclaimerId ? `aria-describedby="${disclaimerId}"` : ''}
        >
          ${placeholderOption}
          ${options}
        </select>
      </div>
      ${disclaimerHtml}
    `;
  }

  /**
   * Render image disclaimer for hybrid mode
   * Currently disabled - returns empty string
   */
  renderImageDisclaimer() {
    return '';
  }

  /**
   * Update hybrid mode button states in-place without full DOM rebuild
   * This prevents spurious click events caused by DOM rebuild during click processing
   */
  updateHybridButtonStates() {

    // Update swatch buttons
    this.container.querySelectorAll('.v2-swatch-btn').forEach(btn => {
      const dimIndex = parseInt(btn.dataset.dimension);
      const optIndex = parseInt(btn.dataset.option);
      const isSelected = this.selectedIndices[dimIndex] === optIndex;

      // Recalculate compatibility - may have changed after other selections
      const hasPriorUnselected = this.selectedIndices.slice(0, dimIndex).some(idx => idx === -1);
      const isCompatible = hasPriorUnselected ? true : this.isOptionCompatible(dimIndex, optIndex);
      const showUnavailable = dimIndex >= 1 && !isCompatible;

      btn.classList.toggle('v2-swatch-btn--selected', isSelected);
      btn.classList.toggle('v2-swatch-btn--unavailable', showUnavailable);
      btn.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      btn.setAttribute('aria-disabled', showUnavailable ? 'true' : 'false');
      // Roving tabindex: only selected option is tabbable
      btn.setAttribute('tabindex', isSelected ? '0' : '-1');
    });

    // Update text buttons
    this.container.querySelectorAll('.v2-text-btn').forEach(btn => {
      const dimIndex = parseInt(btn.dataset.dimension);
      const optIndex = parseInt(btn.dataset.option);
      const isSelected = this.selectedIndices[dimIndex] === optIndex;

      // Recalculate compatibility - may have changed after other selections
      const hasPriorUnselected = this.selectedIndices.slice(0, dimIndex).some(idx => idx === -1);
      const isCompatible = hasPriorUnselected ? true : this.isOptionCompatible(dimIndex, optIndex);
      const showUnavailable = dimIndex >= 1 && !isCompatible;

      btn.classList.toggle('v2-text-btn--selected', isSelected);
      btn.classList.toggle('v2-text-btn--unavailable', showUnavailable);
      btn.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      btn.setAttribute('aria-disabled', showUnavailable ? 'true' : 'false');
      // Roving tabindex: only selected option is tabbable
      btn.setAttribute('tabindex', isSelected ? '0' : '-1');
    });

    // Update hybrid dropdowns (for dimensions exceeding threshold)
    this.container.querySelectorAll('.v2-hybrid-dropdown').forEach(select => {
      const dimIndex = parseInt(select.dataset.dimension);
      const selectedIndex = this.selectedIndices[dimIndex];
      const dimension = this.config.dimensions[dimIndex];

      // Update selected value
      select.value = selectedIndex >= 0 ? selectedIndex : -1;

      // Update option availability
      const hasPriorUnselected = this.selectedIndices.slice(0, dimIndex).some(idx => idx === -1);

      Array.from(select.options).forEach(option => {
        const optIndex = parseInt(option.value);
        if (optIndex < 0) return; // Skip placeholder

        const isSelected = optIndex === selectedIndex;

        // Determine compatibility based on dimension position
        let isCompatible;
        if (dimIndex === 0) {
          // For dim 0: check if this option has ANY valid combinations downstream
          isCompatible = this.hasCombinationExistsForPrefix([optIndex], 1);
        } else if (hasPriorUnselected) {
          // If prior dims unselected, can't determine compatibility - assume true
          isCompatible = true;
        } else {
          // For dims 1+: check compatibility with current prior selections
          isCompatible = this.isOptionCompatible(dimIndex, optIndex);
        }

        // NEVER mark the selected option as unavailable - user must be able to change selection
        const showUnavailable = !isCompatible && !isSelected;

        option.disabled = showUnavailable;
        option.dataset.unavailable = showUnavailable ? 'true' : 'false';

        // Update text to show/hide unavailability
        const label = this.extractVendorSku(dimension.options[optIndex].label);
        option.textContent = showUnavailable ? `${label} (unavailable)` : label;
      });
    });

    // Update dimension labels to show selected value
    this.config.dimensions.forEach((dim, dimIndex) => {
      const selectedIndex = this.selectedIndices[dimIndex];
      if (selectedIndex >= 0) {
        const selectedOption = dim.options[selectedIndex];
        if (selectedOption) {
          const label = this.extractVendorSku(selectedOption.label);
          // Find the label element for this dimension
          const labelEl = this.container.querySelector(
            `[data-dimension="${dimIndex}"] .v2-dimension__selected-value`
          );
          if (labelEl) {
            labelEl.textContent = label;
            // Clear any hover preview original value since we're making permanent change
            delete labelEl.dataset.originalValue;
          }
        }
      }
    });
  }

  /**
   * Attach event listeners for hybrid mode
   */
  attachHybridEventListeners() {
    // Swatch button clicks
    this.container.querySelectorAll('.v2-swatch-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const dimIndex = parseInt(btn.dataset.dimension);
        const optIndex = parseInt(btn.dataset.option);

        this.selectOption(dimIndex, optIndex);
      });

      // Only add hover preview on devices that support hover (not touch)
      if (this.supportsHover()) {
        // Hover preview - update main image and dimension label on mouseenter
        btn.addEventListener('mouseenter', () => {
          const dimIndex = parseInt(btn.dataset.dimension);
          const optIndex = parseInt(btn.dataset.option);
          this.previewVariantImage(dimIndex, optIndex);
          this.previewDimensionLabel(dimIndex, optIndex);
          this.scheduleHoverPrefetch(dimIndex, optIndex);
        });

        // Restore image and label on mouseleave
        btn.addEventListener('mouseleave', () => {
          const dimIndex = parseInt(btn.dataset.dimension);
          this.restoreSelectedVariantImage();
          this.restoreDimensionLabel(dimIndex);
          this.cancelHoverPrefetch();
        });
      }
    });

    // Text button clicks + hover preview (for non-color dimensions)
    this.container.querySelectorAll('.v2-text-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const dimIndex = parseInt(btn.dataset.dimension);
        const optIndex = parseInt(btn.dataset.option);

        this.selectOption(dimIndex, optIndex);
      });

      // Only add hover preview on devices that support hover (not touch)
      if (this.supportsHover()) {
        // Hover preview - update main image and dimension label on mouseenter
        btn.addEventListener('mouseenter', () => {
          const dimIndex = parseInt(btn.dataset.dimension);
          const optIndex = parseInt(btn.dataset.option);
          this.previewVariantImage(dimIndex, optIndex);
          this.previewDimensionLabel(dimIndex, optIndex);
          this.scheduleHoverPrefetch(dimIndex, optIndex);
        });

        // Restore image and label on mouseleave
        btn.addEventListener('mouseleave', () => {
          const dimIndex = parseInt(btn.dataset.dimension);
          this.restoreSelectedVariantImage();
          this.restoreDimensionLabel(dimIndex);
          this.cancelHoverPrefetch();
        });
      }
    });

    // Hybrid dropdown change events (for dimensions exceeding swatch/button threshold)
    this.container.querySelectorAll('.v2-hybrid-dropdown').forEach(select => {
      select.addEventListener('change', (e) => {
        const dimIndex = parseInt(select.dataset.dimension);
        const optIndex = parseInt(e.target.value);

        if (optIndex >= 0) {
          const dimension = this.config.dimensions[dimIndex];
          const label = this.extractVendorSku(dimension.options[optIndex].label);
          this.selectOption(dimIndex, optIndex);
          // Announce selection for screen readers
          this.announceSelection(dimension.label, label);
        }
      });
    });

    // Attach keyboard navigation for all radiogroups
    this.attachKeyboardNavigation();
  }

  /**
   * Create ARIA live region for screen reader announcements
   */
  createAnnouncer() {
    // Check if announcer already exists (e.g., from previous init)
    const existingAnnouncer = document.getElementById(`v2-selector-announcer-${this.sectionId}`);
    if (existingAnnouncer) {
      this.announcer = existingAnnouncer;
      return;
    }

    this.announcer = document.createElement('div');
    this.announcer.id = `v2-selector-announcer-${this.sectionId}`;
    this.announcer.setAttribute('aria-live', 'polite');
    this.announcer.setAttribute('aria-atomic', 'true');
    this.announcer.className = 'visually-hidden';
    // Insert announcer at the start of container so it's available immediately
    this.container.insertBefore(this.announcer, this.container.firstChild);
  }

  /**
   * Announce selection change to screen readers
   * @param {string} dimensionLabel - The label of the dimension (e.g., "Finish", "Size")
   * @param {string} optionLabel - The label of the selected option
   * @param {boolean} isUnavailable - Whether the option is unavailable
   * @param {boolean} isCascadeReset - Whether this is a cascade reset announcement
   */
  announceSelection(dimensionLabel, optionLabel, isUnavailable = false, isCascadeReset = false) {
    if (!this.announcer) return;

    let message;
    if (isCascadeReset) {
      message = `${dimensionLabel} ${optionLabel} due to previous choice`;
    } else if (isUnavailable) {
      message = `${optionLabel} for ${dimensionLabel} is unavailable`;
    } else {
      message = `${dimensionLabel}: ${optionLabel} selected`;
    }

    // Clear and re-announce to ensure screen readers pick up the change
    this.announcer.textContent = '';
    requestAnimationFrame(() => {
      this.announcer.textContent = message;
    });
  }

  /**
   * Attach keyboard navigation for radiogroup elements (WCAG 2.1 AA)
   * Supports: Arrow keys (navigation), Home/End (first/last), Enter/Space (select)
   */
  attachKeyboardNavigation() {
    const groups = this.container.querySelectorAll('[role="radiogroup"]');

    groups.forEach(group => {
      group.addEventListener('keydown', (e) => {
        const buttons = Array.from(group.querySelectorAll('[role="radio"]'));
        const currentIndex = buttons.findIndex(btn => btn === document.activeElement);

        // If focus isn't on a button in this group, ignore
        if (currentIndex === -1) return;

        let nextIndex;
        switch (e.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            e.preventDefault();
            nextIndex = this.findNextFocusableOption(buttons, currentIndex, 1);
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            e.preventDefault();
            nextIndex = this.findNextFocusableOption(buttons, currentIndex, -1);
            break;
          case 'Home':
            e.preventDefault();
            nextIndex = this.findNextFocusableOption(buttons, -1, 1);
            break;
          case 'End':
            e.preventDefault();
            nextIndex = this.findNextFocusableOption(buttons, buttons.length, -1);
            break;
          case ' ':
          case 'Enter':
            e.preventDefault();
            // Click the currently focused button to trigger selection
            buttons[currentIndex].click();
            return;
          default:
            return;
        }

        if (nextIndex !== undefined && nextIndex >= 0 && nextIndex < buttons.length) {
          buttons[nextIndex].focus();
          // Update tabindex for roving focus
          buttons.forEach((btn, i) => {
            btn.setAttribute('tabindex', i === nextIndex ? '0' : '-1');
          });
        }
      });
    });
  }

  /**
   * Find next focusable option, skipping aria-disabled options
   * @param {Element[]} buttons - Array of radio buttons
   * @param {number} currentIndex - Current focused index
   * @param {number} direction - 1 for forward, -1 for backward
   * @returns {number} - Index of next focusable option, or original if none found
   */
  findNextFocusableOption(buttons, currentIndex, direction) {
    const totalButtons = buttons.length;
    let nextIndex = currentIndex + direction;

    // Wrap around and search for non-disabled option
    for (let i = 0; i < totalButtons; i++) {
      // Wrap around
      if (nextIndex < 0) nextIndex = totalButtons - 1;
      if (nextIndex >= totalButtons) nextIndex = 0;

      const btn = buttons[nextIndex];
      // Skip aria-disabled buttons (for dimensions 1+), but allow all for dim 0
      if (btn.getAttribute('aria-disabled') !== 'true') {
        return nextIndex;
      }

      nextIndex += direction;
    }

    // If all options are disabled, return original index
    return currentIndex;
  }
}; // End of class expression

} // End of VariantSelectorV2 guard

// Initialize V2 variant selectors - shared function
function initializeV2VariantSelectors(root = document, retryCount = 0) {

  // Check if the class is defined on window - if not, retry after a short delay
  if (typeof window.VariantSelectorV2 === 'undefined') {
    if (retryCount < 10) {
      // Retry up to 10 times with 100ms delay (1 second total)
      setTimeout(() => initializeV2VariantSelectors(root, retryCount + 1), 100);
    } else {
      console.error('[V2 Variant Selector] VariantSelectorV2 class not loaded after retries');
    }
    return;
  }

  const configScripts = root.querySelectorAll('[data-v2-variant-config]');

  configScripts.forEach(configScript => {
    try {
      const sectionId = configScript.dataset.sectionId;

      // Skip if already initialized
      const container = root.getElementById ? root.getElementById(`v2-variant-selector-${sectionId}`) : document.getElementById(`v2-variant-selector-${sectionId}`);
      if (!container || container.dataset.v2Initialized) {
        return;
      }

      const variantConfig = JSON.parse(configScript.textContent);

      const productDataScript = (root.querySelector || document.querySelector).call(root.querySelector ? root : document, `[data-v2-product-data][data-section-id="${sectionId}"]`);
      if (!productDataScript) {
        return;
      }

      const productData = JSON.parse(productDataScript.textContent);

      // Parse settings if available
      const settingsScript = (root.querySelector || document.querySelector).call(root.querySelector ? root : document, `[data-v2-settings][data-section-id="${sectionId}"]`);
      const settings = settingsScript ? JSON.parse(settingsScript.textContent) : {};

      const productFormId = container.dataset.productFormId;

      const instance = new window.VariantSelectorV2(productData, variantConfig, sectionId, productFormId, settings);

      // Store instance on container for cleanup access
      container._v2Instance = instance;

      // Mark as initialized to prevent re-init
      container.dataset.v2Initialized = 'true';
    } catch (error) {
      console.error('[V2 Variant Selector] Error:', error);
    }
  });
}

// Cleanup V2 variant selectors - call when removing selectors from DOM
function destroyV2VariantSelectors(root = document) {
  root.querySelectorAll('[data-v2-initialized="true"]').forEach(container => {
    if (container._v2Instance && typeof container._v2Instance.destroy === 'function') {
      container._v2Instance.destroy();
      container._v2Instance = null;
    }
    delete container.dataset.v2Initialized;
  });
}

// Make functions available globally
window.initializeV2VariantSelectors = initializeV2VariantSelectors;
window.destroyV2VariantSelectors = destroyV2VariantSelectors;

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => initializeV2VariantSelectors());

// Initialize when quick-view content loads
document.addEventListener('quick-view:loaded', () => initializeV2VariantSelectors());

// Cleanup when quick-view is closed
document.addEventListener('quick-view:closed', () => {
  const quickViewContainer = document.querySelector('.quick-view, [data-quick-view]');
  if (quickViewContainer) {
    destroyV2VariantSelectors(quickViewContainer);
  }
});
